import { useAiAccess } from '../hooks/useAiAccess';
import { gatePresentationOpen } from '../components/common/PresentationScreenGate';
import { useDirtySinceOpen } from '../hooks/useDirtySinceOpen';
import { finalizeBehaviorAiFeedbackAfterSave } from '../services/recordAiFeedbackService';
import AiRecordFeedbackPanel, { type AiRecordFeedbackPanelHandle } from '../components/program/AiRecordFeedbackPanel';
import { LearningPlanGuideView, learningPlanGuideHasPlanMarkers } from '../components/program/LearningPlanGuideView';
import React from 'react';
import { createPortal } from 'react-dom';
import { createSearchParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { AnnualProgressReadOnlyEmptyTable } from '../components/common/ProgressEmptyActualTables';
import { useProgramSidebar } from '../contexts/ProgramSidebarContext';
import { usePaidPassGate } from '../contexts/PaidPassGateContext';
import { useTutorial } from '../contexts/TutorialContext';
import {
  finishClassPresentationHandoffOnOpener,
  resolveClassPresentationSlideOpenOptions,
} from '../utils/classPresentationTutorialHandoff';
import { useCalendarLinkedAcademicYear } from '../hooks/useCalendarLinkedAcademicYear';
import { useProgramFixedContentRows } from '../hooks/useProgramFixedContentRows';
import { useAnnualProgressPageQueries } from '../hooks/useAnnualProgressPageQueries';
import { useSchoolScopedCacheSyncReady } from '../hooks/useSchoolScopedCacheSyncReady';
import { getAnnualProjectPrograms } from '../services/annualProjectProgramsService';
import { getClassMemberGradeLayoutsMap } from '../services/classMembersService';
import {
  getGradeTrackSections,
  ClassScheduleSettings,
  getGradeTrackProgramPreferences,
  loadGradeTrackProgramPreferencesCache,
  type PageSectionLayout,
} from '../services/sectionLayoutService';
import { upsertProgramProgressCells } from '../services/programProgressService';
import { updateProgramScopedContentRowFieldsById } from '../services/programScopedContentService';
import { addDays, mondayOf, parseISODate, toISODate } from '../utils/date';
import {
  ANNUAL_WEEKDAY_KEYS,
  AnnualClassEntry,
  buildAnnualCellKey,
  buildAnnualCellMap,
  buildAnnualClassEntries,
  buildAnnualPeriodLabels,
  deriveAnnualEffectiveCell,
  isAnnualShortDayTailMemo,
  isDedicatedColor,
  annualSlotIsFixedProgram,
  parseAnnualCellKey,
} from '../utils/annualTimetable';
import { KEYBOARD_TABLE_ARROW_UP_TOP_INSET_PX } from '../utils/keyboardVerticalTableScroll';
import {
  ANNUAL_PROJECT_PROGRAMS_CHANGE_EVENT,
  readAnnualProjectProgramsCache,
  matchesAnnualProjectProgramCell,
  type AnnualProjectProgram,
} from '../utils/annualProjectProgramsStorage';
import {
  ANNUAL_TIMETABLE_SAVED_EVENT,
  type AnnualTimetableSavedDetail,
} from '../utils/annualTimetableSavedEvents';
import {
  clearAnnualProgressRelatedCachesForGrade,
  clearProgramDisplayCachesForGrade,
} from '../utils/annualProgressClientCacheInvalidation';
import { invalidateAnnualBehaviorNotesQuery, invalidateAnnualPresentationAuditQuery, setAnnualBehaviorNotesQueryData } from '../query/cacheUpdates';
import { useAnnualPresentationAuditQuery } from '../query/hooks/useAnnualPresentationAuditQuery';
import { useAnnualBehaviorNotesQuery } from '../query/hooks/useAnnualBehaviorNotesQuery';
import {
  emitProgramProgressSaved,
  PROGRAM_PROGRESS_SAVED_EVENT,
  type ProgramProgressSavedDetail,
} from '../utils/programProgressSavedEvents';
import {
  ANNUAL_BEHAVIOR_NOTES_SAVED_EVENT,
  type AnnualBehaviorNotesSavedDetail,
} from '../utils/annualBehaviorNotesSavedEvents';
import {
  AnnualBehaviorNote,
  AcademicTerm,
  AnnualTimetableCell,
  AnnualWeekdayKey,
  type DayKind,
  Grade,
  ProgramFixedContentRow,
  type ProgramProgressBgColorByField,
  ProgramProgressCell,
  ProgramScopedContentRow,
  Track,
} from '../types/domain';
import {
  CLASS_MEMBERS_CHANGED_EVENT,
  emptyGradeLayout,
  flattenGradeLayout,
  hasClassMemberLocalStorageSnapshot,
  loadClassMemberGradeLayoutFromStorage,
  type ClassMemberGradeLayout,
  type ClassMemberRow,
} from '../utils/classMembersStorage';
import {
  matchMembersByClassRecordTagKeyword,
  membersForRecordGroup,
  type MemberWithGroupContext,
} from '../utils/classMemberRecordGroupUtils';
import { renderNoteWithMemberTags } from '../utils/classRecordMemoHashtags';
import { useSealedMemoTagLabels } from '../hooks/useSealedMemoTagLabels';
import ClassRecordMemoNoteEditor, {
  type ClassRecordMemoNoteEditorHandle,
} from '../components/program/ClassRecordMemoNoteEditor';
import DeleteHoldButton from '../components/common/DeleteHoldButton';
import RecordMemberGroupSelect from '../components/program/RecordMemberGroupSelect';
import {
  formatClassRecordCodeForDisplay,
  formatEvaluationCodeForDisplay,
  formatLearningTextForDisplay,
  getCrossTopicAndNoteForDisplay,
  getUnitTextAndNoteForDisplay,
  parseMemoFieldStorageDeep,
  parseProgramProgressMemoJsonForDisplay,
} from '../utils/programCellDisplayText';
import {
  computeLearningPlanHoverTooltipStyle,
  LEARNING_PLAN_HOVER_TOOLTIP_DISMISS_MS,
  PROGRAM_TABLE_CELL_HOVER_TOOLTIP_DELAY_MS,
  computeProgramProgressHoverTooltipStyle,
  computeProgramTableFloatingPopupStyle,
  resolveProgramTableFloatingHostRect,
  type ProgramProgressOverlayWidthKind,
} from '../utils/programProgressHoverTooltip';
import {
  type OffDayByDate,
  timetableTextForOffDayRow,
} from '../utils/annualProgressDataCache';
import { shouldIgnoreAppNavigationShortcut } from '../utils/appNavigationShortcutGuard';
import { focusBrowseScrollWrapIfVisible, releaseTrappedKeepAliveFocus } from '../utils/keepAliveFocusRelease';
import {
  buildAnnualEntryFocusNavKey,
  isAnnualEntryFocusPending,
  logAnnualEntryFocusResolved,
  resolveAnnualEntryFocusTarget,
  type AnnualEntryFocusResolution,
} from '../utils/annualProgressEntryFocus';
import type { ProgramContentSlideOwnerRef } from '../types/programContentSlideCanvas';
import {
  closeProgramContentSlideTabPlaceholder,
  openProgramContentSlideFailureMessage,
  openProgramContentSlideInNewTab,
  openProgramContentSlideTabPlaceholder,
} from '../utils/programContentSlideStorage';
import {
  getPresentationOverlayAnnualCellKeys,
  programSlideEditScopeLockStorageKeyPrefix,
  programSlidePresentationOverlayStorageKeyPrefix,
} from '../utils/programSlideEditScopeLock';
import {
  buildAnnualSlideWorkspaceScopeId,
  resolveAnnualSlideContentOwnerCandidates,
  resolveAnnualSlideContentSlideOwner,
  resolveAnnualSlideOpenSlideDbOwner,
  resolvePresentationRefIdFromLearningText,
  isPersistableUuid,
} from '../utils/programContentSlideOwner';
import type { ProgramContentSlidePayloadV1 } from '../utils/programContentSlideStorage';
import {
  PROGRAM_CONTENT_SLIDE_LEARNING_UPDATED_EVENT,
  PROGRAM_CONTENT_SLIDE_LEARNING_UPDATED_KEY,
} from '../utils/programContentSlideStorage';
import {
  fetchProgramContentSlidePresenceKeySet,
  hasProgramContentSlideDraftForOwner,
  PROGRAM_CONTENT_SLIDE_DRAFTS_CHANGED_EVENT,
  PROGRAM_CONTENT_SLIDE_PRESENCE_CACHE_PREFIX,
  programContentSlidePresenceKey,
  resolveSlideTabOwnerForSave,
  deleteProgramContentSlidePagesForOwner,
  invalidateProgramContentSlidePresenceCache,
  learningTextStorageWithoutPresentationRef,
  queueProgramContentSlideDeleteDraft,
} from '../services/programContentSlideService';
import {
  createWorksheetRefOnSave,
  fetchProgramContentWorksheetPresence,
  learningTextWithWorksheetRef,
  type ProgramWorksheetLearningTextPatch,
} from '../services/programContentWorksheetService';
import {
  LEARNING_CONTENT_WORKSHEET_DRAFTS_CHANGED_EVENT,
  loadLearningContentWorksheetDraft,
} from '../utils/learningContentWorksheetCanvasDraftStorage';
import { cellLearningTextForEditor } from '../utils/programLearningContentFields';
import { resolveWorksheetRefIdFromLearningText } from '../utils/programContentWorksheetOwner';
import {
  LEARNING_WORKSHEET_PENDING_CHANGED_EVENT,
  hasPendingLearningWorksheetFlush,
  hasPendingLearningWorksheetFlushForAnnualKeys,
  queueLearningWorksheetPendingFlush,
  flushPendingLearningWorksheetDrafts,
  flushPendingLearningWorksheetDraftForRef,
  resolvePendingWorksheetRefIdForAnnualKeys,
  resolveWorksheetDocumentForEditor,
} from '../utils/learningContentWorksheetPendingStorage';
import { resolveWorksheetDraftStorageRowKey } from '../utils/programSyncMirrorIndicators';
import {
  buildWorksheetListItemFromAnnualRow,
  sortWorksheetListItems,
  type ProgramWorksheetListItem,
} from '../utils/programWorksheetList';
import type { LearningContentWorksheetDocumentV1 } from '../types/learningContentWorksheetCanvas';
import { notifyProgramContentSlideLearningUpdated } from '../utils/programContentSlideStorage';
import {
  LEARNING_ATTACHMENT_INDICATOR_CHANGED_EVENT,
  markLearningAttachmentIndicatorDeletedForSyncKeys,
  readLearningAttachmentIndicatorDeletedHints,
} from '../utils/learningAttachmentIndicatorStorage';
import {
  buildAnnualSyncMirrorIndicatorMaps,
  collectAnnualSlideOwnerSignatureKeys,
  computeAnnualLearningSlidePagesByAnnualKey,
  computeAnnualLearningMemoNoteByAnnualKey,
  computeAnnualLearningWorksheetByAnnualKey,
  resolveAnnualIndicatorLearningText,
} from '../utils/annualProgressLearningIndicators';
import { resolveWeeklyLearningGuideTrackBackgroundHex } from '../utils/weeklyLearningGuideTrackColorResolve';
import AnnualProgressDataCleanupModal from '../components/annual/AnnualProgressDataCleanupModal';
import AnnualBehaviorNoteListModal, {
  type AnnualBehaviorNoteListItem,
} from '../components/annual/AnnualBehaviorNoteListModal';
import AnnualCrossTopicCountModal from '../components/annual/AnnualCrossTopicCountModal';
import AnnualCrossTopicAiModal from '../components/annual/AnnualCrossTopicAiModal';
import NeisLearningContentUploadModal from '../components/annual/NeisLearningContentUploadModal';
import DocumentActionIcon from '../components/common/DocumentActionIcon';
import DownloadActionIcon from '../components/common/DownloadActionIcon';
import NeisExportFormatPickModal, {
  type NeisExportFormat,
} from '../components/common/NeisExportFormatPickModal';
import LearningContentWorksheetEditorModal from '../components/learningWorksheet/LearningContentWorksheetEditorModal';
import ProgramPresentationListModal from '../components/program/ProgramPresentationListModal';
import ProgramWorksheetListModal from '../components/program/ProgramWorksheetListModal';
import { saveNeisLearningContentUploadAsExcel } from '../utils/neisLearningContentUploadExcelExport';
import {
  openDocumentExportPreviewFailureMessage,
  openDocumentExportPreviewInNewTab,
  openDocumentExportPreviewTabPlaceholder,
} from '../utils/documentExportPreviewStorage';
import {
  deletePresentationAuditSelections,
  parsePresentationAuditSelectionKey,
  type PresentationAuditEntry,
} from '../services/programPresentationAuditService';
import {
  filterPresentationOrphansToPageScope,
  mergeOrphanPresentationListItems,
  resolvePresentationListSlideOwner,
  resolveAnnualPresentationSyncMirror,
  sortPresentationListItems,
  type ProgramPresentationListItem,
} from '../utils/programPresentationList';
import {
  pickDateOrNearestFutureAnnualCellKey,
  pickTodayOrNearestFutureForLesson,
} from '../utils/todayLessonAnnualKey';
import {
  deleteAnnualBehaviorNoteByNaturalKey,
  loadAnnualBehaviorNotesCache,
  upsertAnnualBehaviorNote,
} from '../services/annualBehaviorNotesService';
import { recordDevLocalStorageProgramCacheUse, markDevPresentationOpenTransferScope } from '../utils/devNetworkTransferMonitor';
import {
  computeProgramSavePending,
  saveProgramGradeTermWorkspaceAndAttachments,
  type ProgramGradeTermSaveTarget,
} from '../utils/programGradeTermSave';
import { saveProgramWorkspaceScopes } from '../utils/programUnifiedSave';
import {
  collectDirtyWorkspaceScopeIdsForAttachmentSave,
  ProgramTableCoSaveCancelledError,
  programTableDirtyWithAttachmentSaveConfirmMessage,
} from '../utils/programAttachmentCoSaveWorkspace';
import { publishAnnualProgressCrossTopics, publishAnnualProgressCrossTopicDetailRows } from '../utils/annualProgressCrossTopicRegistry';
import type { AnnualCrossTopicDetailRow } from '../utils/annualProgressCrossTopicExtract';
import { filterTracksSubscribedToGradeTrackSections } from '../utils/gradeTrackSectionSubscription';
import {
  buildNeisUploadLearningContentRows,
  buildSubscribedTrackFilterOptions,
  filterNeisLearningContentUploadRows,
  neisLearningContentUploadRowsHaveData,
  type NeisLearningContentUploadSourceRow,
} from '../utils/neisLearningContentUploadRows';
import { touchAuthActivity } from '../utils/authIdleTimeout';
import { loadTrackProgramModeMap } from '../utils/trackProgramModeStorage';
import { loadTrackProgramSyncRepMap } from '../utils/trackProgramSyncRepStorage';
import { TRACK_PROGRAM_SPLIT_CHANGE_EVENT } from '../utils/trackProgramSplitStorage';
import { applyGradeTrackProgramPrefsToLocalStores } from '../utils/gradeTrackProgramPrefsLocalSeed';
import { ANNUAL_PREREQUISITE_REFRESH_EVENT } from '../utils/annualPrerequisiteRefresh';
import { programCellBgRenderProps } from '../utils/programCellBgStyle';
import {
  mapProgressLikeToDisplayFields,
  programProgressCellToProgressLike,
  type ProgramProgressLikeRow,
} from '../utils/annualProgressFieldDisplay';
import {
  annualSlotHasTimetableProgramAssignment,
  buildScopedRowsAnnualIndex,
  enrichScopedRowsForAnnualProgressDisplay,
  eventNameFromFixedContentRow,
  isFixedProgramSeedLearningText,
  normalizeFixedEventName,
  pickRepScopedContentRowForMirrorSlot,
  pickScopedContentFromAnnualIndex,
  pickScopedRecordRowFromAnnualIndex,
  resolveAnnualSlotProgressSource,
  type ScopedRowsAnnualIndex,
} from '../utils/annualProgressSlotSource';
import { isSyncTrackMirrorScope } from '../utils/trackSyncScopedContent';
import {
  resolveAnnualProgressRecordTargets,
  type AnnualProgressProgramRecordTarget,
} from '../utils/annualProgressProgramRecordTarget';
import AnnualProgressProgramRecordModals, {
  type AnnualProgressProgramRecordModalKind,
} from '../components/annualProgress/AnnualProgressProgramRecordModals';
import {
  getProgramWorkspaceScopeEntries,
  getProgramWorkspaceScopeEntry,
  PROGRAM_WORKSPACE_CHANGED_EVENT,
} from '../utils/programEditWorkspace';
import { mergeProgramWorkspaceUpsertsIntoBaselineRows } from '../utils/programWorkspaceDisplayMerge';
import {
  buildGradeTrackTimetableContext,
  normalizeTimetableTextToDefaultAlias,
  resolveTrackNameFromTimetableText,
} from '../utils/trackTimetableAlias';
import {
  ANNUAL_PROGRAM_DISPLAY_OVERLAY_CHANGED_EVENT,
  readAnnualProgramDisplayOverlayEntries,
} from '../utils/annualProgramDisplayOverlayStorage';

interface WeekInfo {
  weekIndex: number;
  mondayIso: string;
  datesByWeekday: string[];
}

interface AnnualProgressPageProps {
  workspaceId: string;
  grades: Grade[];
  currentGradeId?: string;
  termNumber: 1 | 2;
  isActive?: boolean;
}

interface AnnualProgressRow {
  seq: number;
  dateIso: string;
  /** 이 행에 표시 중인 프로그램 내용 행 id(탐색 대상 판정용) */
  entryContentRowId?: string;
  dateLabel: string;
  weekdayLabel: string;
  periodLabel: string;
  gradeLabel: string;
  semesterLabel: string;
  templateLabel: string;
  timetableText: string;
  timetableColorHex: string;
  timetableTextShrink: boolean;
  timetableSlotKind: AnnualTimetableCell['slot_kind'];
  programKindLabel: string;
  annualCellKey: string;
  progressUnit: string;
  progressUnitNote: string;
  progressLearning: string;
  progressLearningNote: string;
  progressPage: string;
  progressAssistantPage: string;
  progressCurrentPeriod: string;
  progressTotalPeriod: string;
  progressMaterial: string;
  /** 프로그램 표에서 지정한 본문 칸 배경색 */
  progressBgByField: ProgramProgressBgColorByField;
  progressCross: string;
  progressCrossNote: string;
  /** 메모 본문: program 저장·연간시간표 memo(고정 제외) */
  displayMemoPrimary: string;
  /** 메모 상세(JSON note): 프로그램 저장값만 */
  displayMemoNote: string;
  progressEval: string;
  /** 평가 저장 text(1·2·3 등): 프로그램 테이블 원형 색상 클래스용 */
  progressEvalCode: string;
  progressEvalNote: string;
  progressClassRecord: string;
  progressClassRecordKind: 'empty' | 'lesson' | 'eval_log' | 'custom';
  progressClassRecordNote: string;
  progressBehaviorNote: string;
  /** 행동특성 기록에 내장된 AI 피드백(컬럼 미적용이면 null) */
  progressBehaviorAiFeedback?: string | null;
  progressClassRecordMemberGroupId: string | null;
  progressBehaviorRecordMemberGroupId: string | null;
  dayRowOrder: number;
  /** 0~9, 교시 열 10블럭 중 현재 행의 슬롯 */
  slotIndex: number;
  /** `slotKind === 'holiday'`인 행: 학사 달력의 공휴/휴업/방학 구분(단원~준비물 배경색) */
  calendarOffDayKind: DayKind | null;
  /** 프로그램 내용 슬라이드 DB owner(없으면 추가 슬라이드만 로컬) */
  slideOwner: ProgramContentSlideOwnerRef | null;
  /** DB 슬라이드 presence 조회용 owner 후보 */
  slideOwnerCandidates: ProgramContentSlideOwnerRef[];
  /** Shift+U 시 실제 DB 조회·저장에 쓸 owner(원본 program_cell 등). `slideOwner`가 presentation_ref일 때 대체 */
  slideOpenDbOwner: ProgramContentSlideOwnerRef | null;
  /** 프로그램 워크스페이스와 동일한 `scopeId`(슬라이드 탭 저장·락용) */
  slideWorkspaceScopeId: string;
  mondayIso: string;
  weekdayKey: AnnualWeekdayKey;
  /** DB 저장용 learning_text(JSON·presentationRefId 포함) */
  slideLearningTextRaw: string;
  /** 연간진도표에서 최초 프레젠테이션 저장 시 프로그램 원본 행에 ref를 기록 */
  slideCreatePresentationRefOnSave?: ProgramContentSlidePayloadV1['createPresentationRefOnSave'];
  /** 편제 프로그램 scope 조회용 시간표 alias(동기화 대표 판별) */
  scopeLookupAlias: string;
  /** 동기화 편제 미러 행 — 프레젠테이션 목록에서 비활성 */
  isPresentationSyncMirror: boolean;
  /** 평가 원본(대표/고정/스코프) */
  evalRecordTarget: AnnualProgressProgramRecordTarget | null;
  /** 수업기록 원본(미러는 alias, 그 외는 내용 행) */
  classRecordTarget: AnnualProgressProgramRecordTarget | null;
  progressEvalRaw: string;
  progressClassRaw: string;
}

const ANNUAL_PROGRESS_TOTAL_COLUMN_COUNT = 21;
const ANNUAL_PROGRESS_OVERSCAN_ROWS = 26;
const ANNUAL_PROGRESS_DEFAULT_ROW_HEIGHT = 32;
type AnnualProgressFieldId =
  | 'unit'
  | 'learning'
  | 'page'
  | 'assistantPage'
  | 'currentPeriod'
  | 'totalPeriod'
  | 'material'
  | 'cross'
  | 'memo'
  | 'eval'
  | 'class';

type AnnualProgressKeyboardAnchorField = AnnualProgressFieldId | 'behavior';
type AnnualProgressKeyboardArrow = 'ArrowUp' | 'ArrowDown';

function parseGradeDisplayName(raw: string): { title: string } {
  const [base] = raw.split('::');
  return { title: (base ?? raw).trim() };
}

function gradeNumberLabel(raw: string): string {
  const nums = (raw.match(/\d+/g) ?? []).map((v) => Number(v)).filter((n) => Number.isInteger(n));
  return nums.length > 0 ? String(nums[0]) : '';
}

function buildWeeksFromRange(startsOn: string, endsOn: string): WeekInfo[] {
  if (!startsOn || !endsOn) return [];
  const endDate = parseISODate(endsOn);
  const endMonday = mondayOf(endsOn);
  const endMondayIso = toISODate(endMonday);
  let cur = mondayOf(startsOn);
  const list: WeekInfo[] = [];
  let weekIndex = 0;
  while (cur.getTime() <= endDate.getTime()) {
    const mondayIso = toISODate(cur);
    const datesByWeekday: string[] = [];
    for (let d = 0; d < 6; d += 1) {
      datesByWeekday.push(toISODate(addDays(cur, d)));
    }
    list.push({ weekIndex: ++weekIndex, mondayIso, datesByWeekday });
    cur = addDays(cur, 7);
  }
  const hasEndWeek = list.some((w) => w.mondayIso === endMondayIso);
  if (!hasEndWeek) {
    const datesByWeekday: string[] = [];
    for (let d = 0; d < 6; d += 1) {
      datesByWeekday.push(toISODate(addDays(endMonday, d)));
    }
    list.push({ weekIndex: ++weekIndex, mondayIso: endMondayIso, datesByWeekday });
  }
  return list;
}

function weekdayLabel(key: AnnualWeekdayKey): string {
  return ANNUAL_WEEKDAY_KEYS.find((w) => w.key === key)?.label ?? key;
}

function shortDateLabel(iso: string): string {
  const d = parseISODate(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getMonth() + 1}월${d.getDate()}일`;
}

function formatDateTimeMinute(raw: string): string {
  const text = (raw ?? '').trim();
  if (!text) return '';
  const d = new Date(text);
  if (Number.isNaN(d.getTime())) return text;
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${mm}-${dd} ${hh}:${mi}`;
}

function behaviorNoteNaturalKey(lessonDate: string, dayRowOrder: number): string {
  return `${(lessonDate ?? '').trim()}|${Math.max(0, dayRowOrder | 0)}`;
}

/** 행동특성 모달: 입력은 로컬 state로만 두어 연간진도표 전체 리렌더를 막는다 */
const AnnualProgressBehaviorModalBody = React.memo(function AnnualProgressBehaviorModalBody({
  annualCellKey,
  initialNote,
  contextLine,
  memberLayout,
  initialRecordMemberGroupId,
  classMembers,
  innerRef,
  saving,
  error,
  canDelete,
  onClose,
  onSave,
  onDelete,
  workspaceId,
  initialAiFeedback,
}: {
  workspaceId?: string;
  annualCellKey: string;
  initialNote: string;
  contextLine: string;
  memberLayout: ClassMemberGradeLayout;
  initialRecordMemberGroupId: string | null;
  classMembers: ClassMemberRow[];
  innerRef: React.RefObject<HTMLDivElement | null>;
  saving: boolean;
  error: string | null;
  canDelete: boolean;
  onClose: () => void;
  onSave: (
    key: string,
    note: string,
    recordMemberGroupId: string | null,
    aiFeedback?: string
  ) => boolean | void | Promise<boolean | void>;
  initialAiFeedback?: string | null;
  onDelete: (key: string) => void | Promise<void>;
}) {
  const [note, setNote] = React.useState(initialNote);
  const [recordMemberGroupId, setRecordMemberGroupId] = React.useState<string | null>(initialRecordMemberGroupId);
  const [isBehaviorModalExpanded, setIsBehaviorModalExpanded] = React.useState(false);
  const behaviorMemoEditorRef = React.useRef<ClassRecordMemoNoteEditorHandle | null>(null);
  const sealedLabelByToken = useSealedMemoTagLabels([note, initialNote]);
  const [behaviorTagGroupPick, setBehaviorTagGroupPick] = React.useState<{
    keyword: string;
    candidates: MemberWithGroupContext[];
  } | null>(null);
  React.useEffect(() => {
    setNote(initialNote);
  }, [annualCellKey, initialNote]);
  React.useEffect(() => {
    const init = (initialRecordMemberGroupId ?? '').trim();
    if (init && memberLayout.groups.some((g) => g.id === init)) {
      setRecordMemberGroupId(init);
    } else {
      setRecordMemberGroupId(null);
    }
  }, [annualCellKey, initialRecordMemberGroupId, memberLayout]);
  React.useEffect(() => {
    setIsBehaviorModalExpanded(false);
  }, [annualCellKey]);

  const behaviorAiRef = React.useRef<AiRecordFeedbackPanelHandle>(null);
  const [behaviorAiDirty, setBehaviorAiDirty] = React.useState(false);
  const behaviorDirty =
    useDirtySinceOpen(annualCellKey, JSON.stringify([note, recordMemberGroupId])) || behaviorAiDirty;
  const effectiveMembers = React.useMemo(
    () => membersForRecordGroup(memberLayout, recordMemberGroupId, classMembers),
    [classMembers, memberLayout, recordMemberGroupId]
  );

  const finalizeBehaviorHashtagForMemberGroup = React.useCallback(
    (tagBody: string) => {
      const hits = matchMembersByClassRecordTagKeyword(memberLayout, tagBody);
      if (hits.length === 0) return;
      if (hits.length === 1) {
        setRecordMemberGroupId(hits[0].groupId);
        return;
      }
      setBehaviorTagGroupPick({ keyword: tagBody, candidates: hits });
    },
    [memberLayout]
  );

  const readNoteForSave = React.useCallback(() => {
    const domNote = behaviorMemoEditorRef.current?.readSerializedNoteFromDom?.();
    return domNote !== undefined ? domNote : note;
  }, [note]);

  return (
    <div
      ref={innerRef}
      className={
        'cp-program-note-popup cp-program-note-popup--with-head cp-program-note-popup--dashboard-lesson' +
        (isBehaviorModalExpanded ? ' cp-program-note-popup-expanded' : '')
      }
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="cp-program-note-popup-stack">
        <div className="cp-program-note-popup-head">
          <button
            type="button"
            className="cp-program-note-popup-expand-btn"
            aria-label={isBehaviorModalExpanded ? '기본 크기로 축소' : '확장 모달로 보기'}
            title={isBehaviorModalExpanded ? '기본 크기로 축소' : '확장 모달로 보기'}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={() => setIsBehaviorModalExpanded((prev) => !prev)}
          >
            <img
              src={isBehaviorModalExpanded ? '/Minimize.svg' : '/Expand.svg'}
              alt=""
              aria-hidden
              className="cp-program-note-popup-expand-icon"
            />
          </button>
        </div>
        <div className="cp-program-eval-choice-tabs">
          <div
            className="cp-annual-progress-tabs cp-annual-project-term-tabs cp-program-class-rec-choice-row-half"
            role="group"
          >
            <button
              type="button"
              className="cp-annual-progress-tab cp-program-class-rec-tab-behavior is-active"
              tabIndex={-1}
            >
              행동특성
            </button>
          </div>
          <div className="cp-program-note-popup-context-wrap">
            <p className="cp-program-note-popup-context cp-program-note-popup-context-primary">{contextLine}</p>
          </div>
        </div>
        <div className="cp-program-note-popup-field cp-program-class-rec-memo-field">
          <div className="cp-class-rec-member-toolbar cp-class-rec-member-toolbar-lesson">
            <RecordMemberGroupSelect
              id="cp-annual-behavior-member-group"
              layout={memberLayout}
              value={recordMemberGroupId}
              onChange={setRecordMemberGroupId}
              disabled={saving}
            />
          </div>
          <ClassRecordMemoNoteEditor
            key={`${annualCellKey}:behavior`}
            ref={behaviorMemoEditorRef}
            value={note}
            onChange={setNote}
            members={effectiveMembers}
            applyBulkMemberTagRules={!!recordMemberGroupId}
            sealedLabelByToken={sealedLabelByToken}
            onHashtagTagFinalized={finalizeBehaviorHashtagForMemberGroup}
            placeholder="행동특성을 입력해 주세요."
          />
        </div>
        {error ? (
          <p className="cp-program-class-rec-modal-error" role="alert">
            {error}
          </p>
        ) : null}
        <AiRecordFeedbackPanel
          ref={behaviorAiRef}
          workspaceId={workspaceId}
          recordKey={annualCellKey}
          kind="behavior"
          embeddedText={initialAiFeedback ?? null}
          onDirtyChange={setBehaviorAiDirty}
          note={note}
          contextLines={[contextLine && `일시: ${contextLine}`]}
          members={effectiveMembers}
          allMembers={classMembers}
          disabled={saving}
        />
        <div className="cp-program-note-popup-actions cp-annual-behavior-note-popup-actions">
          <button type="button" className="cp-program-note-confirm-btn" onClick={onClose} disabled={saving}>
            닫기
          </button>
          <button
            type="button"
            className="cp-program-note-confirm-btn cp-program-note-confirm-btn--primary cp-annual-behavior-note-save-btn cp-record-save-btn"
            onClick={() => {
              const commitAi = behaviorAiRef.current?.capture();
              const aiFeedback = behaviorAiRef.current?.getEmbedValue();
              void Promise.resolve(onSave(annualCellKey, readNoteForSave(), recordMemberGroupId, aiFeedback)).then(
                (ok) =>
                  finalizeBehaviorAiFeedbackAfterSave({
                    ok,
                    commit: commitAi,
                    workspaceId: workspaceId ?? '',
                    recordKey: annualCellKey,
                    text: aiFeedback,
                  })
              );
            }}
            disabled={saving || !behaviorDirty}
          >
            {saving ? '저장 중…' : behaviorDirty ? '저장' : '변경사항 없음'}
          </button>
          <DeleteHoldButton
            disabled={saving || !canDelete}
            disabledTitle="삭제할 행동특성 기록이 없습니다"
            disabledAriaLabel="삭제할 행동특성 기록이 없습니다"
            ariaLabel="행동특성 기록 삭제 (길게 누르기)"
            onDelete={() => {
              void behaviorAiRef.current?.remove(['behavior']);
              void onDelete(annualCellKey);
            }}
          />
        </div>
      </div>
      {behaviorTagGroupPick
        ? createPortal(
            <div
              className="cp-modal-backdrop"
              role="presentation"
              onMouseDown={(ev) => {
                if (ev.target === ev.currentTarget) setBehaviorTagGroupPick(null);
              }}
            >
              <div
                className="cp-modal"
                role="dialog"
                aria-modal="true"
                onMouseDown={(ev) => ev.stopPropagation()}
              >
                <h3>멤버 그룹 선택</h3>
                <p className="page-desc">
                  &quot;{behaviorTagGroupPick.keyword}&quot;에 해당하는 학생이 여러 그룹에 있습니다. 기록에 사용할
                  학생을 선택해 주세요.
                </p>
                <div className="cp-modal-body cp-class-rec-tag-group-pick-list">
                  {behaviorTagGroupPick.candidates.map((c) => (
                    <button
                      key={`${c.groupId}-${c.memberId}`}
                      type="button"
                      className="cp-btn-secondary cp-class-rec-tag-group-pick-item"
                      onClick={() => {
                        setRecordMemberGroupId(c.groupId);
                        setBehaviorTagGroupPick(null);
                      }}
                    >
                      {c.groupName} · {c.memberName}
                    </button>
                  ))}
                </div>
                <div className="cp-modal-footer">
                  <button type="button" className="cp-btn-secondary" onClick={() => setBehaviorTagGroupPick(null)}>
                    취소
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
});

const AnnualProgressTableRow = React.memo(function AnnualProgressTableRow({
  row,
  rowIndex,
  isRowSelection,
  isBehaviorSelection,
  rowFieldEdgeClassById,
  displayFontSize,
  annualNoteCellHandlers,
  classRecordMemberTagMembersForRow,
  behaviorRecordMemberTagMembersForRow,
  selectRowByAnnualKey,
  selectBehaviorCellByAnnualKey,
  openBehaviorPopup,
  openProgramRecordModal,
  learningHasSlidePages,
  learningHasWorksheet,
  learningHasMemoNote,
  learningDisplayText,
  learningDisplayNote,
  presentationLockedAnnualCellKeys,
}: {
  row: AnnualProgressRow;
  rowIndex: number;
  isRowSelection: boolean;
  isBehaviorSelection: boolean;
  rowFieldEdgeClassById: Record<AnnualProgressFieldId, string>;
  displayFontSize: (value: string) => string;
  annualNoteCellHandlers: (
    anchorKey: string,
    note: string,
    tooltipKind: 'note' | 'unit' | 'cross',
    layoutKind: ProgramProgressOverlayWidthKind,
    isClassRecord?: boolean,
    memberTagMembers?: ClassMemberRow[],
    applyBulkMemberTagRules?: boolean
  ) => React.HTMLAttributes<HTMLTableCellElement> & {
    ref: (el: HTMLTableCellElement | null) => void;
  };
  classRecordMemberTagMembersForRow: (row: AnnualProgressRow) => ClassMemberRow[];
  behaviorRecordMemberTagMembersForRow: (row: AnnualProgressRow) => ClassMemberRow[];
  selectRowByAnnualKey: (
    annualKey: string,
    options?: {
      forceCenter?: boolean;
      ensureVisible?: boolean;
      anchorField?: AnnualProgressFieldId;
      keyboardArrow?: AnnualProgressKeyboardArrow;
    }
  ) => void;
  selectBehaviorCellByAnnualKey: (annualKey: string) => void;
  openBehaviorPopup: (annualCellKey: string) => void;
  openProgramRecordModal: (annualCellKey: string, kind: AnnualProgressProgramRecordModalKind) => void;
  learningHasSlidePages: boolean;
  learningHasWorksheet: boolean;
  learningHasMemoNote: boolean;
  learningDisplayText: string;
  learningDisplayNote: string;
  presentationLockedAnnualCellKeys: Set<string>;
}) {
  const unitSelected = isRowSelection;
  const learningSelected = isRowSelection;
  const pageSelected = isRowSelection;
  const assistantPageSelected = isRowSelection;
  const currentPeriodSelected = isRowSelection;
  const totalPeriodSelected = isRowSelection;
  const materialSelected = isRowSelection;
  const crossSelected = isRowSelection;
  const memoSelected = isRowSelection;
  const evalSelected = isRowSelection;
  const classSelected = isRowSelection;
  const selectedMulti = isRowSelection;
  const behaviorSelected = isBehaviorSelection;
  /** 선택 행 맥락: 일정 열·비선택 열에 프로그램 표와 동일한 회색 워시 */
  const rowContextWash = isRowSelection || isBehaviorSelection;
  const contentAreaContextWash = isBehaviorSelection;
  const behaviorContextWash = isRowSelection;
  const renderRowContextWash = () =>
    rowContextWash ? <span className="cp-annual-timetable-week-context-wash" aria-hidden /> : null;
  const renderContentContextWash = () =>
    contentAreaContextWash ? <span className="cp-annual-timetable-week-context-wash" aria-hidden /> : null;
  const renderBehaviorContextWash = () =>
    behaviorContextWash ? <span className="cp-annual-timetable-week-context-wash" aria-hidden /> : null;
  const scheduleWashClass = rowContextWash ? 'cp-program-progress-row-context' : '';
  const contentWashClass = contentAreaContextWash ? 'cp-program-progress-row-context' : '';
  const behaviorWashClass = behaviorContextWash ? 'cp-program-progress-row-context' : '';
  const unitEdgeClasses = unitSelected ? rowFieldEdgeClassById.unit : '';
  const learningEdgeClasses = learningSelected ? rowFieldEdgeClassById.learning : '';
  const pageEdgeClasses = pageSelected ? rowFieldEdgeClassById.page : '';
  const assistantPageEdgeClasses = assistantPageSelected ? rowFieldEdgeClassById.assistantPage : '';
  const currentPeriodEdgeClasses = currentPeriodSelected ? rowFieldEdgeClassById.currentPeriod : '';
  const totalPeriodEdgeClasses = totalPeriodSelected ? rowFieldEdgeClassById.totalPeriod : '';
  const materialEdgeClasses = materialSelected ? rowFieldEdgeClassById.material : '';
  const crossEdgeClasses = crossSelected ? rowFieldEdgeClassById.cross : '';
  const memoEdgeClasses = memoSelected ? rowFieldEdgeClassById.memo : '';
  const evalEdgeClasses = evalSelected ? rowFieldEdgeClassById.eval : '';
  const classEdgeClasses = classSelected ? rowFieldEdgeClassById.class : '';
  const behaviorEdgeClasses = behaviorSelected
    ? 'cp-base-timetable-cell-edge-top cp-base-timetable-cell-edge-bottom cp-base-timetable-cell-edge-left cp-base-timetable-cell-edge-right'
    : '';
  /** 비수업/활동: 단원~준비물에도 시간표 열과 동일 계열 배경 확장. 공휴/휴업/방학은 종류별 배경 */
  const programContentAreaBgClass = (() => {
    if (row.timetableSlotKind === 'non_class') {
      return 'cp-annual-progress-nonclass-program-bg';
    }
    if (row.timetableSlotKind === 'activity') {
      return 'cp-annual-progress-activity-program-bg';
    }
    if (row.timetableSlotKind !== 'holiday') return '';
    const k = row.calendarOffDayKind;
    if (k === 'closure') return 'cp-annual-progress-closure-program-bg';
    if (k === 'vacation') return 'cp-annual-progress-vacation-program-bg';
    return 'cp-annual-progress-holiday-program-bg';
  })();
  const customBgClass = (field: keyof ProgramProgressBgColorByField): string =>
    programCellBgRenderProps(row.progressBgByField[field]).className;
  const customBgStyle = (field: keyof ProgramProgressBgColorByField): React.CSSProperties | undefined =>
    programCellBgRenderProps(row.progressBgByField[field]).style;
  const learningCrossSuffixText = row.progressCross.trim()
    ? `[${row.progressCross.trim()}]${row.progressCrossNote.trim() ? ` ${row.progressCrossNote.trim()}` : ''}`
    : row.progressCrossNote.trim();
  const showLearningPresentationOverlay = presentationLockedAnnualCellKeys.has(row.annualCellKey);

  return (
    <tr data-annual-progress-row="true" data-row-index={rowIndex}>
      <td className={['cp-annual-progress-col-seq', scheduleWashClass].filter(Boolean).join(' ')}>
        {renderRowContextWash()}
        {row.seq}
      </td>
      <td className={['cp-annual-progress-col-date', scheduleWashClass].filter(Boolean).join(' ')}>
        {renderRowContextWash()}
        {row.dateLabel}
      </td>
      <td className={['cp-annual-progress-col-weekday', scheduleWashClass].filter(Boolean).join(' ')}>
        {renderRowContextWash()}
        {row.weekdayLabel}
      </td>
      <td
        className={['cp-annual-progress-col-period', scheduleWashClass].filter(Boolean).join(' ')}
        title={row.periodLabel}
      >
        {renderRowContextWash()}
        {row.periodLabel}
      </td>
      <td className={['cp-annual-progress-col-grade', scheduleWashClass].filter(Boolean).join(' ')}>
        {renderRowContextWash()}
        {row.gradeLabel}
      </td>
      <td className={['cp-annual-progress-col-semester', scheduleWashClass].filter(Boolean).join(' ')}>
        {renderRowContextWash()}
        {row.semesterLabel}
      </td>
      <td
        className={[
          'cp-annual-progress-col-template',
          row.templateLabel.length > 2 ? 'cp-annual-progress-col-template-shrink' : '',
          scheduleWashClass,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {renderRowContextWash()}
        {row.templateLabel}
      </td>
      <td className={['cp-annual-progress-col-program', scheduleWashClass].filter(Boolean).join(' ')}>
        {renderRowContextWash()}
        {row.programKindLabel}
      </td>
      <td
        className={[
          'cp-annual-progress-col-timetable',
          row.timetableTextShrink ? 'cp-annual-progress-col-timetable-shrink' : '',
          row.timetableColorHex ? 'cp-base-timetable-cell-has-bg' : '',
          row.programKindLabel === '프로' ? 'cp-annual-project-cell' : '',
          !row.timetableColorHex && row.timetableSlotKind === 'out_of_term' ? 'cp-weekly-cell-out-of-range' : '',
          !row.timetableColorHex && row.timetableSlotKind === 'holiday' ? 'cp-weekly-cell-holiday' : '',
          row.timetableSlotKind === 'activity' ? 'cp-weekly-cell-activity-hatch' : '',
          row.timetableSlotKind === 'non_class' ? 'cp-weekly-cell-unused-kind' : '',
          row.timetableSlotKind === 'non_class' ? 'cp-weekly-cell-no-class' : '',
          scheduleWashClass,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {renderRowContextWash()}
        {row.timetableColorHex ? (
          <div
            className={
              'cp-base-timetable-cell-bg' +
              (row.programKindLabel === '프로' ? ' cp-annual-project-grid-fill' : '') +
              (row.timetableSlotKind === 'non_class'
                ? ' cp-base-timetable-cell-bg--non-class-stripes-over-fill'
                : row.timetableSlotKind === 'activity'
                  ? ' cp-base-timetable-cell-bg--activity-hatch-over-fill'
                  : row.programKindLabel === '고정'
                    ? ' cp-base-timetable-cell-bg--fixed-program-hatch-over-fill'
                    : '')
            }
            style={{ backgroundColor: row.timetableColorHex }}
            aria-hidden
          />
        ) : null}
        <span className="cp-annual-progress-timetable-text">{row.timetableText}</span>
      </td>
      <td
        className={[
          'cp-annual-progress-col-unit',
          'cp-annual-progress-read-note-cell',
          programContentAreaBgClass,
          customBgClass('unit_text'),
          row.progressUnitNote.trim() ? 'cp-program-unit-has-criterion' : '',
          unitSelected && selectedMulti ? 'cp-base-timetable-cell-selected cp-base-timetable-cell-selected-multi' : '',
          unitEdgeClasses,
          contentWashClass,
        ]
          .filter(Boolean)
          .join(' ')}
        style={customBgStyle('unit_text')}
        onMouseDown={(e) => {
          e.preventDefault();
          selectRowByAnnualKey(row.annualCellKey, { anchorField: 'unit' });
        }}
        {...annualNoteCellHandlers(`${row.annualCellKey}\tunit`, row.progressUnitNote, 'unit', 'unit')}
      >
        {renderContentContextWash()}
        <span className="cp-annual-progress-unit-learning-text" style={{ fontSize: displayFontSize(row.progressUnit), lineHeight: 1.25 }}>
          {row.progressUnit}
        </span>
      </td>
      <td
        className={[
          'cp-annual-progress-col-learning',
          'cp-annual-progress-read-note-cell',
          programContentAreaBgClass,
          customBgClass('learning_text'),
          learningHasMemoNote || learningDisplayNote.trim() ? 'cp-program-note-has-note' : '',
          learningHasSlidePages ? 'cp-program-slide-has-pages' : '',
          learningHasWorksheet ? 'cp-program-worksheet-has-document' : '',
          learningSelected && selectedMulti ? 'cp-base-timetable-cell-selected cp-base-timetable-cell-selected-multi' : '',
          learningEdgeClasses,
          showLearningPresentationOverlay ? 'cp-program-learning-cell-presentation-locked' : '',
          contentWashClass,
        ]
          .filter(Boolean)
          .join(' ')}
        style={customBgStyle('learning_text')}
        onMouseDown={(e) => {
          e.preventDefault();
          selectRowByAnnualKey(row.annualCellKey, { anchorField: 'learning' });
        }}
        {...annualNoteCellHandlers(`${row.annualCellKey}\tlearning`, learningDisplayNote, 'note', 'memo_narrow')}
      >
        {renderContentContextWash()}
        {showLearningPresentationOverlay ? (
          <div className="cp-program-learning-presentation-overlay" aria-hidden>
            <span className="cp-program-learning-presentation-overlay-label">프레젠테이션 중</span>
          </div>
        ) : null}
        {learningCrossSuffixText ? (
          <span className="cp-program-learning-combined-text cp-annual-progress-unit-learning-text" style={{ fontSize: displayFontSize(learningDisplayText), lineHeight: 1.25 }}>
            {learningDisplayText}
            <span className="cp-program-learning-cross-suffix">
              {learningDisplayText ? ' ' : ''}
              {learningCrossSuffixText}
            </span>
          </span>
        ) : (
          <span className="cp-annual-progress-unit-learning-text" style={{ fontSize: displayFontSize(learningDisplayText), lineHeight: 1.25 }}>
            {learningDisplayText}
          </span>
        )}
      </td>
      <td
        className={[
          'cp-annual-progress-col-metric',
          programContentAreaBgClass,
          customBgClass('page_text'),
          pageSelected && selectedMulti ? 'cp-base-timetable-cell-selected cp-base-timetable-cell-selected-multi' : '',
          pageEdgeClasses,
          contentWashClass,
        ]
          .filter(Boolean)
          .join(' ')}
        style={customBgStyle('page_text')}
        onMouseDown={(e) => {
          e.preventDefault();
          selectRowByAnnualKey(row.annualCellKey, { anchorField: 'page' });
        }}
      >
        {renderContentContextWash()}
        {row.progressPage}
      </td>
      <td
        className={[
          'cp-annual-progress-col-metric',
          programContentAreaBgClass,
          customBgClass('assistant_page_text'),
          assistantPageSelected && selectedMulti ? 'cp-base-timetable-cell-selected cp-base-timetable-cell-selected-multi' : '',
          assistantPageEdgeClasses,
          contentWashClass,
        ]
          .filter(Boolean)
          .join(' ')}
        style={customBgStyle('assistant_page_text')}
        onMouseDown={(e) => {
          e.preventDefault();
          selectRowByAnnualKey(row.annualCellKey, { anchorField: 'assistantPage' });
        }}
      >
        {renderContentContextWash()}
        {row.progressAssistantPage}
      </td>
      <td
        className={[
          'cp-annual-progress-col-metric-period',
          programContentAreaBgClass,
          customBgClass('current_period_text'),
          currentPeriodSelected && selectedMulti ? 'cp-base-timetable-cell-selected cp-base-timetable-cell-selected-multi' : '',
          currentPeriodEdgeClasses,
          contentWashClass,
        ]
          .filter(Boolean)
          .join(' ')}
        style={customBgStyle('current_period_text')}
        onMouseDown={(e) => {
          e.preventDefault();
          selectRowByAnnualKey(row.annualCellKey, { anchorField: 'currentPeriod' });
        }}
      >
        {renderContentContextWash()}
        {row.progressCurrentPeriod}
      </td>
      <td
        className={[
          'cp-annual-progress-col-metric-period',
          programContentAreaBgClass,
          customBgClass('total_period_text'),
          totalPeriodSelected && selectedMulti ? 'cp-base-timetable-cell-selected cp-base-timetable-cell-selected-multi' : '',
          totalPeriodEdgeClasses,
          contentWashClass,
        ]
          .filter(Boolean)
          .join(' ')}
        style={customBgStyle('total_period_text')}
        onMouseDown={(e) => {
          e.preventDefault();
          selectRowByAnnualKey(row.annualCellKey, { anchorField: 'totalPeriod' });
        }}
      >
        {renderContentContextWash()}
        {row.progressTotalPeriod}
      </td>
      <td
        className={[
          'cp-annual-progress-col-metric',
          programContentAreaBgClass,
          customBgClass('material_text'),
          materialSelected && selectedMulti ? 'cp-base-timetable-cell-selected cp-base-timetable-cell-selected-multi' : '',
          materialEdgeClasses,
          contentWashClass,
        ]
          .filter(Boolean)
          .join(' ')}
        style={customBgStyle('material_text')}
        onMouseDown={(e) => {
          e.preventDefault();
          selectRowByAnnualKey(row.annualCellKey, { anchorField: 'material' });
        }}
      >
        {renderContentContextWash()}
        {row.progressMaterial}
      </td>
      <td
        className={[
          'cp-annual-progress-col-record',
          'cp-annual-progress-col-record-fill',
          'cp-annual-progress-read-note-cell',
          row.progressCrossNote.trim() ? 'cp-program-cross-has-content' : '',
          crossSelected && selectedMulti ? 'cp-base-timetable-cell-selected cp-base-timetable-cell-selected-multi' : '',
          crossEdgeClasses,
          contentWashClass,
        ]
          .filter(Boolean)
          .join(' ')}
        onMouseDown={(e) => {
          e.preventDefault();
          selectRowByAnnualKey(row.annualCellKey, { anchorField: 'cross' });
        }}
        {...annualNoteCellHandlers(`${row.annualCellKey}\tcross`, row.progressCrossNote, 'cross', 'cross')}
      >
        {renderContentContextWash()}
        {row.progressCross}
      </td>
      <td
        className={[
          'cp-annual-progress-col-record',
          'cp-annual-progress-col-record-fill',
          'cp-annual-progress-read-note-cell',
          row.displayMemoNote.trim() ? 'cp-program-note-has-note' : '',
          memoSelected && selectedMulti ? 'cp-base-timetable-cell-selected cp-base-timetable-cell-selected-multi' : '',
          memoEdgeClasses,
          contentWashClass,
        ]
          .filter(Boolean)
          .join(' ')}
        onMouseDown={(e) => {
          e.preventDefault();
          selectRowByAnnualKey(row.annualCellKey, { anchorField: 'memo' });
        }}
        {...annualNoteCellHandlers(`${row.annualCellKey}\tmemo`, row.displayMemoNote, 'note', 'memo_narrow')}
      >
        {renderContentContextWash()}
        {row.displayMemoPrimary}
      </td>
      <td
        className={[
          'cp-annual-progress-col-record',
          'cp-annual-progress-col-record-fill',
          'cp-annual-progress-read-note-cell',
          'cp-annual-progress-read-eval-cell',
          row.evalRecordTarget ? 'is-record-openable' : '',
          row.progressEvalNote.trim() ? 'cp-program-note-has-note' : '',
          evalSelected && selectedMulti ? 'cp-base-timetable-cell-selected cp-base-timetable-cell-selected-multi' : '',
          evalEdgeClasses,
          contentWashClass,
        ]
          .filter(Boolean)
          .join(' ')}
        onMouseDown={(e) => {
          e.preventDefault();
          selectRowByAnnualKey(row.annualCellKey, { anchorField: 'eval' });
        }}
        onClick={row.evalRecordTarget ? () => openProgramRecordModal(row.annualCellKey, 'eval') : undefined}
        {...annualNoteCellHandlers(`${row.annualCellKey}\teval`, row.progressEvalNote, 'note', 'memo_wide')}
      >
        {renderContentContextWash()}
        {row.progressEval || row.progressEvalCode ? (
          <span className={`cp-program-eval-inline cp-program-eval-inline-${row.progressEvalCode.trim() || '0'}`}>{row.progressEval}</span>
        ) : null}
      </td>
      <td
        className={[
          'cp-annual-progress-col-suhub-girog',
          'cp-annual-progress-col-record-fill',
          'cp-annual-progress-td-class-rec-visual',
          'cp-annual-progress-read-note-cell',
          'cp-annual-progress-read-class-rec-cell',
          row.classRecordTarget ? 'is-record-openable' : '',
          row.progressClassRecordNote.trim() ? 'cp-program-note-has-note' : '',
          classSelected && selectedMulti ? 'cp-base-timetable-cell-selected cp-base-timetable-cell-selected-multi' : '',
          classEdgeClasses,
          contentWashClass,
        ]
          .filter(Boolean)
          .join(' ')}
        onMouseDown={(e) => {
          e.preventDefault();
          selectRowByAnnualKey(row.annualCellKey, { anchorField: 'class' });
        }}
        onClick={row.classRecordTarget ? () => openProgramRecordModal(row.annualCellKey, 'class') : undefined}
        {...annualNoteCellHandlers(
          `${row.annualCellKey}\tclass`,
          row.progressClassRecordNote,
          'note',
          'memo_wide',
          true,
          classRecordMemberTagMembersForRow(row),
          !!row.progressClassRecordMemberGroupId
        )}
      >
        {renderContentContextWash()}
        {row.progressClassRecordKind !== 'empty' ? (
          <span className={`cp-program-class-rec-inline cp-program-class-rec-inline-${row.progressClassRecordKind}`}>{row.progressClassRecord}</span>
        ) : null}
      </td>
      <td
        className={[
          'cp-annual-progress-col-log',
          'cp-annual-progress-read-note-cell',
          'is-record-openable',
          row.progressBehaviorNote.trim() ? 'cp-program-note-has-note' : '',
          behaviorSelected ? 'cp-base-timetable-cell-selected cp-base-timetable-cell-selected-multi' : '',
          behaviorEdgeClasses,
          behaviorWashClass,
        ]
          .filter(Boolean)
          .join(' ')}
        onMouseDown={(e) => {
          e.preventDefault();
          selectBehaviorCellByAnnualKey(row.annualCellKey);
        }}
        onClick={() => {
          openBehaviorPopup(row.annualCellKey);
        }}
        {...annualNoteCellHandlers(
          `${row.annualCellKey}\tbehavior`,
          row.progressBehaviorNote,
          'note',
          'memo_wide',
          true,
          behaviorRecordMemberTagMembersForRow(row),
          !!row.progressBehaviorRecordMemberGroupId
        )}
      >
        {renderBehaviorContextWash()}
        {row.progressBehaviorNote.trim() ? (
          <span className="cp-program-class-rec-inline cp-program-class-rec-inline-behavior">행동특성</span>
        ) : null}
      </td>
    </tr>
  );
});

const AnnualProgressPage: React.FC<AnnualProgressPageProps> = ({
  workspaceId,
  grades,
  currentGradeId = '',
  termNumber,
  isActive = true,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const tutorial = useTutorial();
  const { isFeatureLocked, requireFeature } = usePaidPassGate();
  const presentationLocked = isFeatureLocked('presentation');
  const worksheetLocked = isFeatureLocked('worksheet');
  const behaviorLocked = isFeatureLocked('behavior_record');
  const passLockClassOf = (locked: boolean) => (locked ? ' is-pass-locked' : '');
  const [searchParams] = useSearchParams();
  const focusAnnualCellKeyFromUrl = searchParams.get('focusAnnualCellKey')?.trim() ?? '';
  const gradeIdFromUrl = searchParams.get('gradeId')?.trim() ?? '';
  const openTodayToken = searchParams.get('openToday')?.trim() ?? '';
  const [activeGradeTabId, setActiveGradeTabId] = React.useState('');
  const academicYear = useCalendarLinkedAcademicYear(
    workspaceId,
    (gradeIdFromUrl || currentGradeId || activeGradeTabId).trim()
  );
  const { schoolScopedCacheSyncReady, schoolScopedCacheSyncPending } = useSchoolScopedCacheSyncReady(
    workspaceId,
    isActive
  );
  const [programRefreshTick, setProgramRefreshTick] = React.useState(0);
  const [programCellsFetchNonce, setProgramCellsFetchNonce] = React.useState(0);
  const [annualAggregateRefreshNonce, setAnnualAggregateRefreshNonce] = React.useState(0);
  const [annualTimetableSaveReloadToken, setAnnualTimetableSaveReloadToken] = React.useState(0);
  const [programWorkspaceStateRev, setProgramWorkspaceStateRev] = React.useState(0);
  const [programDisplayOverlayRev, setProgramDisplayOverlayRev] = React.useState(0);
  const processedAnnualCellsSaveTokenRef = React.useRef(0);
  const processedProgramSaveTokenRef = React.useRef(0);

  const effectiveGradeId = gradeIdFromUrl || currentGradeId;
  const programGradeId = (effectiveGradeId || activeGradeTabId || grades[0]?.id || '').trim();
  const metaGradeId = programGradeId;

  const apPageData = useAnnualProgressPageQueries({
    workspaceId: schoolScopedCacheSyncReady ? workspaceId : '',
    grades,
    academicYear,
    termNumber,
    selectedGradeId: programGradeId,
    metaGradeId,
    isActive,
    schoolScopedCacheSyncReady,
    annualTimetableSaveReloadToken,
    programRefreshTick,
    annualAggregateRefreshNonce,
    programCellsFetchNonce,
  });

  const terms = apPageData.terms;
  const schedule = apPageData.schedule;
  const tracks = apPageData.tracks;
  const regularColors = apPageData.regularColors;
  const dedicatedColors = apPageData.dedicatedColors;
  const baseExcludeColors = apPageData.baseExcludeColors;
  const holidaySet = apPageData.holidaySet;
  const offDayByDate = apPageData.offDayByDate;
  const cellsByKey = apPageData.cellsByKey;
  const programCellsByKey = apPageData.programCellsByKey;
  const scopedRowsForTerm = apPageData.scopedRowsForTerm;
  const loading = apPageData.metaLoading;
  const annualCellsLoading = apPageData.annualCellsLoading;
  const programDataLoading = apPageData.programDataLoading;
  const [gradeTrackSections, setGradeTrackSections] = React.useState<PageSectionLayout[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  React.useEffect(() => {
    setError(apPageData.metaError);
  }, [apPageData.metaError]);

  const { setProgramFocusGradeId } = useProgramSidebar();
  type AnnualSelectionMode = 'row' | 'behavior';
  const [selectedAnnualKey, setSelectedAnnualKey] = React.useState<string>('');
  const [selectionMode, setSelectionMode] = React.useState<AnnualSelectionMode>('row');
  const selectedAnnualKeyRef = React.useRef<string>('');
  /** «오늘 학습내용» 버튼 연속 클릭 순환용(일반 행 선택 시 초기화). 단축키·Shift+R 진입은 시각만 사용. */
  const [todayLessonCycleAnchorKey, setTodayLessonCycleAnchorKey] = React.useState<string | null>(null);
  const [lastTomorrowSelectionKey, setLastTomorrowSelectionKey] = React.useState<string | null>(null);

  type AnnualReadHoverTooltipState = {
    anchorKey: string;
    kind: 'note' | 'unit' | 'cross';
    note: string;
    isClassRecord: boolean;
    memberTagMembers?: ClassMemberRow[];
    applyBulkMemberTagRules?: boolean;
    layoutKind: ProgramProgressOverlayWidthKind;
    style: React.CSSProperties;
  };
  const annualTableScrollRef = React.useRef<HTMLDivElement | null>(null);
  const annualTableHeadRef = React.useRef<HTMLTableSectionElement | null>(null);
  const annualTableBodyRef = React.useRef<HTMLTableSectionElement | null>(null);
  const annualCellAnchorRef = React.useRef<Map<string, HTMLTableCellElement>>(new Map());
  const [annualHoverTooltip, setAnnualHoverTooltip] = React.useState<AnnualReadHoverTooltipState | null>(null);
  const annualHoverTooltipRef = React.useRef<AnnualReadHoverTooltipState | null>(null);
  const annualHoverHideTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const annualHoverShowTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  React.useEffect(() => {
    annualHoverTooltipRef.current = annualHoverTooltip;
  }, [annualHoverTooltip]);
  const annualLearningPlanHoverAnchorKey =
    annualHoverTooltip?.kind === 'note' &&
    !annualHoverTooltip.isClassRecord &&
    learningPlanGuideHasPlanMarkers(annualHoverTooltip.note)
      ? annualHoverTooltip.anchorKey
      : null;
  React.useEffect(() => {
    if (!annualLearningPlanHoverAnchorKey) return;
    const anchorKey = annualLearningPlanHoverAnchorKey;
    const onMove = (event: MouseEvent) => {
      const target = event.target;
      const onPopup = target instanceof Element && !!target.closest('.cp-program-table-hover-memo-tooltip');
      const cell = annualCellAnchorRef.current.get(anchorKey);
      const onCell = !!cell && target instanceof Node && (target === cell || cell.contains(target));
      if (onPopup || onCell) {
        if (annualHoverHideTimerRef.current != null) {
          clearTimeout(annualHoverHideTimerRef.current);
          annualHoverHideTimerRef.current = null;
        }
        return;
      }
      if (annualHoverHideTimerRef.current != null) return;
      annualHoverHideTimerRef.current = setTimeout(() => {
        annualHoverHideTimerRef.current = null;
        setAnnualHoverTooltip((prev) => (prev?.anchorKey === anchorKey ? null : prev));
      }, LEARNING_PLAN_HOVER_TOOLTIP_DISMISS_MS);
    };
    document.addEventListener('mousemove', onMove);
    return () => document.removeEventListener('mousemove', onMove);
  }, [annualLearningPlanHoverAnchorKey]);
  React.useEffect(
    () => () => {
      if (annualHoverHideTimerRef.current != null) clearTimeout(annualHoverHideTimerRef.current);
      if (annualHoverShowTimerRef.current != null) clearTimeout(annualHoverShowTimerRef.current);
    },
    []
  );
  const [classMembers, setClassMembers] = React.useState<ClassMemberRow[]>([]);
  const [memberGradeLayout, setMemberGradeLayout] = React.useState<ClassMemberGradeLayout>(emptyGradeLayout());
  const [behaviorPopup, setBehaviorPopup] = React.useState<{
    annualCellKey: string;
  } | null>(null);
  const [programRecordModal, setProgramRecordModal] = React.useState<{
    annualCellKey: string;
    kind: AnnualProgressProgramRecordModalKind;
  } | null>(null);
  const [behaviorPopupStyle, setBehaviorPopupStyle] = React.useState<React.CSSProperties | null>(null);
  const behaviorPopupRef = React.useRef<HTMLDivElement | null>(null);
  const [behaviorSaveError, setBehaviorSaveError] = React.useState<string | null>(null);
  const [behaviorSaving, setBehaviorSaving] = React.useState(false);
  const [virtualScrollTop, setVirtualScrollTop] = React.useState(0);
  const virtualScrollTopRef = React.useRef(0);
  const [virtualViewportHeight, setVirtualViewportHeight] = React.useState(0);
  const handledOpenTodayTokenRef = React.useRef('');
  const pendingOpenTodayTokenRef = React.useRef('');
  const virtualRowHeight = ANNUAL_PROGRESS_DEFAULT_ROW_HEIGHT;
  const [tableHeadHeight, setTableHeadHeight] = React.useState(64);
  // openToday 진입: pendingOpenTodayTokenRef → rows 준비 후 selectRowByAnnualKey + burst 스크롤

  const [slidePresentationOverlayRev, setSlidePresentationOverlayRev] = React.useState(0);
  const [presentationLockedAnnualCellKeys, setPresentationLockedAnnualCellKeys] = React.useState<Set<string>>(
    () => new Set()
  );

  const refreshAnnualPresentationOverlayKeys = React.useCallback(() => {
    const sid = workspaceId?.trim();
    if (!sid) {
      setPresentationLockedAnnualCellKeys(new Set());
      return;
    }
    setPresentationLockedAnnualCellKeys(new Set(getPresentationOverlayAnnualCellKeys(sid)));
  }, [workspaceId]);

  React.useEffect(() => {
    refreshAnnualPresentationOverlayKeys();
  }, [refreshAnnualPresentationOverlayKeys, slidePresentationOverlayRev]);

  React.useEffect(() => {
    const sid = workspaceId?.trim();
    if (!sid) return;
    const id = window.setInterval(() => refreshAnnualPresentationOverlayKeys(), 20_000);
    return () => window.clearInterval(id);
  }, [refreshAnnualPresentationOverlayKeys, workspaceId]);

  React.useEffect(() => {
    const bump = () => setSlidePresentationOverlayRev((x) => x + 1);
    const lockPrefix = programSlideEditScopeLockStorageKeyPrefix();
    const overlayPrefix = programSlidePresentationOverlayStorageKeyPrefix();
    const onStorage = (e: StorageEvent) => {
      if (!e.key) return;
      if (e.key.startsWith(lockPrefix) || e.key.startsWith(overlayPrefix)) bump();
    };
    const onVis = () => {
      if (document.visibilityState === 'visible') bump();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', bump);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', bump);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  const recomputeAnnualHoverTooltip = React.useCallback(() => {
    setAnnualHoverTooltip((prev) => {
      if (!prev) return null;
      const el = annualCellAnchorRef.current.get(prev.anchorKey);
      if (!el) return null;
      return {
        ...prev,
        style:
          prev.kind === 'note' && !prev.isClassRecord && learningPlanGuideHasPlanMarkers(prev.note)
            ? computeLearningPlanHoverTooltipStyle(el.getBoundingClientRect())
            : computeProgramProgressHoverTooltipStyle(el.getBoundingClientRect(), prev.layoutKind),
      };
    });
  }, []);

  const recomputeBehaviorPopupStyle = React.useCallback(() => {
    if (!behaviorPopup) {
      setBehaviorPopupStyle(null);
      return;
    }
    const anchorKey = `${behaviorPopup.annualCellKey}\tbehavior`;
    const anchorEl = annualCellAnchorRef.current.get(anchorKey);
    if (!anchorEl) {
      setBehaviorPopupStyle(null);
      return;
    }
    const r = anchorEl.getBoundingClientRect();
    const hostRect =
      resolveProgramTableFloatingHostRect(annualTableScrollRef.current) ??
      annualTableScrollRef.current?.getBoundingClientRect() ??
      null;
    const rawMeasured = behaviorPopupRef.current?.offsetWidth ?? 0;
    const measuredW = rawMeasured > 1 ? rawMeasured : null;
    setBehaviorPopupStyle(
      computeProgramTableFloatingPopupStyle({
        anchorRect: r,
        hostRect,
        preferredWidth: 520,
        measuredWidth: measuredW,
        alignX: 'left',
        zIndex: 2200,
      })
    );
  }, [behaviorPopup]);

  const annualNoteCellHandlers = React.useCallback(
    (
      anchorKey: string,
      note: string,
      tooltipKind: 'note' | 'unit' | 'cross',
      layoutKind: ProgramProgressOverlayWidthKind,
      isClassRecord = false,
      memberTagMembers?: ClassMemberRow[],
      applyBulkMemberTagRules?: boolean
    ) => ({
      ref: (el: HTMLTableCellElement | null) => {
        const m = annualCellAnchorRef.current;
        if (el) m.set(anchorKey, el);
        else m.delete(anchorKey);
      },
      onMouseEnter: (e: React.MouseEvent<HTMLTableCellElement>) => {
        const n = (note ?? '').trim();
        if (!n) return;
        if (annualHoverShowTimerRef.current != null) {
          clearTimeout(annualHoverShowTimerRef.current);
          annualHoverShowTimerRef.current = null;
        }
        const openTip = annualHoverTooltipRef.current;
        if (
          openTip?.anchorKey === anchorKey &&
          openTip.kind === 'note' &&
          !openTip.isClassRecord &&
          learningPlanGuideHasPlanMarkers(openTip.note)
        ) {
          if (annualHoverHideTimerRef.current != null) {
            clearTimeout(annualHoverHideTimerRef.current);
            annualHoverHideTimerRef.current = null;
          }
        }
        const r = e.currentTarget.getBoundingClientRect();
        const payload: AnnualReadHoverTooltipState = {
          anchorKey,
          kind: tooltipKind,
          note,
          isClassRecord,
          memberTagMembers: isClassRecord ? memberTagMembers : undefined,
          applyBulkMemberTagRules: isClassRecord ? applyBulkMemberTagRules : undefined,
          layoutKind,
          style:
            tooltipKind === 'note' && !isClassRecord && learningPlanGuideHasPlanMarkers(note)
              ? computeLearningPlanHoverTooltipStyle(r)
              : computeProgramProgressHoverTooltipStyle(r, layoutKind),
        };
        if (anchorKey.endsWith('\tlearning')) {
          annualHoverShowTimerRef.current = setTimeout(() => {
            annualHoverShowTimerRef.current = null;
            setAnnualHoverTooltip(payload);
          }, PROGRAM_TABLE_CELL_HOVER_TOOLTIP_DELAY_MS);
          return;
        }
        setAnnualHoverTooltip(payload);
      },
      onMouseLeave: () => {
        if (annualHoverShowTimerRef.current != null) {
          clearTimeout(annualHoverShowTimerRef.current);
          annualHoverShowTimerRef.current = null;
        }
        const cur = annualHoverTooltipRef.current;
        const keepForScroll =
          cur?.anchorKey === anchorKey &&
          cur.kind === 'note' &&
          !cur.isClassRecord &&
          learningPlanGuideHasPlanMarkers(cur.note);
        if (!keepForScroll) {
          setAnnualHoverTooltip((prev) => (prev?.anchorKey === anchorKey ? null : prev));
          return;
        }
        if (annualHoverHideTimerRef.current == null) {
          annualHoverHideTimerRef.current = setTimeout(() => {
            annualHoverHideTimerRef.current = null;
            setAnnualHoverTooltip((prev) => (prev?.anchorKey === anchorKey ? null : prev));
          }, LEARNING_PLAN_HOVER_TOOLTIP_DISMISS_MS);
        }
      },
    }),
    []
  );

  /** 프로그램 진도표와 동일: 표시 문자열 길이에 따라 본문 폰트 크기 축소 */
  const displayFontSize = React.useCallback((value: string) => {
    const len = (value ?? '').trim().length;
    if (len >= 22) return '0.58rem';
    if (len >= 16) return '0.64rem';
    if (len >= 10) return '0.69rem';
    return '0.75rem';
  }, []);

  React.useEffect(() => {
    if (!isActive) return;
    const scrollEl = annualTableScrollRef.current;
    const onMove = () => {
      recomputeAnnualHoverTooltip();
      recomputeBehaviorPopupStyle();
    };
    scrollEl?.addEventListener('scroll', onMove, { passive: true });
    window.addEventListener('scroll', onMove, { passive: true, capture: true });
    window.addEventListener('resize', onMove);
    return () => {
      scrollEl?.removeEventListener('scroll', onMove);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [isActive, recomputeAnnualHoverTooltip, recomputeBehaviorPopupStyle]);

  const syncAnnualTableViewport = React.useCallback(() => {
    const wrap = annualTableScrollRef.current;
    if (!wrap) return;
    virtualScrollTopRef.current = wrap.scrollTop;
    setVirtualScrollTop((prev) => (prev === wrap.scrollTop ? prev : wrap.scrollTop));
    setVirtualViewportHeight((prev) => (prev === wrap.clientHeight ? prev : wrap.clientHeight));
  }, []);

  React.useEffect(() => {
    if (!isActive) return;
    const wrap = annualTableScrollRef.current;
    let rafId = 0;
    const scheduleSync = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        syncAnnualTableViewport();
        const nextHeadHeight = annualTableHeadRef.current?.offsetHeight ?? 64;
        if (nextHeadHeight > 0) {
          setTableHeadHeight((prev) => (prev === nextHeadHeight ? prev : nextHeadHeight));
        }
      });
    };
    scheduleSync();
    wrap?.addEventListener('scroll', scheduleSync, { passive: true });
    window.addEventListener('resize', scheduleSync);
    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      wrap?.removeEventListener('scroll', scheduleSync);
      window.removeEventListener('resize', scheduleSync);
    };
  }, [isActive, syncAnnualTableViewport]);

  React.useLayoutEffect(() => {
    recomputeBehaviorPopupStyle();
  }, [recomputeBehaviorPopupStyle]);

  React.useLayoutEffect(() => {
    if (!behaviorPopup) return;
    const raf = window.requestAnimationFrame(() => {
      recomputeBehaviorPopupStyle();
    });
    return () => window.cancelAnimationFrame(raf);
  }, [behaviorPopup, recomputeBehaviorPopupStyle]);

  React.useLayoutEffect(() => {
    if (isActive) {
      releaseTrappedKeepAliveFocus();
      return;
    }
    setAnnualHoverTooltip(null);
    setBehaviorPopup(null);
    setBehaviorPopupStyle(null);
    setBehaviorSaveError(null);
    const wrap = annualTableScrollRef.current;
    if (wrap && document.activeElement === wrap) {
      wrap.blur();
    }
    releaseTrappedKeepAliveFocus();
  }, [isActive]);

  React.useEffect(() => {
    selectedAnnualKeyRef.current = selectedAnnualKey;
  }, [selectedAnnualKey]);

  const classEntries = React.useMemo(() => buildAnnualClassEntries(schedule, grades), [schedule, grades]);
  const visibleEntries = React.useMemo(() => {
    if (!effectiveGradeId) return classEntries;
    return classEntries.filter((entry) => entry.gradeId === effectiveGradeId);
  }, [classEntries, effectiveGradeId]);
  /** 시간표명(또는 편제명) → 표시용 편제명 (tracks: name=편제명, description=시간표명) */
  const trackNameByTimetableTextByGrade = React.useMemo(() => {
    const map: Record<string, Record<string, string>> = {};
    tracks.forEach((row) => {
      const gradeId = (row.grade_id ?? '').trim();
      const trackName = (row.name ?? '').trim();
      const timetableName = (row.description ?? '').trim();
      if (!gradeId || !trackName) return;
      if (!map[gradeId]) map[gradeId] = {};
      map[gradeId][trackName] = trackName;
      if (timetableName) map[gradeId][timetableName] = trackName;
    });
    return map;
  }, [tracks]);
  React.useEffect(() => {
    if (effectiveGradeId) {
      setActiveGradeTabId(effectiveGradeId);
      return;
    }
    if (visibleEntries.length === 0) {
      setActiveGradeTabId('');
      return;
    }
    if (visibleEntries.some((entry) => entry.gradeId === activeGradeTabId)) return;
    setActiveGradeTabId(visibleEntries[0].gradeId);
  }, [activeGradeTabId, effectiveGradeId, visibleEntries]);

  const selectedEntry = React.useMemo<AnnualClassEntry | null>(() => {
    if (visibleEntries.length === 0) return null;
    if (effectiveGradeId) return visibleEntries[0] ?? null;
    return visibleEntries.find((entry) => entry.gradeId === activeGradeTabId) ?? visibleEntries[0] ?? null;
  }, [activeGradeTabId, effectiveGradeId, visibleEntries]);

  /**
   * 편제 모드(sync/unified) LS는 다른 화면에서 갱신·이벤트로 바뀐다.
   * keep-alive 연간진도표가 구독하지 않으면 stale `unified`로 남아 51/52과학 등이
   * 한 occurrence로 합쳐져 보인다. ProgramTrackPage와 동일하게 rev+이벤트+활성화 시 재시드.
   */
  const [programPrefsRev, setProgramPrefsRev] = React.useState(0);
  const prefsGradeId = (selectedEntry?.gradeId ?? effectiveGradeId ?? '').trim();

  const syncProgramPrefsToLocalStores = React.useCallback(() => {
    if (!workspaceId || !prefsGradeId) return;
    const cached = loadGradeTrackProgramPreferencesCache(workspaceId);
    if (cached && applyGradeTrackProgramPrefsToLocalStores(workspaceId, prefsGradeId, cached)) {
      setProgramPrefsRev((r) => r + 1);
    }
  }, [prefsGradeId, workspaceId]);

  React.useEffect(() => {
    if (!isActive || !workspaceId || !prefsGradeId) return;
    syncProgramPrefsToLocalStores();
    let cancelled = false;
    void getGradeTrackProgramPreferences(workspaceId)
      .then((prefs) => {
        if (cancelled) return;
        if (applyGradeTrackProgramPrefsToLocalStores(workspaceId, prefsGradeId, prefs)) {
          setProgramPrefsRev((r) => r + 1);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [isActive, prefsGradeId, syncProgramPrefsToLocalStores, workspaceId]);

  React.useEffect(() => {
    const onTrackProgramSplitChanged = () => {
      setProgramPrefsRev((r) => r + 1);
    };
    const onAnnualPrerequisiteRefresh = () => {
      syncProgramPrefsToLocalStores();
    };
    window.addEventListener(TRACK_PROGRAM_SPLIT_CHANGE_EVENT, onTrackProgramSplitChanged);
    window.addEventListener(ANNUAL_PREREQUISITE_REFRESH_EVENT, onAnnualPrerequisiteRefresh);
    return () => {
      window.removeEventListener(TRACK_PROGRAM_SPLIT_CHANGE_EVENT, onTrackProgramSplitChanged);
      window.removeEventListener(ANNUAL_PREREQUISITE_REFRESH_EVENT, onAnnualPrerequisiteRefresh);
    };
  }, [syncProgramPrefsToLocalStores]);

  const trackProgramModeByTrack = React.useMemo(() => {
    void programPrefsRev;
    if (!workspaceId || !prefsGradeId) return {};
    return loadTrackProgramModeMap(workspaceId, prefsGradeId);
  }, [prefsGradeId, programPrefsRev, workspaceId]);
  const trackProgramSyncRepByTrack = React.useMemo(() => {
    void programPrefsRev;
    if (!workspaceId || !prefsGradeId) return {};
    return loadTrackProgramSyncRepMap(workspaceId, prefsGradeId);
  }, [prefsGradeId, programPrefsRev, workspaceId]);
  const selectedGradeTrackTimetableContext = React.useMemo(() => {
    const gid = (selectedEntry?.gradeId ?? '').trim();
    if (!gid) return null;
    return buildGradeTrackTimetableContext(workspaceId, gid, tracks, gradeTrackSections);
  }, [gradeTrackSections, selectedEntry?.gradeId, tracks, workspaceId]);

  const subscribedTracksForClass = React.useMemo(() => {
    const gid = (selectedEntry?.gradeId ?? programGradeId).trim();
    if (!gid) return [];
    return filterTracksSubscribedToGradeTrackSections(gradeTrackSections, gid, tracks);
  }, [gradeTrackSections, programGradeId, selectedEntry?.gradeId, tracks]);

  const subscribedTrackNamesForClass = React.useMemo(
    () => subscribedTracksForClass.map((track) => (track.name ?? '').trim()).filter(Boolean),
    [subscribedTracksForClass]
  );

  React.useEffect(() => {
    const gid = selectedEntry?.gradeId ?? '';
    if (!workspaceId || !gid) {
      setClassMembers([]);
      setMemberGradeLayout(emptyGradeLayout());
      return undefined;
    }
    const layoutLs = loadClassMemberGradeLayoutFromStorage(workspaceId, gid);
    if (layoutLs) {
      setMemberGradeLayout(layoutLs);
      setClassMembers(flattenGradeLayout(layoutLs));
    } else {
      setClassMembers([]);
      setMemberGradeLayout(emptyGradeLayout());
    }
    const hasSnapshot = hasClassMemberLocalStorageSnapshot(workspaceId, gid);
    if (hasSnapshot && layoutLs != null) {
      if (process.env.NODE_ENV === 'development') {
        recordDevLocalStorageProgramCacheUse('학급멤버·LS');
      }
      return undefined;
    }
    let cancelled = false;
    void getClassMemberGradeLayoutsMap(workspaceId, [gid])
      .then((m) => {
        if (cancelled) return;
        const lay = m[gid];
        if (lay) {
          setMemberGradeLayout(lay);
          setClassMembers(flattenGradeLayout(lay));
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [workspaceId, selectedEntry?.gradeId]);

  React.useEffect(() => {
    const gid = selectedEntry?.gradeId ?? '';
    const onChanged = (event: Event) => {
      const detail = (event as CustomEvent<{ workspaceId?: string; gradeId?: string }>).detail;
      if (!workspaceId || !gid) return;
      if (detail?.workspaceId !== workspaceId || detail?.gradeId !== gid) return;
      const lay = loadClassMemberGradeLayoutFromStorage(workspaceId, gid) ?? emptyGradeLayout();
      setMemberGradeLayout(lay);
      setClassMembers(flattenGradeLayout(lay));
    };
    window.addEventListener(CLASS_MEMBERS_CHANGED_EVENT, onChanged as EventListener);
    return () => window.removeEventListener(CLASS_MEMBERS_CHANGED_EVENT, onChanged as EventListener);
  }, [workspaceId, selectedEntry?.gradeId]);

  const [annualProjectProgramsForSelectedGrade, setAnnualProjectProgramsForSelectedGrade] = React.useState<
    AnnualProjectProgram[]
  >([]);
  React.useEffect(() => {
    const gid = selectedEntry?.gradeId ?? '';
    if (!workspaceId || !gid) {
      setAnnualProjectProgramsForSelectedGrade([]);
      return;
    }
    let cancelled = false;
    setAnnualProjectProgramsForSelectedGrade(readAnnualProjectProgramsCache(workspaceId, gid, academicYear));
    void getAnnualProjectPrograms(workspaceId, gid, academicYear)
      .then((rows) => {
        if (!cancelled) setAnnualProjectProgramsForSelectedGrade(rows);
      })
      .catch(() => {});
    const onChanged = (
      event: Event
    ) => {
      const detail = (event as CustomEvent<{ workspaceId?: string; gradeId?: string; academicYear?: number }>).detail;
      if (detail?.workspaceId !== workspaceId || detail?.gradeId !== gid || detail?.academicYear !== academicYear) return;
      setAnnualProjectProgramsForSelectedGrade(readAnnualProjectProgramsCache(workspaceId, gid, academicYear));
    };
    window.addEventListener(ANNUAL_PROJECT_PROGRAMS_CHANGE_EVENT, onChanged as EventListener);
    return () => {
      cancelled = true;
      window.removeEventListener(ANNUAL_PROJECT_PROGRAMS_CHANGE_EVENT, onChanged as EventListener);
    };
  }, [academicYear, workspaceId, selectedEntry?.gradeId]);

  React.useEffect(() => {
    if (!selectedEntry?.gradeId) return;
    // 통합 보기(네비·URL 모두 비어 있음)에서는 탭/캐시가 사이드바 포커스를 다시 잡지 않음
    if (!gradeIdFromUrl.trim() && !currentGradeId.trim()) return;
    setProgramFocusGradeId(selectedEntry.gradeId);
  }, [selectedEntry?.gradeId, setProgramFocusGradeId, gradeIdFromUrl, currentGradeId]);

  const [annualDataCleanupOpen, setAnnualDataCleanupOpen] = React.useState(false);
  const [crossTopicCountModalOpen, setCrossTopicCountModalOpen] = React.useState(false);
  const [crossTopicAiModalOpen, setCrossTopicAiModalOpen] = React.useState(false);
  /** AI 범교과 자동 구성: 이용권별 권한(AI 교육과정 자료 입력 기능)으로 노출. 관리자는 항상 허용(서버도 동일하게 검사) */
  const isCrossAiMaster = useAiAccess().canPlan('ai_curriculum_input');
  const [neisLearningUploadModalOpen, setNeisLearningUploadModalOpen] = React.useState(false);
  const [neisExportFormatPick, setNeisExportFormatPick] = React.useState<
    null | 'neis-class-timetable' | 'neis-learning-upload'
  >(null);
  const [neisShortcutExportBusy, setNeisShortcutExportBusy] = React.useState(false);
  const [presentationListOpen, setPresentationListOpen] = React.useState(false);
  const [programWorksheetListOpen, setProgramWorksheetListOpen] = React.useState(false);
  const [worksheetEditorOpen, setWorksheetEditorOpen] = React.useState(false);
  const [worksheetEditorAnnualKey, setWorksheetEditorAnnualKey] = React.useState<string | null>(null);
  const [worksheetEditorInitialDocument, setWorksheetEditorInitialDocument] =
    React.useState<LearningContentWorksheetDocumentV1 | null>(null);
  const [behaviorListOpen, setBehaviorListOpen] = React.useState(false);
  const [presentationListToast, setPresentationListToast] = React.useState('');
  const presentationListToastTimerRef = React.useRef<number | null>(null);
  const [programSaveToastState, setProgramSaveToastState] = React.useState<
    | {
        kind: 'saving' | 'success' | 'error';
        message: string;
      }
    | null
  >(null);
  const showPresentationListToast = React.useCallback((message: string, durationMs = 2800) => {
    if (presentationListToastTimerRef.current != null) {
      window.clearTimeout(presentationListToastTimerRef.current);
    }
    setPresentationListToast(message);
    presentationListToastTimerRef.current = window.setTimeout(() => {
      presentationListToastTimerRef.current = null;
      setPresentationListToast('');
    }, durationMs);
  }, []);
  React.useEffect(
    () => () => {
      if (presentationListToastTimerRef.current != null) {
        window.clearTimeout(presentationListToastTimerRef.current);
      }
    },
    []
  );
  const annualPresentationClipboardRef = React.useRef<{ owner: ProgramContentSlideOwnerRef } | null>(null);
  const programDataLoadedScopeRef = React.useRef('');
  const slidePresenceForceRefreshRef = React.useRef(false);
  const [slidePresenceReloadNonce, setSlidePresenceReloadNonce] = React.useState(0);
  const requestSlidePresenceReload = React.useCallback((forceRefresh = false) => {
    if (forceRefresh) slidePresenceForceRefreshRef.current = true;
    setSlidePresenceReloadNonce((n) => n + 1);
  }, []);

  React.useEffect(() => {
    processedAnnualCellsSaveTokenRef.current = 0;
    processedProgramSaveTokenRef.current = 0;
  }, [workspaceId, academicYear, termNumber]);

  React.useEffect(() => {
    if (!isActive) return;
    if (!schoolScopedCacheSyncReady) return;
    if (!workspaceId) {
      setGradeTrackSections([]);
      return;
    }
    let cancelled = false;
    void getGradeTrackSections(workspaceId)
      .then((rows) => {
        /** 내용이 같으면 새 배열로 바꾸지 않음 — 탐색 진입마다 행 전체 재계산 방지 */
        if (!cancelled) {
          setGradeTrackSections((prev) => (JSON.stringify(prev) === JSON.stringify(rows) ? prev : rows));
        }
      })
      .catch(() => {
        if (!cancelled) setGradeTrackSections([{ id: 'base', gradeIds: [] }]);
      });
    return () => {
      cancelled = true;
    };
  }, [isActive, schoolScopedCacheSyncReady, workspaceId]);

  /** keep-alive 비활성 중에도 저장 이벤트를 받아 scope ref·캐시를 갱신한다 */
  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onProgramSaved = (e: Event) => {
      const d = (e as CustomEvent<ProgramProgressSavedDetail>).detail;
      if (!d?.workspaceId || !d.gradeId) return;
      if (d.workspaceId !== workspaceId || d.academicYear !== academicYear || d.termNumber !== termNumber) return;
      const gid = selectedEntry?.gradeId ?? '';
      if (d.gradeId !== gid) return;
      /**
       * cache: 전량 refetch는 생략. 저장 경로가 LS/RQ를 맞추므로 초안·presence만 재동기화.
       * (scoped upsert가 setProgramScopedRowsQueryData로 학기 RQ를 갱신하면 keep-alive 연간진도 rows도 따라감.)
       */
      if ((d.uiRefreshMode ?? 'full') === 'cache') {
        setProgramWorkspaceStateRev((v) => v + 1);
        setProgramDisplayOverlayRev((v) => v + 1);
        setAnnualSlideDraftRev((v) => v + 1);
        setAnnualWorksheetDraftRev((v) => v + 1);
        requestSlidePresenceReload(true);
        invalidateProgramContentSlidePresenceCache(workspaceId);
        return;
      }
      programDataLoadedScopeRef.current = '';
      setProgramCellsFetchNonce((n) => n + 1);
      setProgramRefreshTick((x) => x + 1);
      setAnnualSlideDraftRev((v) => v + 1);
      setAnnualWorksheetDraftRev((v) => v + 1);
      requestSlidePresenceReload(true);
      invalidateProgramContentSlidePresenceCache(workspaceId);
    };
    const onTimetableSaved = (e: Event) => {
      const d = (e as CustomEvent<AnnualTimetableSavedDetail>).detail;
      if (!d?.workspaceId || d.workspaceId !== workspaceId || d.academicYear !== academicYear) return;
      if (!d.termNumbers?.includes(termNumber)) return;
      setAnnualTimetableSaveReloadToken((t) => t + 1);
    };
    window.addEventListener(PROGRAM_PROGRESS_SAVED_EVENT, onProgramSaved as EventListener);
    window.addEventListener(ANNUAL_TIMETABLE_SAVED_EVENT, onTimetableSaved as EventListener);
    return () => {
      window.removeEventListener(PROGRAM_PROGRESS_SAVED_EVENT, onProgramSaved as EventListener);
      window.removeEventListener(ANNUAL_TIMETABLE_SAVED_EVENT, onTimetableSaved as EventListener);
    };
  }, [academicYear, requestSlidePresenceReload, selectedEntry?.gradeId, termNumber, workspaceId]);

  React.useEffect(() => {
    const gradeId = selectedEntry?.gradeId ?? '';
    const onBehaviorSaved = (e: CustomEvent<AnnualBehaviorNotesSavedDetail>) => {
      const d = e.detail;
      if (
        d.workspaceId === workspaceId &&
        d.academicYear === academicYear &&
        d.termNumber === termNumber &&
        d.gradeId === gradeId
      ) {
        const cached = loadAnnualBehaviorNotesCache(workspaceId, academicYear, termNumber, gradeId);
        if (cached != null) {
          setAnnualBehaviorNotesQueryData(workspaceId, academicYear, termNumber, gradeId, cached);
        } else {
          invalidateAnnualBehaviorNotesQuery(workspaceId, academicYear, termNumber, gradeId);
        }
      }
    };
    window.addEventListener(ANNUAL_BEHAVIOR_NOTES_SAVED_EVENT, onBehaviorSaved as EventListener);
    return () => {
      window.removeEventListener(ANNUAL_BEHAVIOR_NOTES_SAVED_EVENT, onBehaviorSaved as EventListener);
    };
  }, [workspaceId, academicYear, termNumber, selectedEntry?.gradeId]);

  React.useEffect(() => {
    const refresh = () => setProgramWorkspaceStateRev((v) => v + 1);
    window.addEventListener(PROGRAM_WORKSPACE_CHANGED_EVENT, refresh as EventListener);
    return () => window.removeEventListener(PROGRAM_WORKSPACE_CHANGED_EVENT, refresh as EventListener);
  }, []);
  React.useEffect(() => {
    if (!isActive) return;
    const onOverlayChanged = () => setProgramDisplayOverlayRev((v) => v + 1);
    const onStorage = (e: StorageEvent) => {
      if (!e.key?.startsWith('cp:annual-program-display-overlay:v1:')) return;
      onOverlayChanged();
    };
    window.addEventListener(ANNUAL_PROGRAM_DISPLAY_OVERLAY_CHANGED_EVENT, onOverlayChanged);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(ANNUAL_PROGRAM_DISPLAY_OVERLAY_CHANGED_EVENT, onOverlayChanged);
      window.removeEventListener('storage', onStorage);
    };
  }, [isActive]);

  const { loading: fixedContentRowsLoading, rows: fixedContentRows } = useProgramFixedContentRows({
    workspaceId: schoolScopedCacheSyncReady ? workspaceId : '',
    academicYear,
    termNumber,
    gradeId: selectedEntry?.gradeId ?? '',
    refreshNonce: programRefreshTick + annualAggregateRefreshNonce,
  });

  const behaviorGradeId = selectedEntry?.gradeId ?? '';
  const behaviorNotesQuery = useAnnualBehaviorNotesQuery({
    workspaceId: schoolScopedCacheSyncReady ? workspaceId : '',
    academicYear,
    termNumber,
    gradeId: behaviorGradeId,
    enabled: isActive && schoolScopedCacheSyncReady && !!behaviorGradeId,
  });
  const annualBehaviorNotes = behaviorNotesQuery.data ?? [];
  const behaviorNotesLoading = behaviorNotesQuery.isPending && annualBehaviorNotes.length === 0;
  const sealedLabelByToken = useSealedMemoTagLabels(
    React.useMemo(() => {
      const out: string[] = [];
      if (annualHoverTooltip?.kind === 'note') out.push(annualHoverTooltip.note ?? '');
      for (const row of annualBehaviorNotes) {
        out.push(row.behavior_note ?? '');
      }
      return out;
    }, [annualHoverTooltip, annualBehaviorNotes])
  );

  const presentationAuditQuery = useAnnualPresentationAuditQuery({
    workspaceId,
    academicYear,
    termNumber,
    gradeId: behaviorGradeId,
    enabled: presentationListOpen && !!workspaceId.trim() && !!behaviorGradeId.trim(),
  });

  const orphanPresentationEntries = React.useMemo((): PresentationAuditEntry[] => {
    if (!presentationListOpen) return [];
    const entries = presentationAuditQuery.data?.presentationEntries ?? [];
    return filterPresentationOrphansToPageScope(entries, {
      scopedRowIds: new Set(scopedRowsForTerm.map((row) => row.id.trim()).filter(Boolean)),
      fixedRowIds: new Set(fixedContentRows.map((row) => row.id.trim()).filter(Boolean)),
      programCellIds: new Set(
        Object.values(programCellsByKey)
          .map((cell) => cell?.id?.trim())
          .filter(Boolean) as string[]
      ),
    });
  }, [
    fixedContentRows,
    presentationAuditQuery.data?.presentationEntries,
    presentationListOpen,
    programCellsByKey,
    scopedRowsForTerm,
  ]);

  const tableLoading =
    loading ||
    annualCellsLoading ||
    programDataLoading ||
    fixedContentRowsLoading ||
    schoolScopedCacheSyncPending;
  /** 행 구조(개수·순서)에 영향을 주는 로딩만 — 표 언마운트 시 scrollTop=0 방지 */
  const entryGeometryLoading = loading || annualCellsLoading || schoolScopedCacheSyncPending;

  const termDef = React.useMemo(
    () => terms.find((term) => term.term_number === termNumber) ?? null,
    [termNumber, terms]
  );
  const visibleWeekdays = React.useMemo(
    () => ANNUAL_WEEKDAY_KEYS.filter((w) => w.key !== 'sat') as Array<{ key: AnnualWeekdayKey; label: string }>,
    []
  );
  const weeks = React.useMemo(
    () => buildWeeksFromRange(termDef?.starts_on ?? '', termDef?.ends_on ?? ''),
    [termDef?.ends_on, termDef?.starts_on]
  );

  const displayScopedRowsForTerm = React.useMemo(() => {
    void programWorkspaceStateRev;
    const gid = programGradeId.trim();
    if (!workspaceId.trim() || !gid || !scopedRowsForTerm.length) return scopedRowsForTerm;
    return enrichScopedRowsForAnnualProgressDisplay(scopedRowsForTerm, {
      workspaceId,
      gradeId: gid,
      academicYear,
      termNumber,
      trackProgramModeByTrack,
      trackProgramSyncRepByTrack,
    });
  }, [
    academicYear,
    programGradeId,
    programWorkspaceStateRev,
    scopedRowsForTerm,
    termNumber,
    trackProgramModeByTrack,
    trackProgramSyncRepByTrack,
    workspaceId,
  ]);

  const scopedRowsIndex = React.useMemo(
    () => buildScopedRowsAnnualIndex(displayScopedRowsForTerm),
    [displayScopedRowsForTerm]
  );
  const displayFixedContentRows = React.useMemo(() => {
    void programWorkspaceStateRev;
    const sid = workspaceId.trim();
    const gid = (selectedEntry?.gradeId ?? '').trim();
    if (!sid || !gid) return fixedContentRows;
    const fixedScopeId = `fixed|${sid}|${gid}|${academicYear}|${termNumber}|fixed`;
    const entry = getProgramWorkspaceScopeEntry(fixedScopeId);
    if (!entry?.isDirty || !entry.rows.length) return fixedContentRows;
    const baseline = [...fixedContentRows].sort((a, b) => a.order_index - b.order_index);
    return mergeProgramWorkspaceUpsertsIntoBaselineRows(baseline, entry.rows, {
      persistKind: entry.persistKind === 'replace' ? 'replace' : 'patch',
    }) as ProgramFixedContentRow[];
  }, [
    academicYear,
    fixedContentRows,
    programWorkspaceStateRev,
    selectedEntry?.gradeId,
    termNumber,
    workspaceId,
  ]);
  const annualProgramDisplayOverlayByAnnualKey = React.useMemo(() => {
    void programDisplayOverlayRev;
    const sid = workspaceId.trim();
    const gid = (selectedEntry?.gradeId ?? programGradeId).trim();
    if (!sid || !gid) return {};
    return readAnnualProgramDisplayOverlayEntries({
      workspaceId: sid,
      academicYear,
      termNumber,
      gradeId: gid,
    });
  }, [academicYear, programDisplayOverlayRev, programGradeId, selectedEntry?.gradeId, termNumber, workspaceId]);
  const annualProgramDirtyOverlayKeySet = React.useMemo(() => {
    void programWorkspaceStateRev;
    const sid = workspaceId.trim();
    const gid = (selectedEntry?.gradeId ?? programGradeId).trim();
    if (!sid || !gid) return new Set<string>();
    const out = new Set<string>();
    getProgramWorkspaceScopeEntries().forEach((entry) => {
      if (!entry?.isDirty) return;
      if (entry.workspaceId.trim() !== sid) return;
      if (entry.gradeId.trim() !== gid) return;
      if (entry.academicYear !== academicYear || entry.termNumber !== termNumber) return;
      entry.rows.forEach((row) => {
        const annualKey = (row.source_annual_cell_key ?? '').trim();
        if (annualKey) out.add(annualKey);
      });
    });
    return out;
  }, [academicYear, programGradeId, programWorkspaceStateRev, selectedEntry?.gradeId, termNumber, workspaceId]);
  const fixedContentByEventName = React.useMemo(() => {
    const map = new Map<string, ProgramFixedContentRow>();
    displayFixedContentRows.forEach((row) => {
      const key = eventNameFromFixedContentRow(row);
      if (!key || map.has(key)) return;
      map.set(key, row);
    });
    return map;
  }, [displayFixedContentRows]);
  /** 고정 행은 연간 셀 키로 먼저 묶는다(동일 이벤트명이 여러 칸일 때 이벤트 맵 한 건만 쓰면 틀어짐) */
  const fixedContentBySourceAnnualKey = React.useMemo(() => {
    const map = new Map<string, ProgramFixedContentRow>();
    displayFixedContentRows.forEach((row) => {
      const k = (row.source_annual_cell_key ?? '').trim();
      if (!k || map.has(k)) return;
      map.set(k, row);
    });
    return map;
  }, [displayFixedContentRows]);
  const selectedGradeNumber = React.useMemo(() => {
    const gradeRawName = selectedEntry ? grades.find((g) => g.id === selectedEntry.gradeId)?.name ?? selectedEntry.label : '';
    return gradeNumberLabel(gradeRawName);
  }, [grades, selectedEntry]);
  const periodLabels = React.useMemo(
    () => (selectedEntry ? buildAnnualPeriodLabels(selectedEntry.sectionRows) : []),
    [selectedEntry]
  );
  const projectProgramByColor = React.useMemo(() => {
    const map = new Map<string, AnnualProjectProgram>();
    annualProjectProgramsForSelectedGrade.forEach((program) => {
      if (program.termNumber !== termNumber) return;
      const key = program.colorHex.trim().toLowerCase();
      if (!key || map.has(key)) return;
      map.set(key, program);
    });
    return map;
  }, [annualProjectProgramsForSelectedGrade, termNumber]);
  const annualBehaviorNoteByNaturalKey = React.useMemo(() => {
    const map = new Map<string, AnnualBehaviorNote>();
    annualBehaviorNotes.forEach((row) => {
      map.set(behaviorNoteNaturalKey(row.lesson_date, row.day_row_order), row);
    });
    return map;
  }, [annualBehaviorNotes]);

  const rows = React.useMemo<AnnualProgressRow[]>(() => {
    if (!selectedEntry || !termDef || weeks.length === 0 || periodLabels.length === 0) return [];
    const out: AnnualProgressRow[] = [];
    const scopedOccurrenceCountByScopeKey = new Map<string, number>();
    const dayRowOrderByDate = new Map<string, number>();
    let seq = 1;
    weeks.forEach((week) => {
      visibleWeekdays.forEach(({ key }, weekdayIdx) => {
        const weekdayKey = key as AnnualWeekdayKey;
        const dateIso = week.datesByWeekday[weekdayIdx] ?? '';
        for (let slotIndex = 0; slotIndex < periodLabels.length; slotIndex += 1) {
          const stored = cellsByKey[buildAnnualCellKey(selectedEntry.gradeId, week.mondayIso, weekdayKey, slotIndex)];
          const effective = deriveAnnualEffectiveCell({
            workspaceId,
            academicYear,
            termNumber,
            gradeId: selectedEntry.gradeId,
            mondayIso: week.mondayIso,
            weekdayKey,
            slotIndex,
            dateIso,
            sectionRows: selectedEntry.sectionRows,
            slotLimitByDay: selectedEntry.slotLimitByDay,
            startsOn: termDef.starts_on,
            endsOn: termDef.ends_on,
            holidaySet,
            storedCell: stored,
            dedicatedColors,
            baseExcludeColors,
          });
          const rawDisplayText = (effective.textValue ?? '').trim();
          const timetableMemoForDisplay = isAnnualShortDayTailMemo(effective.memo)
            ? ''
            : (effective.memo ?? '').trim();
          let templateLabel = rawDisplayText
            ? trackNameByTimetableTextByGrade[selectedEntry.gradeId]?.[rawDisplayText] ?? ''
            : '';
          if (!templateLabel && rawDisplayText && selectedGradeTrackTimetableContext) {
            templateLabel = resolveTrackNameFromTimetableText(rawDisplayText, selectedGradeTrackTimetableContext);
          }
          const normalizedDisplayText =
            rawDisplayText && selectedGradeTrackTimetableContext
              ? normalizeTimetableTextToDefaultAlias(
                  rawDisplayText,
                  selectedGradeTrackTimetableContext,
                  templateLabel
                )
              : rawDisplayText;
          const timetableTextForRow =
            effective.slotKind === 'holiday'
              ? timetableTextForOffDayRow(dateIso, offDayByDate)
              : rawDisplayText ||
                (effective.slotKind === 'non_class' &&
                effective.nonClassSource === 'user' &&
                timetableMemoForDisplay
                  ? timetableMemoForDisplay
                  : rawDisplayText);
          const displayColor = (effective.colorHex ?? '').trim();
          const annualCellKey = buildAnnualCellKey(selectedEntry.gradeId, week.mondayIso, weekdayKey, slotIndex);
          const programKindLabel = (() => {
            if (
              matchesAnnualProjectProgramCell(
                displayColor,
                annualProjectProgramsForSelectedGrade,
                termNumber,
                effective.isBaseExcluded
              )
            )
              return '프로';
            if (
              annualSlotIsFixedProgram({
                colorHex: displayColor,
                regularColors,
              })
            )
              return '고정';
            if (isDedicatedColor(displayColor, dedicatedColors)) return '전담';
            return '';
          })();
          const hasTimetableAssignment = annualSlotHasTimetableProgramAssignment(rawDisplayText, displayColor);
          const isFixedSlot = programKindLabel === '고정';
          let pc = hasTimetableAssignment ? programCellsByKey[annualCellKey] : undefined;
          if (pc && !isFixedSlot) {
            const progressTimetableText = (pc.timetable_text ?? '').trim();
            if (rawDisplayText && progressTimetableText && rawDisplayText !== progressTimetableText) {
              pc = undefined;
            }
          }
          const timetableMemoRaw = timetableMemoForDisplay;
          const eventKeyForFixed = isFixedSlot
            ? normalizeFixedEventName(timetableMemoRaw) || normalizeFixedEventName(rawDisplayText)
            : '';
          const fixedMatchByCell = isFixedSlot ? fixedContentBySourceAnnualKey.get(annualCellKey.trim()) ?? null : null;
          const fixedMatchByEvent =
            isFixedSlot && eventKeyForFixed ? fixedContentByEventName.get(eventKeyForFixed) ?? null : null;
          const rowColorLower = displayColor.trim().toLowerCase();
          const projectForRow =
            programKindLabel === '프로'
              ? projectProgramByColor.get(rowColorLower) ?? null
              : null;
          const scopeLookupAlias = ((pc?.timetable_text ?? '').trim() || normalizedDisplayText.trim());
          const scopeLookupKey =
            templateLabel.trim() && scopeLookupAlias ? `${templateLabel.trim()}|${scopeLookupAlias}` : '';
          const trackModeForRow = templateLabel.trim() ? trackProgramModeByTrack[templateLabel.trim()] ?? 'unified' : 'unified';
          const preferredTrackScopeKey =
            !isFixedSlot && programKindLabel !== '프로' && templateLabel.trim()
              ? trackModeForRow === 'unified'
                ? `${templateLabel.trim()}|-`
                : trackModeForRow === 'sync'
                  ? `${templateLabel.trim()}|${trackProgramSyncRepByTrack[templateLabel.trim()]?.trim() || scopeLookupAlias || '-'}`
                  : scopeLookupKey
              : '';
          const fixedRowForOwner = isFixedSlot ? fixedMatchByCell ?? fixedMatchByEvent : null;
          const scopedOccurrenceKey = (() => {
            if (isFixedSlot) return '';
            if (programKindLabel === '프로' && projectForRow?.id) return `project:${projectForRow.id}`;
            const trackKey = trackModeForRow === 'sync' ? scopeLookupKey : preferredTrackScopeKey || scopeLookupKey;
            return trackKey ? `track:${trackKey}` : '';
          })();
          const scopedOccurrenceIndex = scopedOccurrenceKey
            ? scopedOccurrenceCountByScopeKey.get(scopedOccurrenceKey) ?? 0
            : undefined;
          if (scopedOccurrenceKey) {
            scopedOccurrenceCountByScopeKey.set(scopedOccurrenceKey, (scopedOccurrenceIndex ?? 0) + 1);
          }
          const shouldPickScopedRow =
            !isFixedSlot &&
            hasTimetableAssignment &&
            (programKindLabel === '프로' ? !!projectForRow?.id : !!templateLabel.trim());
          const scopedRowForOwner = shouldPickScopedRow
            ? pickScopedContentFromAnnualIndex(
                scopedRowsIndex,
                annualCellKey,
                programKindLabel,
                templateLabel,
                normalizedDisplayText,
                projectForRow?.id ?? null,
                pc?.timetable_text ?? undefined,
                scopedOccurrenceIndex,
                preferredTrackScopeKey
              )
            : null;
          const recordScopedRow = shouldPickScopedRow
            ? pickScopedRecordRowFromAnnualIndex(
                scopedRowsIndex,
                annualCellKey,
                programKindLabel,
                templateLabel,
                normalizedDisplayText,
                projectForRow?.id ?? null,
                pc?.timetable_text ?? undefined,
                scopedOccurrenceIndex,
                scopeLookupKey,
                preferredTrackScopeKey,
                trackProgramModeByTrack
              )
            : null;
          const isMirrorSlot = isSyncTrackMirrorScope(
            templateLabel,
            scopeLookupKey,
            preferredTrackScopeKey,
            trackProgramModeByTrack
          );
          const repContentRowForMirror =
            isMirrorSlot && shouldPickScopedRow
              ? pickRepScopedContentRowForMirrorSlot(
                  scopedRowsIndex,
                  preferredTrackScopeKey,
                  scopedOccurrenceIndex
                )
              : null;
          const { evalTarget, classTarget } = resolveAnnualProgressRecordTargets({
            isFixedSlot,
            fixedRow: fixedRowForOwner,
            isMirror: isMirrorSlot,
            contentScopedRow: isMirrorSlot ? repContentRowForMirror ?? scopedRowForOwner : scopedRowForOwner,
            recordScopedRow,
            aliasScopeKey: scopeLookupKey,
            programKindLabel,
            projectId: projectForRow?.id ?? null,
            scopedOccurrenceIndex,
            preferredTrackScopeKey,
            legacyPc: pc,
          });
          if (
            process.env.NODE_ENV !== 'production' &&
            shouldPickScopedRow &&
            typeof window !== 'undefined' &&
            window.localStorage.getItem('cp.debugProgramAnnualMatch') === '1'
          ) {
            console.info('[cp:annual-program-match]', {
              annualCellKey,
              programKindLabel,
              templateLabel,
              normalizedDisplayText,
              expectedScopeKey: scopeLookupKey,
              preferredTrackScopeKey,
              trackModeForRow,
              selectedRowId: scopedRowForOwner?.id ?? null,
              selectedScopeKey: scopedRowForOwner?.scope_key ?? null,
              selectedUpdatedAt: scopedRowForOwner?.updated_at ?? null,
              selectedLearningText: cellLearningTextForEditor(scopedRowForOwner),
            });
          }
          const progressSource = resolveAnnualSlotProgressSource({
            scopedRowsIndex,
            annualCellKey,
            programKindLabel,
            isFixedSlot,
            hasTimetableAssignment,
            templateLabel,
            displayText: normalizedDisplayText,
            projectId: projectForRow?.id ?? null,
            legacyPc: pc,
            trackProgramModeByTrack,
            trackProgramSyncRepByTrack,
            scopedOccurrenceIndex,
            preferredTrackScopeKey,
            scopeLookupKey,
            fixedMatchByCell,
            fixedMatchByEvent,
          });
          const slideOwner = resolveAnnualSlideContentSlideOwner({
            isFixedSlot,
            fixedRow: fixedRowForOwner,
            scopedRow: scopedRowForOwner,
            programCell: pc,
          });
          const slideOwnerCandidates = resolveAnnualSlideContentOwnerCandidates({
            isFixedSlot,
            fixedRow: fixedRowForOwner,
            scopedRow: scopedRowForOwner,
            programCell: pc,
          });
          const slideOpenDbOwner = resolveAnnualSlideOpenSlideDbOwner({
            isFixedSlot,
            fixedRow: fixedRowForOwner,
            scopedRow: scopedRowForOwner,
            programCell: pc,
          });
          const slideWorkspaceScopeId = buildAnnualSlideWorkspaceScopeId({
            programKindLabel,
            isFixedSlot,
            workspaceId,
            gradeId: selectedEntry.gradeId,
            academicYear,
            termNumber,
            projectId: projectForRow?.id ?? null,
            trackWorkspaceScopeKey: preferredTrackScopeKey.trim() || scopeLookupKey.trim(),
          });
          const overlayEntry = annualProgramDirtyOverlayKeySet.has(annualCellKey)
            ? annualProgramDisplayOverlayByAnnualKey[annualCellKey] ?? null
            : null;
          const overlayProgress =
            overlayEntry &&
            (!slideWorkspaceScopeId.trim() ||
              !overlayEntry.scopeId ||
              overlayEntry.scopeId === slideWorkspaceScopeId.trim())
              ? overlayEntry.row
              : null;
          const effectiveProgressSource = overlayProgress ?? progressSource;
          const d = mapProgressLikeToDisplayFields(effectiveProgressSource);
          const behaviorBaseDisplay = mapProgressLikeToDisplayFields(progressSource);
          const dayRowOrder = dayRowOrderByDate.get(dateIso) ?? 0;
          dayRowOrderByDate.set(dateIso, dayRowOrder + 1);
          const behaviorNoteMatched = annualBehaviorNoteByNaturalKey.get(
            behaviorNoteNaturalKey(dateIso, dayRowOrder)
          );
          const progressBehaviorNote = behaviorNoteMatched
            ? (behaviorNoteMatched.behavior_note ?? '').trim()
            : behaviorBaseDisplay.progressBehaviorNote;
          const progressBehaviorRecordMemberGroupId = behaviorNoteMatched
            ? behaviorNoteMatched.record_member_group_id ?? null
            : behaviorBaseDisplay.progressBehaviorNote.trim()
              ? behaviorBaseDisplay.progressRecordMemberGroupId
              : null;
          const displayMemoPrimary = d.progressMemoPrimary || '';
          const displayMemoNote = d.progressMemoNote;
          const slideLearningTextRaw =
            cellLearningTextForEditor(effectiveProgressSource) ||
            (shouldPickScopedRow || isFixedProgramSeedLearningText(pc?.learning_text)
              ? ''
              : cellLearningTextForEditor(pc)) ||
            '';
          const progressLikeForSlide =
            effectiveProgressSource ?? (pc ? programProgressCellToProgressLike(pc) : null);
          const slideSaveMode: 'program_cells' | 'fixed_content' =
            isFixedSlot || shouldPickScopedRow ? 'fixed_content' : 'program_cells';
          const slideProgramMode: 'default' | 'fixed_to_unit' | 'project_to_template' = isFixedSlot
            ? 'fixed_to_unit'
            : programKindLabel === '프로'
              ? 'project_to_template'
              : 'default';
          const slideCreatePresentationRefOnSave: ProgramContentSlidePayloadV1['createPresentationRefOnSave'] | undefined =
            resolvePresentationRefIdFromLearningText(slideLearningTextRaw)
              ? undefined
              : {
                  annualKey: annualCellKey,
                  learningText: slideLearningTextRaw,
                  saveMode: slideSaveMode,
                  mode: slideProgramMode,
                  gradeId: selectedEntry.gradeId,
                  mondayIso: week.mondayIso,
                  weekdayKey,
                  slotIndex,
                  orderIndex: isFixedSlot
                    ? fixedRowForOwner?.order_index ?? seq
                    : /** 편제·프로젝트는 일정 위치(몇 번째 수업)가 행 위치 */
                      Number.isFinite(scopedOccurrenceIndex)
                      ? Number(scopedOccurrenceIndex)
                      : scopedRowForOwner?.order_index ?? seq,
                  sourceAnnualCellKey: isFixedSlot ? annualCellKey : null,
                  timetableText: (pc?.timetable_text ?? '').trim() || timetableTextForRow,
                  unitText: progressLikeForSlide?.unit_text ?? '',
                  pageText: progressLikeForSlide?.page_text ?? '',
                  assistantPageText: progressLikeForSlide?.assistant_page_text ?? '',
                  currentPeriodText: progressLikeForSlide?.current_period_text ?? '',
                  totalPeriodText: progressLikeForSlide?.total_period_text ?? '',
                  materialText: progressLikeForSlide?.material_text ?? '',
                  recordCrossCurricularText: progressLikeForSlide?.record_cross_curricular_text ?? '',
                  recordMemoText: progressLikeForSlide?.record_memo_text ?? '',
                  recordEvaluationText: progressLikeForSlide?.record_evaluation_text ?? '',
                  recordClassText: progressLikeForSlide?.record_class_text ?? '',
                };
          const isPresentationSyncMirror = resolveAnnualPresentationSyncMirror({
            programKindLabel,
            isFixedSlot,
            templateLabel,
            scopeLookupAlias,
            trackProgramModeByTrack,
            trackProgramSyncRepByTrack,
          });
          out.push({
            seq: seq++,
            dateIso,
            dateLabel: shortDateLabel(dateIso),
            weekdayLabel: weekdayLabel(weekdayKey),
            periodLabel: periodLabels[slotIndex] || `${slotIndex + 1}타임`,
            slotIndex,
            entryContentRowId: String(
              (isFixedSlot
                ? fixedRowForOwner?.id
                : (isMirrorSlot ? repContentRowForMirror ?? scopedRowForOwner : scopedRowForOwner)?.id) ?? ''
            ),
            gradeLabel: selectedGradeNumber,
            semesterLabel: String(termNumber),
            templateLabel,
            timetableText: timetableTextForRow,
            timetableColorHex: displayColor,
            timetableTextShrink: timetableTextForRow.length > 2,
            timetableSlotKind: effective.slotKind,
            programKindLabel,
            annualCellKey,
            progressUnit: d.progressUnit,
            progressUnitNote: d.progressUnitNote,
            progressLearning: d.progressLearning,
            progressLearningNote: d.progressLearningNote,
            progressPage: d.progressPage,
            progressAssistantPage: d.progressAssistantPage,
            progressCurrentPeriod: d.progressCurrentPeriod,
            progressTotalPeriod: d.progressTotalPeriod,
            progressMaterial: d.progressMaterial,
            progressBgByField: d.progressBgByField,
            progressCross: d.progressCross,
            progressCrossNote: d.progressCrossNote,
            displayMemoPrimary,
            displayMemoNote,
            progressEval: d.progressEval,
            progressEvalCode: d.progressEvalCode,
            progressEvalNote: d.progressEvalNote,
            progressClassRecord: d.progressClassRecord,
            progressClassRecordKind: d.progressClassRecordKind,
            progressClassRecordNote: d.progressClassRecordNote,
            progressBehaviorNote,
            progressBehaviorAiFeedback: behaviorNoteMatched?.ai_feedback ?? null,
            progressClassRecordMemberGroupId: d.progressRecordMemberGroupId,
            progressBehaviorRecordMemberGroupId,
            dayRowOrder,
            calendarOffDayKind:
              effective.slotKind === 'holiday' ? (offDayByDate[dateIso]?.kind ?? 'holiday') : null,
            slideOwner,
            slideOwnerCandidates,
            slideOpenDbOwner,
            slideWorkspaceScopeId,
            mondayIso: week.mondayIso,
            weekdayKey,
            slideLearningTextRaw,
            slideCreatePresentationRefOnSave,
            scopeLookupAlias,
            isPresentationSyncMirror,
            evalRecordTarget: evalTarget,
            classRecordTarget: classTarget,
            progressEvalRaw: effectiveProgressSource?.record_evaluation_text ?? '',
            progressClassRaw: effectiveProgressSource?.record_class_text ?? '',
          });
        }
      });
    });
    return out;
  }, [
    academicYear,
    annualProjectProgramsForSelectedGrade,
    annualProgramDirtyOverlayKeySet,
    annualProgramDisplayOverlayByAnnualKey,
    annualBehaviorNoteByNaturalKey,
    cellsByKey,
    dedicatedColors,
    baseExcludeColors,
    fixedContentByEventName,
    fixedContentBySourceAnnualKey,
    holidaySet,
    offDayByDate,
    periodLabels,
    programCellsByKey,
    projectProgramByColor,
    regularColors,
    workspaceId,
    scopedRowsIndex,
    selectedEntry,
    selectedGradeNumber,
    termDef,
    termNumber,
    trackProgramModeByTrack,
    trackProgramSyncRepByTrack,
    selectedGradeTrackTimetableContext,
    trackNameByTimetableTextByGrade,
    visibleWeekdays,
    weeks,
  ]);

  const currentTermDetailRows = React.useMemo((): AnnualCrossTopicDetailRow[] => {
    const out: AnnualCrossTopicDetailRow[] = [];
    rows.forEach((row) => {
      const crossTopic = row.progressCross.trim();
      if (!crossTopic) return;
      out.push({
        dateLabel: row.dateLabel,
        weekdayLabel: row.weekdayLabel,
        periodLabel: row.periodLabel,
        templateLabel: row.templateLabel.trim(),
        unit: row.progressUnit.trim(),
        learning: row.progressLearning.trim(),
        page: row.progressPage.trim(),
        assistantPage: row.progressAssistantPage.trim(),
        currentPeriod: row.progressCurrentPeriod.trim(),
        totalPeriod: row.progressTotalPeriod.trim(),
        material: row.progressMaterial.trim(),
        crossTopic,
        crossNote: row.progressCrossNote.trim(),
        isSyncTimetableMirror: row.isPresentationSyncMirror,
        annualCellKey: row.annualCellKey,
        mondayIso: row.mondayIso,
        weekdayKey: row.weekdayKey,
        slotIndex: row.slotIndex,
        seq: row.seq,
      });
    });
    return out;
  }, [rows]);

  const neisLearningUploadSourceRows = React.useMemo((): NeisLearningContentUploadSourceRow[] => {
    return rows.map((row) => ({
      dateIso: row.dateIso,
      dateLabel: row.dateLabel,
      weekdayLabel: row.weekdayLabel,
      periodLabel: row.periodLabel,
      dayRowOrder: row.dayRowOrder,
      slotIndex: row.slotIndex,
      mondayIso: row.mondayIso,
      weekdayKey: row.weekdayKey,
      gradeLabel: row.gradeLabel,
      semesterLabel: row.semesterLabel,
      templateLabel: row.templateLabel,
      timetableSlotKind: row.timetableSlotKind,
      progressUnit: row.progressUnit,
      progressLearning: row.progressLearning,
      progressCross: row.progressCross,
      progressCrossNote: row.progressCrossNote,
      progressMemoPrimary: row.displayMemoPrimary,
      progressMemoNote: row.displayMemoNote,
      progressPage: row.progressPage,
      progressAssistantPage: row.progressAssistantPage,
      progressCurrentPeriod: row.progressCurrentPeriod,
      progressTotalPeriod: row.progressTotalPeriod,
      progressMaterial: row.progressMaterial,
      isSyncTimetableMirror: row.isPresentationSyncMirror,
    }));
  }, [rows]);

  const buildNeisUploadShortcutExportRows = React.useCallback(() => {
    const trackOptions = buildSubscribedTrackFilterOptions(subscribedTrackNamesForClass);
    const subscribedTrackNameSet = new Set(trackOptions);
    const filtered = filterNeisLearningContentUploadRows(neisLearningUploadSourceRows, {
      selectedTrack: null,
      includeActivity: false,
      includeNonClass: false,
      includeSyncTimetable: false,
      subscribedTrackNames: subscribedTrackNameSet,
    });
    return buildNeisUploadLearningContentRows(filtered, trackOptions);
  }, [neisLearningUploadSourceRows, subscribedTrackNamesForClass]);

  const handleNeisExportFormatPick = React.useCallback(
    async (format: NeisExportFormat) => {
      const kind = neisExportFormatPick;
      if (!kind) return;

      if (kind === 'neis-class-timetable') {
        const gradeId = (selectedEntry?.gradeId ?? '').trim();
        if (!gradeId) {
          alert('클래스를 선택한 뒤 다시 시도해 주세요.');
          return;
        }
        setNeisExportFormatPick(null);
        navigate({
          pathname: '/weekly-learning-guide',
          search: createSearchParams({
            gradeId,
            term: String(termNumber),
            neisAutoExport: format === 'xls' ? 'xls' : 'pdf',
          }).toString(),
        });
        return;
      }

      const exportRows = buildNeisUploadShortcutExportRows();
      if (tableLoading || exportRows.length === 0 || !neisLearningContentUploadRowsHaveData(exportRows)) {
        alert('NEIS 업로드용 학습내용으로 내보낼 데이터가 없습니다.');
        return;
      }

      const dateLabel = new Date().toLocaleDateString('ko-KR').replace(/\./g, '').trim();
      const headerTitle = 'NEIS 업로드 학습내용';
      const fileNameBase = `연간지도_${termNumber}학기_NEIS_업로드_학습내용_${dateLabel}`;
      const printTitle = `${termNumber}학기 ${headerTitle}`;

      setNeisShortcutExportBusy(true);
      try {
        if (format === 'xls') {
          await saveNeisLearningContentUploadAsExcel({
            rows: exportRows,
            headerTitle,
            termNumber,
            showDateColumns: false,
            viewMode: 'neis-upload',
            fileName: `${fileNameBase}.xlsx`,
          });
        } else {
          const placeholder = openDocumentExportPreviewTabPlaceholder();
          const result = openDocumentExportPreviewInNewTab(
            {
              v: 1,
              kind: 'neis-learning-content-upload',
              fileNameBase,
              printTitle,
              data: {
                termNumber,
                headerTitle,
                showDateColumns: false,
                viewMode: 'neis-upload',
                rows: exportRows,
              },
            },
            'pdf',
            { preOpenedWindow: placeholder }
          );
          if (!result.ok) {
            alert(openDocumentExportPreviewFailureMessage(result.reason));
          }
        }
        setNeisExportFormatPick(null);
      } catch (error) {
        console.error('NEIS 업로드용 학습내용 바로 다운로드 실패:', error);
        alert('다운로드에 실패했습니다.');
      } finally {
        setNeisShortcutExportBusy(false);
      }
    },
    [
      buildNeisUploadShortcutExportRows,
      navigate,
      neisExportFormatPick,
      selectedEntry?.gradeId,
      tableLoading,
      termNumber,
    ]
  );

  React.useEffect(() => {
    const gid = selectedEntry?.gradeId?.trim() ?? '';
    if (!workspaceId.trim() || !gid || rows.length === 0) return;
    publishAnnualProgressCrossTopics(
      workspaceId,
      academicYear,
      gid,
      termNumber,
      rows.map((row) => row.progressCross)
    );
    publishAnnualProgressCrossTopicDetailRows(workspaceId, academicYear, gid, termNumber, currentTermDetailRows);
  }, [academicYear, currentTermDetailRows, rows, selectedEntry?.gradeId, termNumber, workspaceId]);

  const [annualProgressSlidePresenceKeys, setAnnualProgressSlidePresenceKeys] = React.useState<Set<string>>(
    () => new Set()
  );
  const [annualLearningTextOverlayByKey, setAnnualLearningTextOverlayByKey] = React.useState<Record<string, string>>(
    {}
  );
  const annualLearningOverlayUpdatedAtRef = React.useRef<Record<string, number>>({});
  const mergedLearningTextOverlayByKey = React.useMemo(() => {
    return { ...annualLearningTextOverlayByKey };
  }, [annualLearningTextOverlayByKey]);
  const [worksheetPresenceRefIds, setWorksheetPresenceRefIds] = React.useState<Set<string>>(() => new Set());

  const annualSyncMirrorIndicatorMaps = React.useMemo(
    () => buildAnnualSyncMirrorIndicatorMaps(rows, trackProgramModeByTrack, programCellsByKey),
    [programCellsByKey, rows, trackProgramModeByTrack]
  );
  /**
   * 학습내용 텍스트/메모 표시와 인디케이터의 canonical 소스 통합:
   * 연간진도표 셀 표시는 인디케이터 계산과 동일한 learning_text 해석을 사용한다.
   */
  const learningDisplayByAnnualKey = React.useMemo(() => {
    const out: Record<string, { text: string; note: string; raw: string }> = {};
    rows.forEach((row) => {
      const raw = resolveAnnualIndicatorLearningText(row, mergedLearningTextOverlayByKey, annualSyncMirrorIndicatorMaps);
      const parsed = parseProgramProgressMemoJsonForDisplay(raw);
      out[row.annualCellKey] = {
        text: formatLearningTextForDisplay(parsed.text),
        note: parsed.note.trim(),
        raw,
      };
    });
    return out;
  }, [annualSyncMirrorIndicatorMaps, mergedLearningTextOverlayByKey, rows]);

  const annualProgressSlideOwnerSignature = React.useMemo(
    () =>
      collectAnnualSlideOwnerSignatureKeys(
        rows,
        mergedLearningTextOverlayByKey,
        annualSyncMirrorIndicatorMaps
      ).join('|'),
    [annualSyncMirrorIndicatorMaps, mergedLearningTextOverlayByKey, rows]
  );

  React.useEffect(() => {
    setAnnualLearningTextOverlayByKey((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const [annualKey, overlay] of Object.entries(prev)) {
        const row = rows.find((r) => r.annualCellKey === annualKey);
        if (!row || row.slideLearningTextRaw === overlay) {
          delete next[annualKey];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [rows, scopedRowsForTerm]);

  const applyAnnualLearningTextOverlayPayload = React.useCallback(
    (raw: string | Record<string, unknown> | null | undefined): boolean => {
      if (!raw) return false;
      try {
        const parsed = typeof raw === 'string' ? (JSON.parse(raw) as Record<string, unknown>) : raw;
        const sid = String(parsed.workspaceId ?? '').trim();
        if (!sid || sid !== workspaceId?.trim()) return false;
        const annualKey = String(parsed.annualKey ?? '').trim();
        const learningText = parsed.learningText;
        if (!annualKey || typeof learningText !== 'string') return false;
        const updatedAt = Number(parsed.updatedAt ?? 0);
        const prevUpdatedAt = annualLearningOverlayUpdatedAtRef.current[annualKey] ?? 0;
        if (Number.isFinite(updatedAt) && updatedAt > 0 && updatedAt <= prevUpdatedAt) {
          return false;
        }
        let changed = false;
        setAnnualLearningTextOverlayByKey((prev) => {
          if (prev[annualKey] === learningText) return prev;
          changed = true;
          return { ...prev, [annualKey]: learningText };
        });
        if (Number.isFinite(updatedAt) && updatedAt > 0) {
          annualLearningOverlayUpdatedAtRef.current[annualKey] = updatedAt;
        }
        if (changed) requestSlidePresenceReload(true);
        return changed;
      } catch {
        return false;
      }
    },
    [workspaceId, requestSlidePresenceReload]
  );

  React.useEffect(() => {
    if (!workspaceId?.trim()) return undefined;
    const onSlideLearningUpdated: EventListener = (e) => {
      const changed = applyAnnualLearningTextOverlayPayload(
        (e as CustomEvent<Record<string, unknown>>).detail
      );
      if (changed) invalidateProgramContentSlidePresenceCache(workspaceId);
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === PROGRAM_CONTENT_SLIDE_LEARNING_UPDATED_KEY) {
        const changed = applyAnnualLearningTextOverlayPayload(e.newValue);
        if (changed) invalidateProgramContentSlidePresenceCache(workspaceId);
        return;
      }
      if (e.key?.startsWith(PROGRAM_CONTENT_SLIDE_PRESENCE_CACHE_PREFIX)) {
        invalidateProgramContentSlidePresenceCache(workspaceId);
        requestSlidePresenceReload(true);
      }
    };
    window.addEventListener(PROGRAM_CONTENT_SLIDE_LEARNING_UPDATED_EVENT, onSlideLearningUpdated);
    window.addEventListener('storage', onStorage);
    try {
      const raw = window.localStorage?.getItem(PROGRAM_CONTENT_SLIDE_LEARNING_UPDATED_KEY) ?? null;
      applyAnnualLearningTextOverlayPayload(raw);
    } catch {
      /* ignore */
    }
    return () => {
      window.removeEventListener(PROGRAM_CONTENT_SLIDE_LEARNING_UPDATED_EVENT, onSlideLearningUpdated);
      window.removeEventListener('storage', onStorage);
    };
  }, [
    academicYear,
    applyAnnualLearningTextOverlayPayload,
    requestSlidePresenceReload,
    selectedEntry?.gradeId,
    termNumber,
    workspaceId,
  ]);

  React.useEffect(() => {
    if (!workspaceId?.trim()) {
      setAnnualProgressSlidePresenceKeys((prev) => (prev.size === 0 ? prev : new Set()));
      return undefined;
    }
    const owners = annualProgressSlideOwnerSignature
      .split('|')
      .map((raw): ProgramContentSlideOwnerRef | null => {
        const sep = raw.indexOf(':');
        if (sep <= 0) return null;
        const kind = raw.slice(0, sep) as ProgramContentSlideOwnerRef['kind'];
        const id = raw.slice(sep + 1).trim();
        if (
          !id ||
          (kind !== 'fixed_row' &&
            kind !== 'scoped_row' &&
            kind !== 'program_cell' &&
            kind !== 'presentation_ref')
        )
          return null;
        return { kind, id };
      })
      .filter((o): o is ProgramContentSlideOwnerRef => !!o);
    const unique: ProgramContentSlideOwnerRef[] = [];
    const seen = new Set<string>();
    for (const o of owners) {
      const k = programContentSlidePresenceKey(o);
      if (seen.has(k)) continue;
      seen.add(k);
      unique.push(o);
    }
    let cancelled = false;
    const forceRefresh = slidePresenceForceRefreshRef.current;
    slidePresenceForceRefreshRef.current = false;
    void fetchProgramContentSlidePresenceKeySet(workspaceId.trim(), unique, {
      forceRefresh,
    })
      .then((s) => {
        if (!cancelled) {
          setAnnualProgressSlidePresenceKeys((prev) => {
            if (prev.size === s.size && Array.from(s).every((k) => prev.has(k))) return prev;
            return s;
          });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAnnualProgressSlidePresenceKeys((prev) => (prev.size === 0 ? prev : new Set()));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [annualProgressSlideOwnerSignature, slidePresenceReloadNonce, workspaceId]);

  const [annualSlideDraftRev, setAnnualSlideDraftRev] = React.useState(0);
  const [annualWorksheetDraftRev, setAnnualWorksheetDraftRev] = React.useState(0);
  const [indicatorHintRev, setIndicatorHintRev] = React.useState(0);
  React.useEffect(() => {
    const bumpSlides = () => setAnnualSlideDraftRev((n) => n + 1);
    const bumpWorksheets = () => setAnnualWorksheetDraftRev((n) => n + 1);
    const bumpIndicatorHints = () => setIndicatorHintRev((n) => n + 1);
    window.addEventListener(PROGRAM_CONTENT_SLIDE_DRAFTS_CHANGED_EVENT, bumpSlides);
    window.addEventListener(LEARNING_CONTENT_WORKSHEET_DRAFTS_CHANGED_EVENT, bumpWorksheets);
    window.addEventListener(LEARNING_WORKSHEET_PENDING_CHANGED_EVENT, bumpWorksheets);
    window.addEventListener(LEARNING_ATTACHMENT_INDICATOR_CHANGED_EVENT, bumpIndicatorHints);
    return () => {
      window.removeEventListener(PROGRAM_CONTENT_SLIDE_DRAFTS_CHANGED_EVENT, bumpSlides);
      window.removeEventListener(LEARNING_CONTENT_WORKSHEET_DRAFTS_CHANGED_EVENT, bumpWorksheets);
      window.removeEventListener(LEARNING_WORKSHEET_PENDING_CHANGED_EVENT, bumpWorksheets);
      window.removeEventListener(LEARNING_ATTACHMENT_INDICATOR_CHANGED_EVENT, bumpIndicatorHints);
    };
  }, []);

  const programGradeTermSaveTarget = React.useMemo((): ProgramGradeTermSaveTarget => {
    return {
      workspaceId: workspaceId.trim(),
      gradeId: (selectedEntry?.gradeId ?? programGradeId).trim(),
      academicYear,
      termNumber,
    };
  }, [academicYear, programGradeId, selectedEntry?.gradeId, termNumber, workspaceId]);

  const programSavePending = React.useMemo(() => {
    void programWorkspaceStateRev;
    void annualSlideDraftRev;
    void annualWorksheetDraftRev;
    if (!programGradeTermSaveTarget.workspaceId || !programGradeTermSaveTarget.gradeId) return false;
    return computeProgramSavePending({ target: programGradeTermSaveTarget });
  }, [
    annualSlideDraftRev,
    annualWorksheetDraftRev,
    programGradeTermSaveTarget,
    programWorkspaceStateRev,
  ]);

  React.useEffect(() => {
    if (!programSaveToastState || programSaveToastState.kind === 'saving') return;
    const t = window.setTimeout(() => setProgramSaveToastState(null), 1800);
    return () => window.clearTimeout(t);
  }, [programSaveToastState]);

  const handleProgramGradeTermSave = React.useCallback(async () => {
    const target = programGradeTermSaveTarget;
    if (!target.workspaceId || !target.gradeId) return;
    setProgramSaveToastState({ kind: 'saving', message: '저장 중입니다...' });
    try {
      await saveProgramGradeTermWorkspaceAndAttachments(target);
      clearProgramDisplayCachesForGrade(
        target.workspaceId,
        target.academicYear,
        target.termNumber,
        target.gradeId
      );
      setProgramWorkspaceStateRev((v) => v + 1);
      setAnnualSlideDraftRev((v) => v + 1);
      setAnnualWorksheetDraftRev((v) => v + 1);
      setProgramRefreshTick((x) => x + 1);
      setAnnualAggregateRefreshNonce((n) => n + 1);
      setProgramSaveToastState({ kind: 'success', message: '저장되었습니다.' });
    } catch (e) {
      setProgramSaveToastState({
        kind: 'error',
        message: e instanceof Error ? e.message : '저장에 실패했습니다.',
      });
    }
  }, [programGradeTermSaveTarget]);

  const learningSlidePagesByAnnualKey = React.useMemo(() => {
    const deletedHints = readLearningAttachmentIndicatorDeletedHints(workspaceId?.trim() ?? '');
    return computeAnnualLearningSlidePagesByAnnualKey({
      rows,
      workspaceId: workspaceId?.trim() ?? '',
      learningTextOverlayByAnnualKey: mergedLearningTextOverlayByKey,
      slidePresenceKeys: annualProgressSlidePresenceKeys,
      slideDraftRev: annualSlideDraftRev,
      deletedHints,
      syncMaps: annualSyncMirrorIndicatorMaps,
    });
  }, [
    annualProgressSlidePresenceKeys,
    annualSlideDraftRev,
    annualSyncMirrorIndicatorMaps,
    indicatorHintRev,
    mergedLearningTextOverlayByKey,
    rows,
    workspaceId,
  ]);

  const learningMemoNoteByAnnualKey = React.useMemo(
    () =>
      computeAnnualLearningMemoNoteByAnnualKey({
        rows,
        learningTextOverlayByAnnualKey: mergedLearningTextOverlayByKey,
        memoDraftRev: annualSlideDraftRev,
        syncMaps: annualSyncMirrorIndicatorMaps,
      }),
    [annualSlideDraftRev, annualSyncMirrorIndicatorMaps, mergedLearningTextOverlayByKey, rows]
  );

  const learningWorksheetByAnnualKey = React.useMemo(() => {
    const deletedHints = readLearningAttachmentIndicatorDeletedHints(workspaceId?.trim() ?? '');
    return computeAnnualLearningWorksheetByAnnualKey({
      rows,
      workspaceId: workspaceId?.trim() ?? '',
      learningTextOverlayByAnnualKey: mergedLearningTextOverlayByKey,
      worksheetPresenceRefIds,
      worksheetDraftRev: annualWorksheetDraftRev,
      deletedHints,
      syncMaps: annualSyncMirrorIndicatorMaps,
    });
  }, [
    annualSyncMirrorIndicatorMaps,
    annualWorksheetDraftRev,
    indicatorHintRev,
    mergedLearningTextOverlayByKey,
    rows,
    worksheetPresenceRefIds,
    workspaceId,
  ]);

  const worksheetPresenceRefIdsSig = React.useMemo(
    () =>
      rows
        .map((row) =>
          resolveAnnualIndicatorLearningText(row, mergedLearningTextOverlayByKey, annualSyncMirrorIndicatorMaps)
        )
        .map((learningText) => resolveWorksheetRefIdFromLearningText(learningText))
        .filter(Boolean)
        .sort()
        .join('|'),
    [annualSyncMirrorIndicatorMaps, mergedLearningTextOverlayByKey, rows]
  );

  React.useEffect(() => {
    const sid = workspaceId?.trim();
    if (!sid) {
      setWorksheetPresenceRefIds(new Set());
      return;
    }
    const refIds = worksheetPresenceRefIdsSig ? worksheetPresenceRefIdsSig.split('|') : [];
    if (!refIds.length) {
      setWorksheetPresenceRefIds(new Set());
      return;
    }
    let cancelled = false;
    void fetchProgramContentWorksheetPresence(sid, refIds)
      .then((presence) => {
        if (!cancelled) setWorksheetPresenceRefIds(presence);
      })
      .catch(() => {
        if (!cancelled) setWorksheetPresenceRefIds(new Set());
      });
    return () => {
      cancelled = true;
    };
  }, [programRefreshTick, worksheetPresenceRefIdsSig, workspaceId]);

  React.useLayoutEffect(() => {
    syncAnnualTableViewport();
    const nextHeadHeight = annualTableHeadRef.current?.offsetHeight ?? 64;
    if (nextHeadHeight > 0) {
      setTableHeadHeight((prev) => (prev === nextHeadHeight ? prev : nextHeadHeight));
    }
  }, [rows.length, syncAnnualTableViewport]);

  const rowIdxByAnnualKey = React.useMemo<Record<string, number>>(() => {
    const map: Record<string, number> = {};
    rows.forEach((row, idx) => {
      map[row.annualCellKey] = idx;
    });
    return map;
  }, [rows]);
  const rowByAnnualKey = React.useMemo<Record<string, AnnualProgressRow>>(() => {
    const map: Record<string, AnnualProgressRow> = {};
    rows.forEach((row) => {
      map[row.annualCellKey] = row;
    });
    return map;
  }, [rows]);
  const rowByBehaviorNaturalKey = React.useMemo(() => {
    const map = new Map<string, AnnualProgressRow>();
    rows.forEach((row) => {
      const k = behaviorNoteNaturalKey(row.dateIso, row.dayRowOrder);
      if (!map.has(k)) map.set(k, row);
    });
    return map;
  }, [rows]);
  const rowCountByDateIso = React.useMemo(() => {
    const map = new Map<string, number>();
    rows.forEach((row) => {
      const key = row.dateIso.trim();
      map.set(key, (map.get(key) ?? 0) + 1);
    });
    return map;
  }, [rows]);
  const behaviorNoteListItems = React.useMemo<AnnualBehaviorNoteListItem[]>(() => {
    const out = annualBehaviorNotes.map((note) => {
      const lessonDate = (note.lesson_date ?? '').trim();
      const dayRowOrder = Math.max(0, note.day_row_order | 0);
      const matchedRow = rowByBehaviorNaturalKey.get(behaviorNoteNaturalKey(lessonDate, dayRowOrder)) ?? null;
      const dayRows = rowCountByDateIso.get(lessonDate) ?? 0;
      const orphanKind: AnnualBehaviorNoteListItem['orphanKind'] = matchedRow
        ? 'none'
        : dayRows <= 0
          ? 'date'
          : 'overflow';
      return {
        id: note.id,
        lessonDate,
        dayRowOrder,
        behaviorNote: (note.behavior_note ?? '').trim(),
        recordMemberGroupId: note.record_member_group_id ?? null,
        periodLabelSnapshot: (note.period_label_snapshot ?? '').trim(),
        timetableTextSnapshot: (note.timetable_text_snapshot ?? '').trim(),
        updatedAt: formatDateTimeMinute(note.updated_at ?? ''),
        linkedAnnualCellKey: matchedRow?.annualCellKey ?? null,
        linkedDateLabel: matchedRow?.dateLabel ?? '',
        linkedWeekdayLabel: matchedRow?.weekdayLabel ?? '',
        linkedPeriodLabel: matchedRow?.periodLabel ?? '',
        orphanKind,
      };
    });
    out.sort((a, b) => {
      const dateCmp = b.lessonDate.localeCompare(a.lessonDate);
      if (dateCmp !== 0) return dateCmp;
      const orderCmp = a.dayRowOrder - b.dayRowOrder;
      if (orderCmp !== 0) return orderCmp;
      return b.updatedAt.localeCompare(a.updatedAt);
    });
    return out;
  }, [annualBehaviorNotes, rowByBehaviorNaturalKey, rowCountByDateIso]);

  const rowIdxByAnnualKeyRef = React.useRef(rowIdxByAnnualKey);
  /** useLayoutEffect·진입 스크롤이 같은 커밋에서 최신 rows를 읽도록 render 시 동기 갱신 */
  rowIdxByAnnualKeyRef.current = rowIdxByAnnualKey;

  const classRecordMemberTagMembersForRow = React.useCallback(
    (row: AnnualProgressRow) =>
      membersForRecordGroup(memberGradeLayout, row.progressClassRecordMemberGroupId, classMembers),
    [classMembers, memberGradeLayout]
  );
  const behaviorRecordMemberTagMembersForRow = React.useCallback(
    (row: AnnualProgressRow) =>
      membersForRecordGroup(memberGradeLayout, row.progressBehaviorRecordMemberGroupId, classMembers),
    [classMembers, memberGradeLayout]
  );

  const rowFieldEdgeClassById = React.useMemo(() => {
    const rowFieldOrder: AnnualProgressFieldId[] = [
      'unit',
      'learning',
      'page',
      'assistantPage',
      'currentPeriod',
      'totalPeriod',
      'material',
      'cross',
      'memo',
      'eval',
      'class',
    ];
    const map = {} as Record<AnnualProgressFieldId, string>;
    rowFieldOrder.forEach((fieldId, idx) => {
      const classes = ['cp-base-timetable-cell-edge-top', 'cp-base-timetable-cell-edge-bottom'];
      if (idx === 0) classes.push('cp-base-timetable-cell-edge-left');
      if (idx === rowFieldOrder.length - 1) classes.push('cp-base-timetable-cell-edge-right');
      map[fieldId] = classes.join(' ');
    });
    return map;
  }, []);

  const scrollAnnualRowIntoView = React.useCallback(
    (
      rowIdx: number,
      options?: {
        forceCenter?: boolean;
        rowsBelowHeader?: number;
        keyboardArrow?: AnnualProgressKeyboardArrow;
        /** 키보드 스크롤 시 가시 여부 측정용 앵커(기본 단원 열) */
        keyboardAnchorField?: AnnualProgressKeyboardAnchorField;
      }
    ) => {
      const wrap = annualTableScrollRef.current;
      if (!wrap || rowIdx < 0) return;
      const rowHeight = Math.max(virtualRowHeight, 1);
      const measuredHead = annualTableHeadRef.current?.offsetHeight ?? 0;
      const headerHeight = measuredHead > 0 ? measuredHead : Math.max(tableHeadHeight, 0);
      const rowTop = headerHeight + rowIdx * rowHeight;
      const rowBottom = rowTop + rowHeight;
      const V = wrap.clientHeight;
      const H = headerHeight;
      const h = rowHeight;
      const maxTop = Math.max(0, wrap.scrollHeight - V);
      const commitScroll = (top: number) => {
        wrap.scrollTop = Math.min(maxTop, Math.max(0, top));
        syncAnnualTableViewport();
      };
      const nBelow = options?.rowsBelowHeader;
      if (nBelow != null && Number.isFinite(nBelow) && nBelow >= 0) {
        const N = nBelow;
        const idealTop = rowTop - H - N * h;
        commitScroll(idealTop);
        return;
      }
      if (options?.forceCenter) {
        /**
         * 세로 중앙: sticky thead 아래 ~래퍼 하단 사이의 가시 영역 중앙에 행 앵커(단원 열) 중점을 맞춤.
         * 가상 좌표만 쓰면 `rowMid - V/2 - H/2` 식이 실제 thead 높이·행 높이와 어긋나 헤더 쪽으로 치우칠 수 있음.
         */
        const commitForceCenterScroll = (top: number) => {
          commitScroll(top);
        };
        const annualKeyForScroll = rows[rowIdx]?.annualCellKey?.trim() ?? '';
        const anchorSuffix = options?.keyboardAnchorField ?? 'unit';
        const anchorKeyForCenter = annualKeyForScroll ? `${annualKeyForScroll}\t${anchorSuffix}` : '';

        const measureContentMidY = () => {
          const wrapRect = wrap.getBoundingClientRect();
          const headEl = annualTableHeadRef.current;
          const headBottomMeasured = headEl ? headEl.getBoundingClientRect().bottom : wrapRect.top + headerHeight;
          const headBottomFromWrap = wrapRect.top + headerHeight;
          const headBottom = Math.max(headBottomMeasured, headBottomFromWrap);
          return headBottom + (wrapRect.bottom - headBottom) / 2;
        };

        let st = wrap.scrollTop;
        for (let guard = 0; guard < 32; guard += 1) {
          const el = anchorKeyForCenter ? annualCellAnchorRef.current.get(anchorKeyForCenter) : null;
          if (el) {
            commitForceCenterScroll(st);
            const cr = el.getBoundingClientRect();
            const elMidY = cr.top + cr.height / 2;
            const midY = measureContentMidY();
            const err = elMidY - midY;
            if (Math.abs(err) < 1.5) break;
            const prev = st;
            st = Math.min(maxTop, Math.max(0, st + err));
            if (st === prev) break;
            continue;
          }
          const rowMid = rowTop + h / 2;
          const idealTop = rowMid - (H + V) / 2;
          commitForceCenterScroll(idealTop);
          break;
        }
        return;
      }
      const keyboardArrow = options?.keyboardArrow;
      if (keyboardArrow) {
        /**
         * 상·하 키: 가상 행 높이(32)와 실제 DOM 행 높이가 다를 수 있어,
         * sticky thead 하단·스크롤 래퍼 하단과 선택 셀의 getBoundingClientRect 로 맞춤.
         * 앵커가 없으면(가상 창 밖) 기존 수식으로 한 칸씩 스크롤.
         */
        const annualKey = rows[rowIdx]?.annualCellKey?.trim() ?? '';
        const anchorSuffix = options?.keyboardAnchorField ?? 'unit';
        const anchorKey = annualKey ? `${annualKey}\t${anchorSuffix}` : '';

        const commitKeyboardScroll = (top: number) => {
          commitScroll(top);
        };

        let st = wrap.scrollTop;
        for (let guard = 0; guard < 120; guard += 1) {
          commitKeyboardScroll(st);
          const wrapRect = wrap.getBoundingClientRect();
          const headEl = annualTableHeadRef.current;
          const headBottomMeasured = headEl ? headEl.getBoundingClientRect().bottom : wrapRect.top + headerHeight;
          /** ArrowUp만: thead rect와 래퍼 상단+높이 중 아래쪽(큰 Y)을 써서 상단 경계가 화면 위로 밀리는 경우 보정. Down은 기존 측정값 유지 */
          const headBottomFromWrap = wrapRect.top + headerHeight;
          const headBottom =
            keyboardArrow === 'ArrowUp'
              ? Math.max(headBottomMeasured, headBottomFromWrap)
              : headBottomMeasured;
          const contentTopMin =
            keyboardArrow === 'ArrowUp' ? headBottom + KEYBOARD_TABLE_ARROW_UP_TOP_INSET_PX : headBottom;
          const wrapBottom = wrapRect.bottom;
          const el = anchorKey ? annualCellAnchorRef.current.get(anchorKey) : null;

          if (el) {
            const tr = el.closest('tr');
            const rawRowH =
              tr instanceof HTMLTableRowElement ? tr.getBoundingClientRect().height : el.getBoundingClientRect().height;
            const roundedH = Math.round(rawRowH);
            const step = Number.isFinite(roundedH) && roundedH > 0 ? roundedH : h;
            const cr = el.getBoundingClientRect();
            if (cr.top >= contentTopMin - 0.5 && cr.bottom <= wrapBottom + 0.5) break;
            const prev = st;
            if (keyboardArrow === 'ArrowUp' && cr.top < contentTopMin) {
              st = Math.max(0, st - step);
            } else if (keyboardArrow === 'ArrowDown' && cr.bottom > wrapBottom) {
              st = Math.min(maxTop, st + step);
            } else {
              break;
            }
            if (st === prev) break;
            continue;
          }

          const visibleTop =
            keyboardArrow === 'ArrowUp' ? st + headerHeight + KEYBOARD_TABLE_ARROW_UP_TOP_INSET_PX : st + headerHeight;
          const visibleBottom = st + V;
          if (rowTop >= visibleTop && rowBottom <= visibleBottom) break;
          const prev = st;
          if (keyboardArrow === 'ArrowUp' && rowTop < visibleTop) {
            st = Math.max(0, st - h);
          } else if (keyboardArrow === 'ArrowDown' && rowBottom > visibleBottom) {
            st = Math.min(maxTop, st + h);
          } else {
            break;
          }
          if (st === prev) break;
        }
        commitKeyboardScroll(st);
        return;
      }
      const visibleTop = wrap.scrollTop + headerHeight;
      const visibleBottom = wrap.scrollTop + wrap.clientHeight;
      if (rowTop < visibleTop) {
        commitScroll(rowTop - headerHeight);
        return;
      }
      if (rowBottom > visibleBottom) {
        commitScroll(rowBottom - wrap.clientHeight);
      }
    },
    [rows, syncAnnualTableViewport, tableHeadHeight, virtualRowHeight]
  );

  /**
   * 가상 테이블: 첫 페인트 전에는 scrollHeight가 짧아 forceCenter가 먹지 않을 수 있음.
   * `focusAnnualCellKeyFromUrl` 처리와 같이 높이가 충분해질 때까지 rAF로 기다린 뒤 중앙 스크롤을 반복 적용.
   */
  const scheduleAnnualForceCenterScrollWhenReady = React.useCallback(
    (annualKey: string, rowIdxHint: number) => {
      let layoutWaitFrames = 0;
      /** 가상 행 높이(32)와 실제 행 높이가 달라 뒤쪽 행은 scrollHeight 조건이 늦게/영영 안 맞음 — 짧게만 기다림 */
      const LAYOUT_WAIT_CAP = 3;

      const tick = () => {
        const wrap = annualTableScrollRef.current;
        if (!wrap) return;
        const rowIdxRaw = rowIdxByAnnualKeyRef.current[annualKey];
        const rowIdx =
          rowIdxRaw ??
          (Number.isInteger(rowIdxHint) && rowIdxHint >= 0 ? rowIdxHint : -1);
        if (!Number.isInteger(rowIdx) || rowIdx < 0) return;

        syncAnnualTableViewport();
        const headH = annualTableHeadRef.current?.offsetHeight ?? tableHeadHeight;
        const minNeededScrollHeight = headH + (rowIdx + 1) * Math.max(virtualRowHeight, 1);
        const scrollH = wrap.scrollHeight;
        if (scrollH + 8 < minNeededScrollHeight && layoutWaitFrames < LAYOUT_WAIT_CAP) {
          layoutWaitFrames += 1;
          window.requestAnimationFrame(tick);
          return;
        }

        const burst = () => {
          const r0 = rowIdxByAnnualKeyRef.current[annualKey];
          if (!Number.isInteger(r0) || r0 < 0) return;
          scrollAnnualRowIntoView(r0, { forceCenter: true });
          window.requestAnimationFrame(() => {
            const r1 = rowIdxByAnnualKeyRef.current[annualKey];
            if (!Number.isInteger(r1) || r1 < 0) return;
            syncAnnualTableViewport();
            scrollAnnualRowIntoView(r1, { forceCenter: true });
            window.requestAnimationFrame(() => {
              const r2 = rowIdxByAnnualKeyRef.current[annualKey];
              if (!Number.isInteger(r2) || r2 < 0) return;
              syncAnnualTableViewport();
              scrollAnnualRowIntoView(r2, { forceCenter: true });
            });
          });
        };
        burst();
      };

      window.requestAnimationFrame(tick);
    },
    [scrollAnnualRowIntoView, syncAnnualTableViewport, tableHeadHeight, virtualRowHeight]
  );

  const selectRowByAnnualKey = React.useCallback(
    (
      annualKey: string,
      options?: {
        forceCenter?: boolean;
        ensureVisible?: boolean;
        /** true면 스크롤을 건너뜀(행동특성 ↔ 단원 등 좌우 키로만 옮길 때 뷰 유지) */
        skipScroll?: boolean;
        anchorField?: AnnualProgressFieldId;
        rowsBelowHeader?: number;
        keyboardArrow?: AnnualProgressKeyboardArrow;
        /** true면 «오늘 학습내용» 버튼 순환 앵커를 지우지 않음(버튼으로 막 선택한 직후). */
        preserveTodayLessonCycleAnchor?: boolean;
      }
    ) => {
      const forceCenter = options?.forceCenter === true;
      const rowsBelowHeader = options?.rowsBelowHeader;
      const keyboardArrow = options?.keyboardArrow;
      const ensureVisible =
        forceCenter ||
        keyboardArrow != null ||
        (rowsBelowHeader != null && Number.isFinite(rowsBelowHeader)) ||
        options?.ensureVisible === true;
      setSelectionMode('row');
      setSelectedAnnualKey(annualKey);
      if (!options?.preserveTodayLessonCycleAnchor) {
        setTodayLessonCycleAnchorKey(null);
      }

      if (ensureVisible && !options?.skipScroll) {
        if (annualKey in rowIdxByAnnualKeyRef.current) {
          const rowIdx = rowIdxByAnnualKeyRef.current[annualKey];
          if (rowsBelowHeader != null && Number.isFinite(rowsBelowHeader) && rowsBelowHeader >= 0) {
            scrollAnnualRowIntoView(rowIdx, { rowsBelowHeader });
          } else if (keyboardArrow) {
            scrollAnnualRowIntoView(rowIdx, {
              keyboardArrow,
              keyboardAnchorField: options?.anchorField ?? 'unit',
            });
          } else {
            scrollAnnualRowIntoView(rowIdx, {
              forceCenter,
              ...(options?.anchorField
                ? { keyboardAnchorField: options.anchorField }
                : {}),
            });
            if (forceCenter) {
              scheduleAnnualForceCenterScrollWhenReady(annualKey, rowIdx);
            }
          }
        }
      }
    },
    [scrollAnnualRowIntoView, scheduleAnnualForceCenterScrollWhenReady]
  );

  const selectBehaviorCellByAnnualKey = React.useCallback(
    (annualKey: string) => {
      setSelectionMode('behavior');
      setSelectedAnnualKey(annualKey);
      setTodayLessonCycleAnchorKey(null);
    },
    []
  );

  const selectionModeRef = React.useRef(selectionMode);
  const rowByAnnualKeyRef = React.useRef(rowByAnnualKey);
  const rowsRef = React.useRef(rows);

  React.useEffect(() => {
    selectionModeRef.current = selectionMode;
  }, [selectionMode]);
  rowByAnnualKeyRef.current = rowByAnnualKey;
  rowsRef.current = rows;

  React.useEffect(() => {
    if (!isActive) return;
    const onArrowKey = (e: KeyboardEvent) => {
      if (behaviorPopup) return;
      if (e.ctrlKey || e.altKey || e.metaKey) return;
      if (shouldIgnoreAppNavigationShortcut(e)) return;
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;

      const rowKey = selectedAnnualKeyRef.current.trim();
      if (!rowKey) return;

      const rowList = rowsRef.current;
      const idxMap = rowIdxByAnnualKeyRef.current;
      const idx = idxMap[rowKey] ?? -1;
      if (!Number.isInteger(idx) || idx < 0) return;

      const mode = selectionModeRef.current;

      const scrollTo = (
        k: string,
        f: AnnualProgressKeyboardAnchorField,
        keyboardArrow?: AnnualProgressKeyboardArrow
      ) => {
        window.requestAnimationFrame(() => {
          const rowIdx = rowIdxByAnnualKeyRef.current[k];
          if (Number.isInteger(rowIdx)) {
            if (keyboardArrow) {
              scrollAnnualRowIntoView(rowIdx, { keyboardArrow, keyboardAnchorField: f });
            } else {
              scrollAnnualRowIntoView(rowIdx, { forceCenter: false });
            }
          }
          const suffix = f === 'behavior' ? 'behavior' : f;
          const el = annualCellAnchorRef.current.get(`${k}\t${suffix}`);
          if (!keyboardArrow) el?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        });
      };

      if (mode === 'behavior') {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          selectRowByAnnualKey(rowKey, { anchorField: 'unit', skipScroll: true });
          return;
        }
        if (e.key === 'ArrowUp') {
          if (idx <= 0) {
            e.preventDefault();
            return;
          }
          e.preventDefault();
          const next = rowList[idx - 1]?.annualCellKey;
          if (!next) return;
          selectBehaviorCellByAnnualKey(next);
          scrollTo(next, 'behavior', 'ArrowUp');
          return;
        }
        if (e.key === 'ArrowDown') {
          if (idx >= rowList.length - 1) {
            e.preventDefault();
            return;
          }
          e.preventDefault();
          const next = rowList[idx + 1]?.annualCellKey;
          if (!next) return;
          selectBehaviorCellByAnnualKey(next);
          scrollTo(next, 'behavior', 'ArrowDown');
          return;
        }
        if (e.key === 'ArrowRight') {
          e.preventDefault();
        }
        return;
      }

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        return;
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        selectBehaviorCellByAnnualKey(rowKey);
        return;
      }
      if (e.key === 'ArrowUp') {
        if (idx <= 0) {
          e.preventDefault();
          return;
        }
        e.preventDefault();
        const next = rowList[idx - 1]?.annualCellKey;
        if (!next) return;
        selectRowByAnnualKey(next, { anchorField: 'unit', ensureVisible: true, keyboardArrow: 'ArrowUp' });
        return;
      }
      if (e.key === 'ArrowDown') {
        if (idx >= rowList.length - 1) {
          e.preventDefault();
          return;
        }
        e.preventDefault();
        const next = rowList[idx + 1]?.annualCellKey;
        if (!next) return;
        selectRowByAnnualKey(next, { anchorField: 'unit', ensureVisible: true, keyboardArrow: 'ArrowDown' });
        return;
      }
    };

    document.addEventListener('keydown', onArrowKey, true);
    return () => document.removeEventListener('keydown', onArrowKey, true);
  }, [isActive, behaviorPopup, scrollAnnualRowIntoView, selectRowByAnnualKey, selectBehaviorCellByAnnualKey]);

  const selectTodayLessonsClockOnly = React.useCallback(() => {
    if (!selectedEntry) return;
    const key = pickTodayOrNearestFutureForLesson(rows, selectedEntry.sectionRows, {
      pickMode: 'clock_only',
    });
    if (!key) return;
    selectRowByAnnualKey(key, { forceCenter: true });
  }, [rows, selectRowByAnnualKey, selectedEntry]);

  const selectTodayLessonsFromButton = React.useCallback(() => {
    if (!selectedEntry) return;
    const key = pickTodayOrNearestFutureForLesson(rows, selectedEntry.sectionRows, {
      lastTodaySelectionKey: todayLessonCycleAnchorKey,
      pickMode: 'cycle',
    });
    if (!key) return;
    selectRowByAnnualKey(key, {
      forceCenter: true,
      preserveTodayLessonCycleAnchor: true,
    });
    setTodayLessonCycleAnchorKey(key);
  }, [todayLessonCycleAnchorKey, rows, selectRowByAnnualKey, selectedEntry]);
  const selectTomorrowLessons = React.useCallback(() => {
    const base = new Date();
    base.setDate(base.getDate() + 1);
    const tomorrowIso = toISODate(base);
    const key = pickDateOrNearestFutureAnnualCellKey(rows, tomorrowIso, {
      lastSelectionKey: lastTomorrowSelectionKey,
    });
    if (!key) return;
    setLastTomorrowSelectionKey(key);
    selectRowByAnnualKey(key, { forceCenter: true });
  }, [lastTomorrowSelectionKey, rows, selectRowByAnnualKey]);

  React.useEffect(() => {
    if (!isActive) return;
    if (!openTodayToken) return;
    if (handledOpenTodayTokenRef.current === openTodayToken) return;
    pendingOpenTodayTokenRef.current = openTodayToken;
  }, [isActive, openTodayToken]);

  React.useEffect(() => {
    if (!isActive) return;
    const pendingToken = pendingOpenTodayTokenRef.current;
    if (!pendingToken) return;
    if (!selectedEntry) return;
    if (virtualViewportHeight <= 0) return;
    const key = pickTodayOrNearestFutureForLesson(rows, selectedEntry.sectionRows, {
      pickMode: 'clock_only',
    });
    if (!key) return;
    const rowIdx = rowIdxByAnnualKey[key];
    selectRowByAnnualKey(key, { forceCenter: true });
    if (Number.isInteger(rowIdx) && rowIdx >= 0) {
      scheduleAnnualForceCenterScrollWhenReady(key, rowIdx);
    }
    handledOpenTodayTokenRef.current = pendingToken;
    pendingOpenTodayTokenRef.current = '';
  }, [
    isActive,
    rows,
    rowIdxByAnnualKey,
    scheduleAnnualForceCenterScrollWhenReady,
    selectRowByAnnualKey,
    selectedEntry,
    virtualViewportHeight,
  ]);

  /**
   * 탐색(연간시간표 셀 · 프로그램 표 학습내용 → 연간진도표) 진입 대상 행 선택.
   * - URL의 focusAnnualCellKey(+ focusContentRowId)를 렌더 중에 읽어 "이번 이동(location.key)" 1건으로 식별.
   * - 데이터가 준비되면(불러오는 중 해제 + 행·표 높이 있음) 같은 레이아웃 단계에서 즉시
   *   ① 이전 선택을 지우고 ② 우선순위(칸 키 → 내용 행 id → 같은 날짜 가까운 교시 → 없음)로 행을 찾아
   *   ③ 가상 좌표로 바로 스크롤해 그 행을 그리게 한 뒤 ④ 다음 레이아웃 단계에서 실제 셀 기준으로 가운데 보정.
   *   → 가림(visibility)은 데이터 준비 후 1~2 레이아웃 단계(그리기 전)만 유지된다.
   * - 표 실제 높이(scrollHeight)를 기다리지 않는다. 가상 행 높이(32px)와 실제 행 높이가 달라
   *   뒤쪽 행(2학기 마지막 주 등)은 그 조건이 영원히 안 맞아 4초 가림 후 이전 선택이 보이던 원인.
   * - 추가 DB 조회 없음(이미 불러온 rows만 사용).
   */
  const focusContentRowIdFromUrl = searchParams.get('focusContentRowId')?.trim() ?? '';
  const entryFocusNavKey = buildAnnualEntryFocusNavKey(isActive, location.key, focusAnnualCellKeyFromUrl);
  const [entryFocusDoneNavKey, setEntryFocusDoneNavKey] = React.useState('');
  const entryFocusPending = isAnnualEntryFocusPending(entryFocusNavKey, entryFocusDoneNavKey);
  const entryFocusAppliedRef = React.useRef<{ navKey: string; key: string; idx: number; frames: number }>({
    navKey: '',
    key: '',
    idx: -1,
    frames: 0,
  });
  const entryFocusStartedAtRef = React.useRef<{ navKey: string; at: number; sawLoading: boolean }>({
    navKey: '',
    at: 0,
    sawLoading: false,
  });
  const [entryFocusRetryTick, setEntryFocusRetryTick] = React.useState(0);
  const entryFocusResolutionRef = React.useRef<AnnualEntryFocusResolution>({ index: -1, matchedBy: 'none' });
  /** 정확한 칸을 못 찾아 대체 선택했을 때, 잠시 뒤 행이 갱신돼 정확한 칸이 생기면 바로 바꿔 준다 */
  const entryFocusUpgradeRef = React.useRef<{ navKey: string; key: string; until: number }>({
    navKey: '',
    key: '',
    until: 0,
  });

  React.useLayoutEffect(() => {
    if (!entryFocusPending) return;
    const navKey = entryFocusNavKey;
    const targetKey = focusAnnualCellKeyFromUrl;
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    if (entryFocusStartedAtRef.current.navKey !== navKey) {
      entryFocusStartedAtRef.current = { navKey, at: now, sawLoading: false };
      entryFocusAppliedRef.current = { navKey: '', key: '', idx: -1, frames: 0 };
    }
    if (entryGeometryLoading) entryFocusStartedAtRef.current.sawLoading = true;

    let cancelled = false;
    let rafId = 0;
    const finish = (matchedBy: Parameters<typeof logAnnualEntryFocusResolved>[0]['matchedBy'], note?: string) => {
      if (cancelled) return;
      cancelled = true;
      setEntryFocusDoneNavKey(navKey);
      releaseTrappedKeepAliveFocus();
      logAnnualEntryFocusResolved({
        activatedAt: entryFocusStartedAtRef.current.at,
        refetch: entryFocusStartedAtRef.current.sawLoading,
        matchedBy,
        annualCellKey: targetKey,
        note,
      });
    };

    /** 2단계: 1차 스크롤로 대상 행이 그려졌으면 실제 셀 기준 가운데 보정 후 표시(최대 2 레이아웃 단계) */
    const applied = entryFocusAppliedRef.current;
    if (applied.navKey === navKey) {
      applied.frames += 1;
      const anchored = !!annualCellAnchorRef.current.get(`${applied.key}\tunit`);
      if (anchored || applied.frames >= 2) {
        if (applied.idx >= 0) scrollAnnualRowIntoView(applied.idx, { forceCenter: true });
        finish(entryFocusResolutionRef.current.matchedBy, entryFocusResolutionRef.current.note);
        return;
      }
      rafId = requestAnimationFrame(() => {
        if (cancelled) return;
        if (applied.idx >= 0) scrollAnnualRowIntoView(applied.idx, { forceCenter: true });
        finish(entryFocusResolutionRef.current.matchedBy, entryFocusResolutionRef.current.note);
      });
      return () => {
        cancelled = true;
        if (rafId) cancelAnimationFrame(rafId);
      };
    }

    /** 데이터 준비 대기(이 동안은 '불러오는 중' 표시이므로 가림 없음) */
    const wrap = annualTableScrollRef.current;
    const elapsed = now - entryFocusStartedAtRef.current.at;
    const rowList = rowsRef.current;
    const targetGrade = parseAnnualCellKey(targetKey)?.gradeId ?? '';
    const rowsGrade = rowList.length > 0 ? parseAnnualCellKey(rowList[0].annualCellKey)?.gradeId ?? '' : '';
    /** URL로 클래스가 바뀌는 중이면(행이 아직 이전 클래스) 한두 렌더 기다림 */
    const gradeSettling = !!targetGrade && !!rowsGrade && targetGrade !== rowsGrade && elapsed < 1500;
    const notReady =
      entryGeometryLoading || !wrap || wrap.clientHeight <= 0 || rowList.length === 0 || gradeSettling;
    /** 불러오는 중이면 프레임마다 다시 그리지 않고, 로딩 해제 시 이 effect가 다시 실행되기를 기다림 */
    if (entryGeometryLoading) return;
    if (notReady) {
      if (elapsed > 6000) {
        /** 끝내 준비가 안 되면 이전 선택을 남기지 않고 표시만 복구 */
        setSelectedAnnualKey('');
        finish('timeout', `not ready: loading=${entryGeometryLoading} rows=${rowList.length} viewport=${wrap?.clientHeight ?? 0}${gradeSettling ? ' grade-settling' : ''}`);
        return;
      }
      rafId = requestAnimationFrame(() => {
        if (!cancelled) setEntryFocusRetryTick((n) => n + 1);
      });
      return () => {
        cancelled = true;
        if (rafId) cancelAnimationFrame(rafId);
      };
    }

    /** 1단계: 즉시 판정 → 선택 → 가상 좌표 스크롤(대상 행이 가상 창에 들어오도록) */
    const res = resolveAnnualEntryFocusTarget(rowList, rowIdxByAnnualKeyRef.current, {
      annualCellKey: targetKey,
      contentRowId: focusContentRowIdFromUrl,
    });
    entryFocusResolutionRef.current = res;
    if (res.index < 0) {
      /** 대상이 이 진도표에 없음 — 이전 선택을 지우고(이전 주 행이 남지 않게) 바로 표시 */
      setSelectedAnnualKey('');
      finish('none', res.note);
      return;
    }
    const pickedKey = rowList[res.index].annualCellKey;
    if (res.matchedBy !== 'cellKey') {
      entryFocusUpgradeRef.current = { navKey, key: targetKey, until: now + 3000 };
    }
    selectRowByAnnualKey(pickedKey, { skipScroll: true });
    scrollAnnualRowIntoView(res.index, { forceCenter: true });
    entryFocusAppliedRef.current = { navKey, key: pickedKey, idx: res.index, frames: 0 };
    /** scrollAnnualRowIntoView → syncAnnualTableViewport 상태 갱신으로 같은 프레임 안에 다시 렌더·이 effect 재실행 */
    rafId = requestAnimationFrame(() => {
      if (!cancelled) setEntryFocusRetryTick((n) => n + 1);
    });
    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    entryFocusNavKey,
    entryFocusPending,
    entryFocusRetryTick,
    entryGeometryLoading,
    focusAnnualCellKeyFromUrl,
    focusContentRowIdFromUrl,
    rowIdxByAnnualKey,
    rows.length,
    scrollAnnualRowIntoView,
    selectRowByAnnualKey,
    virtualScrollTop,
    virtualViewportHeight,
  ]);

  /** 대체 선택 뒤 행이 갱신돼 정확한 칸이 생기면(최대 3초) 정확한 칸으로 교체 */
  React.useEffect(() => {
    const up = entryFocusUpgradeRef.current;
    if (!up.navKey || up.navKey !== entryFocusNavKey) return;
    const nowTs = typeof performance !== 'undefined' ? performance.now() : Date.now();
    if (nowTs > up.until) {
      entryFocusUpgradeRef.current = { navKey: '', key: '', until: 0 };
      return;
    }
    const idx = rowIdxByAnnualKey[up.key];
    if (!Number.isInteger(idx) || idx < 0) return;
    entryFocusUpgradeRef.current = { navKey: '', key: '', until: 0 };
    selectRowByAnnualKey(up.key, { forceCenter: true });
    logAnnualEntryFocusResolved({
      activatedAt: entryFocusStartedAtRef.current.at,
      refetch: true,
      matchedBy: 'cellKey',
      annualCellKey: up.key,
      note: 'late exact match after rows refreshed',
    });
  }, [entryFocusNavKey, rowIdxByAnnualKey, selectRowByAnnualKey]);

  const selectedAnnualKeyForProgramBrowse = React.useMemo(() => selectedAnnualKey.trim(), [selectedAnnualKey]);

  const selectedAnnualTimetableBrowseTarget = React.useMemo(() => {
    if (!selectedEntry) return null;
    const annualKey = selectedAnnualKeyForProgramBrowse.trim();
    if (!annualKey) return null;
    return `/annual-timetable?${createSearchParams({
      gradeId: selectedEntry.gradeId,
      programTerm: String(termNumber),
      focusAnnualCellKey: annualKey,
    }).toString()}`;
  }, [selectedAnnualKeyForProgramBrowse, selectedEntry, termNumber]);

  const selectedProgramBrowseTarget = React.useMemo(() => {
    if (!selectedEntry) return null;
    const annualKey = selectedAnnualKeyForProgramBrowse.trim();
    if (!annualKey) return null;
    const row = rowByAnnualKey[annualKey];
    if (!row) return null;
    const baseParams: Record<string, string> = {
      gradeId: selectedEntry.gradeId,
      programTerm: String(termNumber),
      programYear: String(academicYear),
      focusAnnualCellKey: annualKey,
    };
    if (row.programKindLabel === '고정' || fixedContentBySourceAnnualKey.has(annualKey)) {
      return `/program/fixed?${createSearchParams(baseParams).toString()}`;
    }
    if (row.programKindLabel === '프로') {
      const rowColor = row.timetableColorHex.trim().toLowerCase();
      const project = annualProjectProgramsForSelectedGrade.find(
        (p) => p.termNumber === termNumber && p.colorHex.trim().toLowerCase() === rowColor
      );
      if (!project) return null;
      const path = `/program/project/${encodeURIComponent(project.id)}`;
      return `${path}?${createSearchParams(baseParams).toString()}`;
    }
    const trackName = row.templateLabel.trim();
    if (!trackName) return null;
    const trackParams: Record<string, string> = { ...baseParams };
    const timetableName = row.timetableText.trim();
    if (timetableName) trackParams.programTimetableName = timetableName;
    const path = `/program/track/${encodeURIComponent(trackName)}`;
    return `${path}?${createSearchParams(trackParams).toString()}`;
  }, [
    annualProjectProgramsForSelectedGrade,
    academicYear,
    fixedContentBySourceAnnualKey,
    rowByAnnualKey,
    selectedAnnualKeyForProgramBrowse,
    selectedEntry,
    termNumber,
  ]);

  const openBehaviorPopup = React.useCallback((annualCellKey: string) => {
    if (requireFeature('behavior_record')) return;
    const row = rowByAnnualKeyRef.current[annualCellKey];
    if (!row) return;
    // 새 셀 오픈 시 이전 셀의 위치 스타일 재사용을 막아 초기 점프를 방지
    setBehaviorPopupStyle(null);
    setBehaviorPopup({
      annualCellKey,
    });
    setBehaviorSaveError(null);
  }, [requireFeature]);

  const openProgramRecordModal = React.useCallback(
    (annualCellKey: string, kind: AnnualProgressProgramRecordModalKind) => {
      if (kind === 'class' && requireFeature('class_record')) return;
      const row = rowByAnnualKeyRef.current[annualCellKey];
      if (!row) return;
      const target = kind === 'eval' ? row.evalRecordTarget : row.classRecordTarget;
      if (!target) return;
      setProgramRecordModal({ annualCellKey, kind });
    },
    [requireFeature]
  );

  /** 행동특성 열 헤더: 선택된 행이 없으면 무반응. 있으면 해당 행 행동특성 셀 선택 후 본문 셀 클릭과 동일하게 모달 연다. */
  const onBehaviorHeadButtonClick = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      const key = selectedAnnualKeyRef.current.trim();
      if (!key) return;
      if (!rowByAnnualKeyRef.current[key]) return;
      selectBehaviorCellByAnnualKey(key);
      focusBrowseScrollWrapIfVisible(annualTableScrollRef.current);
      openBehaviorPopup(key);
    },
    [openBehaviorPopup, selectBehaviorCellByAnnualKey]
  );
  const openBehaviorListItem = React.useCallback(
    (annualCellKey: string) => {
      const rowIdx = rowIdxByAnnualKeyRef.current[annualCellKey];
      if (rowIdx == null) return;
      selectBehaviorCellByAnnualKey(annualCellKey);
      scrollAnnualRowIntoView(rowIdx, { forceCenter: true, keyboardAnchorField: 'behavior' });
      focusBrowseScrollWrapIfVisible(annualTableScrollRef.current);
      openBehaviorPopup(annualCellKey);
      setBehaviorListOpen(false);
    },
    [openBehaviorPopup, scrollAnnualRowIntoView, selectBehaviorCellByAnnualKey]
  );

  const visibleBodyTop = Math.max(0, virtualScrollTopRef.current - tableHeadHeight);
  const visibleBodyBottom = Math.max(
    visibleBodyTop,
    virtualScrollTopRef.current + virtualViewportHeight - tableHeadHeight
  );
  const virtualStartRow = React.useMemo(() => {
    if (rows.length === 0) return 0;
    return Math.max(0, Math.floor(visibleBodyTop / Math.max(virtualRowHeight, 1)) - ANNUAL_PROGRESS_OVERSCAN_ROWS);
  }, [rows.length, virtualRowHeight, visibleBodyTop]);
  const virtualEndRow = React.useMemo(() => {
    if (rows.length === 0) return 0;
    return Math.min(
      rows.length,
      Math.ceil(visibleBodyBottom / Math.max(virtualRowHeight, 1)) + ANNUAL_PROGRESS_OVERSCAN_ROWS
    );
  }, [rows.length, virtualRowHeight, visibleBodyBottom]);
  const virtualTopSpacerHeight = virtualStartRow * virtualRowHeight;
  const virtualBottomSpacerHeight = Math.max(0, (rows.length - virtualEndRow) * virtualRowHeight);
  const visibleRows = React.useMemo(() => rows.slice(virtualStartRow, virtualEndRow), [rows, virtualEndRow, virtualStartRow]);

  const commitBehaviorNote = React.useCallback(
    async (
      annualCellKey: string,
      note: string,
      recordMemberGroupId: string | null,
      aiFeedback?: string
    ): Promise<boolean> => {
      if (!selectedEntry) return false;
      const row = rowByAnnualKeyRef.current[annualCellKey];
      if (!row) return false;
      setBehaviorSaving(true);
      setBehaviorSaveError(null);
      try {
        const lessonDate = row.dateIso.trim();
        const dayRowOrder = row.dayRowOrder;
        const noteTrimmed = (note ?? '').trim();
        if (!lessonDate) return false;
        if (!noteTrimmed) {
          await deleteAnnualBehaviorNoteByNaturalKey({
            workspaceId,
            gradeId: selectedEntry.gradeId,
            academicYear,
            termNumber,
            lessonDate,
            dayRowOrder,
          });
        } else {
          await upsertAnnualBehaviorNote({
            workspace_id: workspaceId,
            grade_id: selectedEntry.gradeId,
            academic_year: academicYear,
            term_number: termNumber,
            lesson_date: lessonDate,
            day_row_order: dayRowOrder,
            behavior_note: noteTrimmed,
            record_member_group_id: recordMemberGroupId,
            source_annual_cell_key_snapshot: annualCellKey,
            period_label_snapshot: row.periodLabel,
            timetable_text_snapshot: row.timetableText,
            ...(aiFeedback !== undefined ? { ai_feedback: aiFeedback } : {}),
          });
        }
        setBehaviorPopup(null);
        setBehaviorPopupStyle(null);
        return true;
      } catch (e) {
        setBehaviorSaveError(e instanceof Error ? e.message : '행동특성 저장에 실패했습니다.');
        return false;
      } finally {
        setBehaviorSaving(false);
      }
    },
    [academicYear, selectedEntry, workspaceId, termNumber]
  );

  const deleteBehaviorNote = React.useCallback(
    async (annualCellKey: string) => {
      await commitBehaviorNote(annualCellKey, '', null);
    },
    [commitBehaviorNote]
  );

  React.useEffect(() => {
    if (!isActive) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (!e.shiftKey || e.ctrlKey || e.altKey || e.metaKey) return;
      if (e.code !== 'KeyT' && e.code !== 'KeyY') return;
      if (e.repeat) return;
      if (shouldIgnoreAppNavigationShortcut(e)) return;
      e.preventDefault();
      e.stopPropagation();
      if (e.code === 'KeyY') {
        selectTomorrowLessons();
      } else if (e.isTrusted) {
        selectTodayLessonsFromButton();
      } else {
        /* Shift+R 등에서 보내는 합성 KeyT: 매번 현재 시각 교시만 */
        selectTodayLessonsClockOnly();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [isActive, selectTodayLessonsClockOnly, selectTodayLessonsFromButton, selectTomorrowLessons]);

  const presentationListItems = React.useMemo(() => {
    const sid = workspaceId?.trim() ?? '';
    const items: ProgramPresentationListItem[] = [];
    for (const r of rows) {
      if (!learningSlidePagesByAnnualKey[r.annualCellKey]) continue;
      const candidates = r.slideOwnerCandidates ?? [];
      const hasDb = candidates.some((o) =>
        annualProgressSlidePresenceKeys.has(programContentSlidePresenceKey(o))
      );
      const slideOwner = resolvePresentationListSlideOwner({
        candidates,
        presenceKeys: annualProgressSlidePresenceKeys,
        fallbackOwner: r.slideOpenDbOwner ?? r.slideOwner,
      });
      const isDraftOnly =
        !hasDb && !!sid && candidates.some((o) => hasProgramContentSlideDraftForOwner(sid, o));
      items.push({
        annualCellKey: r.annualCellKey,
        dateLabel: r.dateLabel,
        weekdayLabel: r.weekdayLabel,
        periodLabel: r.periodLabel,
        templateLabel: r.templateLabel,
        timetableText: r.timetableText,
        unitText: r.progressUnit,
        learningText: r.progressLearning,
        sortKey: `${r.mondayIso}|${r.weekdayKey}|${String(r.slotIndex).padStart(2, '0')}|${String(r.seq).padStart(5, '0')}`,
        slideOwner,
        isDraftOnly,
        isSyncMirror: r.isPresentationSyncMirror,
        hasPresentationRef: !!resolvePresentationRefIdFromLearningText(r.slideLearningTextRaw),
        slideOwnerCandidates: candidates,
      });
    }
    return sortPresentationListItems(items);
  }, [annualProgressSlidePresenceKeys, learningSlidePagesByAnnualKey, rows, workspaceId]);

  const presentationListItemsMerged = React.useMemo(() => {
    const cellById = new Map<string, ProgramProgressCell>();
    Object.values(programCellsByKey).forEach((cell) => {
      if (cell?.id?.trim()) cellById.set(cell.id, cell);
    });
    return mergeOrphanPresentationListItems(presentationListItems, orphanPresentationEntries, {
      cellById,
      periodLabelForSlot: (slotIndex) => periodLabels[slotIndex] || `${slotIndex + 1}타임`,
      weekdayLabelForKey: (key) => weekdayLabel(key as AnnualWeekdayKey),
      shortDateLabelForIso: shortDateLabel,
    });
  }, [orphanPresentationEntries, periodLabels, presentationListItems, programCellsByKey]);

  const resolveAnnualPresentationActionState = React.useCallback(
    (annualCellKey: string) => {
      const sid = workspaceId?.trim() ?? '';
      const orphanOwner = annualCellKey.startsWith('slide:')
        ? parsePresentationAuditSelectionKey(annualCellKey)
        : null;
      if (orphanOwner) {
        return {
          canCopy: true,
          canDelete: true,
          copySourceOwner: orphanOwner,
        };
      }
      const row = rows.find((r) => r.annualCellKey === annualCellKey);
      if (!sid || !row) {
        return { canCopy: false, canDelete: false, copySourceOwner: null as ProgramContentSlideOwnerRef | null };
      }
      if (row.isPresentationSyncMirror) {
        return { canCopy: false, canDelete: false, copySourceOwner: null };
      }
      const hasPresentation = !!learningSlidePagesByAnnualKey[annualCellKey];
      const candidates = row.slideOwnerCandidates ?? [];
      const resolvedOwner =
        row.slideOwner?.kind === 'presentation_ref' && isPersistableUuid(row.slideOwner.id)
          ? row.slideOwner
          : resolvePresentationListSlideOwner({
              candidates,
              presenceKeys: annualProgressSlidePresenceKeys,
              fallbackOwner: row.slideOpenDbOwner ?? row.slideOwner,
            });
      const copySourceOwner =
        hasPresentation && resolvedOwner && isPersistableUuid(resolvedOwner.id) ? resolvedOwner : null;
      return {
        canCopy: !!copySourceOwner,
        canDelete: hasPresentation,
        copySourceOwner,
      };
    },
    [annualProgressSlidePresenceKeys, learningSlidePagesByAnnualKey, rows, workspaceId]
  );

  const copyAnnualPresentationAtKey = React.useCallback(
    (annualCellKey: string): boolean => {
      const state = resolveAnnualPresentationActionState(annualCellKey);
      if (!state.copySourceOwner) return false;
      annualPresentationClipboardRef.current = { owner: state.copySourceOwner };
      showPresentationListToast('프레젠테이션을 복사했습니다.', 1800);
      return true;
    },
    [resolveAnnualPresentationActionState, showPresentationListToast]
  );

  const deleteAnnualPresentationAtKey = React.useCallback(
    (annualCellKey: string): boolean => {
      const sid = workspaceId?.trim() ?? '';
      if (!sid || !resolveAnnualPresentationActionState(annualCellKey).canDelete) return false;
      const patchAnnualKey =
        annualSyncMirrorIndicatorMaps.repAnnualCellKeyByMirrorKey[annualCellKey.trim()]?.trim() ?? annualCellKey;
      const keysToMark = [annualCellKey, patchAnnualKey];
      for (const [mirrorKey, repKey] of Object.entries(annualSyncMirrorIndicatorMaps.repAnnualCellKeyByMirrorKey)) {
        if (repKey.trim() === patchAnnualKey.trim()) keysToMark.push(mirrorKey);
      }
      markLearningAttachmentIndicatorDeletedForSyncKeys(sid, keysToMark, 'presentation');
      if (annualCellKey.startsWith('slide:')) {
        invalidateAnnualPresentationAuditQuery(workspaceId, academicYear, termNumber, selectedEntry?.gradeId ?? '');
        setAnnualProgressSlidePresenceKeys((prev) => {
          const owner = parsePresentationAuditSelectionKey(annualCellKey);
          if (!owner) return prev;
          const next = new Set(prev);
          next.delete(programContentSlidePresenceKey(owner));
          return next;
        });
        void deletePresentationAuditSelections({ workspaceId: sid, selectionKeys: [annualCellKey] });
        return true;
      }
      const row = rows.find((r) => r.annualCellKey === annualCellKey);
      if (!row) return false;
      const refId = resolvePresentationRefIdFromLearningText(row.slideLearningTextRaw);
      const candidates = (row.slideOwnerCandidates ?? []).filter((owner) => isPersistableUuid(owner.id));
      const scopeId = row.slideWorkspaceScopeId.trim();
      setAnnualProgressSlidePresenceKeys((prev) => {
        const next = new Set(prev);
        candidates.forEach((owner) => next.delete(programContentSlidePresenceKey(owner)));
        if (refId) next.delete(programContentSlidePresenceKey({ kind: 'presentation_ref', id: refId }));
        return next;
      });
      void (async () => {
        for (const owner of candidates) {
          try {
            if (scopeId) {
              queueProgramContentSlideDeleteDraft({ workspaceId: sid, scopeId, owner });
            } else {
              await deleteProgramContentSlidePagesForOwner(sid, owner);
            }
          } catch {
            /* ignore */
          }
        }
        if (refId) {
          try {
            await deleteProgramContentSlidePagesForOwner(sid, { kind: 'presentation_ref', id: refId });
          } catch {
            /* ignore */
          }
          const nextLearning = learningTextStorageWithoutPresentationRef(row.slideLearningTextRaw);
          /** 이 칸과 짝지어진 학습내용 행(id) — 연간칸 키로 찾지 않는다 */
          const scopedOwnerIds = new Set(
            (row.slideOwnerCandidates ?? [])
              .filter((owner) => owner.kind === 'scoped_row')
              .map((owner) => owner.id.trim().toLowerCase())
          );
          if (row.slideOwner?.kind === 'scoped_row') scopedOwnerIds.add(row.slideOwner.id.trim().toLowerCase());
          const scopedRow = scopedRowsForTerm.find((r) => scopedOwnerIds.has((r.id ?? '').trim().toLowerCase()));
          const gradeId = selectedEntry?.gradeId ?? '';
          if (scopedRow?.id && gradeId) {
            await updateProgramScopedContentRowFieldsById(scopedRow.id, { learning_text: nextLearning });
          } else {
            const pc = programCellsByKey[annualCellKey];
            if (pc && gradeId) {
              await upsertProgramProgressCells([
                {
                  id: pc.id,
                  workspace_id: sid,
                  grade_id: gradeId,
                  academic_year: academicYear,
                  term_number: termNumber,
                  monday_iso: pc.monday_iso,
                  weekday_key: pc.weekday_key,
                  slot_index: pc.slot_index,
                  timetable_text: pc.timetable_text,
                  unit_text: pc.unit_text,
                  learning_text: nextLearning,
                  page_text: pc.page_text,
                  assistant_page_text: pc.assistant_page_text,
                  current_period_text: pc.current_period_text,
                  total_period_text: pc.total_period_text,
                  material_text: pc.material_text,
                  record_cross_curricular_text: pc.record_cross_curricular_text,
                  record_memo_text: pc.record_memo_text,
                  record_evaluation_text: pc.record_evaluation_text,
                  record_class_text: pc.record_class_text,
                  record_member_group_id: pc.record_member_group_id,
                  bg_color_by_field: pc.bg_color_by_field ?? {},
                },
              ]);
            }
          }
          if (gradeId) {
            clearProgramDisplayCachesForGrade(workspaceId, academicYear, termNumber, gradeId);
            invalidateAnnualPresentationAuditQuery(workspaceId, academicYear, termNumber, gradeId);
            setAnnualAggregateRefreshNonce((n) => n + 1);
          }
        }
        invalidateProgramContentSlidePresenceCache(sid);
      })();
      return true;
    },
    [
      academicYear,
      annualSyncMirrorIndicatorMaps,
      programCellsByKey,
      resolveAnnualPresentationActionState,
      rows,
      scopedRowsForTerm,
      selectedEntry?.gradeId,
      termNumber,
      workspaceId,
    ]
  );

  const openAnnualProgressPresentationForKey = React.useCallback(
    (key: string) => {
      gatePresentationOpen(() => {
        void (async () => {
      if (requireFeature('presentation')) return;
      if (key.startsWith('slide:')) {
        const sid = workspaceId?.trim() ?? '';
        const owner = parsePresentationAuditSelectionKey(key);
        if (!sid || !owner) {
          showPresentationListToast('프레젠테이션을 열 수 없습니다.');
          return;
        }
        touchAuthActivity();
        markDevPresentationOpenTransferScope();
        const handoff = resolveClassPresentationSlideOpenOptions(tutorial);
        const opened = openProgramContentSlideInNewTab(
          {
            v: 1,
            workspaceId: sid,
            academicYear,
            termNumber,
            owner,
            extraSlideBodies: [''],
            meta: {
              templateLabel: '',
              unit: '',
              page: '',
              assistantPage: '',
              programContent: '',
              achievementText: '',
            },
          },
          handoff
            ? { tutorialId: handoff.tutorialId, tutorialStep: handoff.tutorialStep }
            : undefined
        );
        if (!opened.ok) {
          showPresentationListToast(openProgramContentSlideFailureMessage(opened.reason));
        } else if (handoff) {
          finishClassPresentationHandoffOnOpener(tutorial);
        }
        return;
      }
      const row = rows.find((r) => r.annualCellKey === key);
      if (!row) return;
      touchAuthActivity();
      markDevPresentationOpenTransferScope();
      const slideTab = openProgramContentSlideTabPlaceholder();
      try {
      const gradeId = selectedEntry?.gradeId ?? '';
      const learningDisplay = learningDisplayByAnnualKey[row.annualCellKey];
      const metaBase = {
        templateLabel: row.templateLabel.trim(),
        unit: row.progressUnit.trim(),
        page: row.progressPage.trim(),
        assistantPage: row.progressAssistantPage.trim(),
        programContent: (learningDisplay?.text ?? row.progressLearning).trim(),
        achievementText: row.progressUnitNote.trim(),
        learningMemoNote: (learningDisplay?.note ?? row.progressLearningNote).trim(),
        evaluationMemoNote: row.progressEvalNote.trim(),
      };
      const weeklyGuideTrackColorHex =
        gradeId && metaBase.templateLabel
          ? await resolveWeeklyLearningGuideTrackBackgroundHex({
              workspaceId,
              academicYear,
              gradeId,
              trackDisplayName: metaBase.templateLabel,
              tracks,
            })
          : '';
      const slideLoadOwner = await resolveSlideTabOwnerForSave({
        workspaceId,
        candidates: row.slideOwnerCandidates ?? [],
        presenceKeys: annualProgressSlidePresenceKeys,
        presenceKeyOf: programContentSlidePresenceKey,
        fallbackOwner: row.slideOpenDbOwner ?? row.slideOwner,
      });
      // DB 행이 없으면 로컬 전용으로 연다(편집 가능·저장 불가).
      // 학습내용 초안이 있을 때만 첫 저장 시 바인딩 생성을 허용한다.
      const createOnSave =
        slideLoadOwner || (row.slideLearningTextRaw ?? '').trim()
          ? row.slideCreatePresentationRefOnSave
          : undefined;
      const handoff = resolveClassPresentationSlideOpenOptions(tutorial);
      const opened = openProgramContentSlideInNewTab(
        {
          v: 1,
          workspaceId,
          academicYear,
          termNumber,
          annualCellKey: key,
          contextGradeId: gradeId || undefined,
          scopeId: row.slideWorkspaceScopeId.trim() || undefined,
          owner: slideLoadOwner,
          learningTextOwner: row.slideOpenDbOwner ?? row.slideOwner,
          bindingLearningText: row.slideLearningTextRaw,
          createPresentationRefOnSave: createOnSave,
          extraSlideBodies: [''],
          meta: {
            ...metaBase,
            weeklyGuideTrackColorHex: weeklyGuideTrackColorHex || undefined,
          },
        },
        {
          preOpenedWindow: slideTab ?? undefined,
          ...(handoff
            ? { tutorialId: handoff.tutorialId, tutorialStep: handoff.tutorialStep }
            : {}),
        }
      );
      if (!opened.ok) {
        showPresentationListToast(openProgramContentSlideFailureMessage(opened.reason));
      } else if (handoff) {
        finishClassPresentationHandoffOnOpener(tutorial);
      }
      } catch {
        closeProgramContentSlideTabPlaceholder(slideTab);
        showPresentationListToast('프레젠테이션을 열 수 없습니다.');
      }
    })();
      });
    },
    [
      academicYear,
      annualProgressSlidePresenceKeys,
      learningDisplayByAnnualKey,
      rows,
      selectedEntry?.gradeId,
      termNumber,
      showPresentationListToast,
      tracks,
      tutorial,
      workspaceId,
    ]
  );

  const handleOpenAnnualProgressPresentation = React.useCallback(async () => {
    const key = selectedAnnualKey.trim();
    if (!key) return;
    await openAnnualProgressPresentationForKey(key);
  }, [openAnnualProgressPresentationForKey, selectedAnnualKey]);

  const worksheetListItems = React.useMemo(() => {
    const sid = workspaceId?.trim() ?? '';
    const items: ProgramWorksheetListItem[] = [];
    for (const r of rows) {
      if (!learningWorksheetByAnnualKey[r.annualCellKey]) continue;
      const learningText = resolveAnnualIndicatorLearningText(
        r,
        mergedLearningTextOverlayByKey,
        annualSyncMirrorIndicatorMaps
      );
      let refId = resolveWorksheetRefIdFromLearningText(learningText);
      const draftRowKey = resolveWorksheetDraftStorageRowKey(
        r.annualCellKey,
        r.isPresentationSyncMirror,
        annualSyncMirrorIndicatorMaps.repAnnualCellKeyByMirrorKey
      );
      if (!refId && sid) {
        refId = resolvePendingWorksheetRefIdForAnnualKeys(sid, r.annualCellKey, draftRowKey);
      }
      const hasDb = !!refId && worksheetPresenceRefIds.has(refId);
      const hasLocalDraft = !!loadLearningContentWorksheetDraft(draftRowKey);
      const hasPending =
        (!!refId && !!sid && hasPendingLearningWorksheetFlush(sid, refId)) ||
        (!!sid && hasPendingLearningWorksheetFlushForAnnualKeys(sid, r.annualCellKey, draftRowKey));
      items.push(
        buildWorksheetListItemFromAnnualRow({
          annualCellKey: r.annualCellKey,
          dateLabel: r.dateLabel,
          weekdayLabel: r.weekdayLabel,
          periodLabel: r.periodLabel,
          templateLabel: r.templateLabel,
          timetableText: r.timetableText,
          unitText: r.progressUnit,
          learningTextRaw: learningText,
          sortKey: `${r.mondayIso}|${r.weekdayKey}|${String(r.slotIndex).padStart(2, '0')}|${String(r.seq).padStart(5, '0')}`,
          isDraftOnly: !hasDb && (hasLocalDraft || hasPending),
          hasLocalDraft,
          hasDbDocument: hasDb,
          draftStorageRowKey: draftRowKey,
          isSyncMirror: r.isPresentationSyncMirror,
        })
      );
    }
    return sortWorksheetListItems(items);
  }, [
    annualSyncMirrorIndicatorMaps,
    learningWorksheetByAnnualKey,
    mergedLearningTextOverlayByKey,
    rows,
    worksheetPresenceRefIds,
    workspaceId,
  ]);

  const openAnnualWorksheetEditorForKey = React.useCallback(
    (annualKey: string) => {
      if (requireFeature('worksheet')) return;
      const sid = workspaceId?.trim();
      if (!sid) {
        showPresentationListToast('워크스페이스 정보가 없어 활동지를 열 수 없습니다.');
        return;
      }
      const row = rows.find((r) => r.annualCellKey === annualKey);
      if (!row) return;
      if (row.isPresentationSyncMirror) {
        showPresentationListToast(
          '동기화 미러 — 대표 시간표(원본) 행에서만 열고 편집할 수 있습니다.'
        );
        return;
      }
      if (!row.slideWorkspaceScopeId.trim()) {
        showPresentationListToast(
          '아직 이 칸에 저장된 프로그램 내용이 없어 활동지를 열 수 없습니다. 먼저 해당 칸을 저장한 뒤 다시 시도해 주세요.'
        );
        return;
      }
      const learningText = resolveAnnualIndicatorLearningText(
        row,
        mergedLearningTextOverlayByKey,
        annualSyncMirrorIndicatorMaps
      );
      const existingRefId = resolveWorksheetRefIdFromLearningText(learningText);
      const draftRowKey = resolveWorksheetDraftStorageRowKey(
        annualKey,
        row.isPresentationSyncMirror,
        annualSyncMirrorIndicatorMaps.repAnnualCellKeyByMirrorKey
      );
      setWorksheetEditorAnnualKey(annualKey);
      setWorksheetEditorOpen(true);
      void resolveWorksheetDocumentForEditor({
        workspaceId: sid,
        worksheetRefId: existingRefId,
        draftStorageKey: draftRowKey,
      })
        .then((doc) => {
          setWorksheetEditorInitialDocument(doc);
        })
        .catch(() => {
          setWorksheetEditorInitialDocument(null);
          if (existingRefId) {
            showPresentationListToast('활동지를 불러오지 못했습니다.');
          }
        });
    },
    [
      annualSyncMirrorIndicatorMaps,
      mergedLearningTextOverlayByKey,
      rows,
      showPresentationListToast,
      workspaceId,
    ]
  );

  const handleOpenAnnualProgressWorksheet = React.useCallback(() => {
    const key = selectedAnnualKey.trim();
    if (!key) return;
    openAnnualWorksheetEditorForKey(key);
  }, [openAnnualWorksheetEditorForKey, selectedAnnualKey]);

  const handleSaveAnnualWorksheetDocument = React.useCallback(
    async (
      document: LearningContentWorksheetDocumentV1,
      _meta?: { pageNames: string[]; pageIds: string[] },
      options?: { silent?: boolean }
    ) => {
      const silent = !!options?.silent;
      const sid = workspaceId?.trim();
      const annualKey = worksheetEditorAnnualKey?.trim();
      if (!sid || !annualKey) throw new Error('missing scope');
      const row = rows.find((r) => r.annualCellKey === annualKey);
      if (!row) throw new Error('missing row');
      if (row.isPresentationSyncMirror) throw new Error('mirror row');
      const scopeId = row.slideWorkspaceScopeId.trim();
      if (!scopeId) throw new Error('missing program scope');

      const dirtyIds = collectDirtyWorkspaceScopeIdsForAttachmentSave({
        scopeId,
        workspaceId: sid,
        gradeId: selectedEntry?.gradeId ?? '',
        academicYear,
        termNumber,
      });
      /** 자동 저장(5분·닫을 때)은 묻지 않고 활동지만 DB에 저장한다 */
      const coSaveProgramTable = dirtyIds.length > 0 && !silent;
      if (coSaveProgramTable) {
        const ok = window.confirm(programTableDirtyWithAttachmentSaveConfirmMessage('worksheet'));
        if (!ok) throw new ProgramTableCoSaveCancelledError();
      }

      const learningTextRaw = resolveAnnualIndicatorLearningText(
        row,
        mergedLearningTextOverlayByKey,
        annualSyncMirrorIndicatorMaps
      );
      let worksheetRefId = resolveWorksheetRefIdFromLearningText(learningTextRaw);
      if (!worksheetRefId || !isPersistableUuid(worksheetRefId)) {
        worksheetRefId = createWorksheetRefOnSave();
      }
      const nextLearningText = learningTextWithWorksheetRef(learningTextRaw, worksheetRefId);
      const draftRowKey = resolveWorksheetDraftStorageRowKey(
        annualKey,
        row.isPresentationSyncMirror,
        annualSyncMirrorIndicatorMaps.repAnnualCellKeyByMirrorKey
      );

      const dbOwner = row.slideOpenDbOwner;
      let learningPatch: ProgramWorksheetLearningTextPatch | null = null;
      if (
        dbOwner &&
        (dbOwner.kind === 'program_cell' || dbOwner.kind === 'fixed_row' || dbOwner.kind === 'scoped_row') &&
        isPersistableUuid(dbOwner.id)
      ) {
        learningPatch = { kind: dbOwner.kind, id: dbOwner.id.trim(), learningText: nextLearningText };
      }

      queueLearningWorksheetPendingFlush({
        workspaceId: sid,
        scopeId,
        worksheetRefId,
        annualKey: draftRowKey,
        document,
        learningPatch,
        annualFlushMeta: learningPatch
          ? null
          : {
              academicYear,
              termNumber,
              gradeId: selectedEntry?.gradeId ?? '',
              nextLearningText,
              slideOpenDbOwner: row.slideOpenDbOwner,
              slideWorkspaceScopeId: scopeId,
              createProgramRowContext: row.slideCreatePresentationRefOnSave,
            },
      });

      notifyProgramContentSlideLearningUpdated({
        workspaceId: sid,
        scopeId,
        annualKey: draftRowKey,
        learningText: nextLearningText,
        owner:
          learningPatch != null
            ? { kind: learningPatch.kind, id: learningPatch.id }
            : dbOwner &&
                (dbOwner.kind === 'program_cell' ||
                  dbOwner.kind === 'fixed_row' ||
                  dbOwner.kind === 'scoped_row') &&
                isPersistableUuid(dbOwner.id)
              ? { kind: dbOwner.kind, id: dbOwner.id.trim() }
              : null,
        syncAnnualLearningTextOverlay: true,
      });

      setAnnualWorksheetDraftRev((n) => n + 1);
      if (coSaveProgramTable) {
        await flushPendingLearningWorksheetDrafts([scopeId]);
        await saveProgramWorkspaceScopes(dirtyIds);
        showPresentationListToast('활동지와 프로그램 표를 저장했습니다.');
      } else {
        await flushPendingLearningWorksheetDraftForRef(sid, worksheetRefId);
        if (!silent) showPresentationListToast('활동지를 저장했습니다.');
      }
    },
    [
      academicYear,
      annualSyncMirrorIndicatorMaps,
      mergedLearningTextOverlayByKey,
      rows,
      selectedEntry?.gradeId,
      showPresentationListToast,
      termNumber,
      worksheetEditorAnnualKey,
      workspaceId,
    ]
  );

  React.useEffect(() => {
    if (!isActive) return;
    const onKey = (e: KeyboardEvent) => {
      if (!e.shiftKey || e.ctrlKey || e.altKey || e.metaKey) return;
      if (e.repeat) return;
      if (shouldIgnoreAppNavigationShortcut(e)) return;
      if (e.code === 'KeyG') {
        if (!selectedProgramBrowseTarget) return;
        e.preventDefault();
        e.stopPropagation();
        navigate(selectedProgramBrowseTarget);
        return;
      }
      if (e.code === 'KeyF') {
        if (!selectedAnnualTimetableBrowseTarget) return;
        e.preventDefault();
        e.stopPropagation();
        navigate(selectedAnnualTimetableBrowseTarget);
        return;
      }
      if (e.code === 'KeyU') {
        const key = selectedAnnualKey.trim();
        if (!key) return;
        const row = rows.find((r) => r.annualCellKey === key);
        if (!row) return;
        e.preventDefault();
        e.stopPropagation();
        void handleOpenAnnualProgressPresentation();
        return;
      }
      if (e.code === 'KeyI') {
        const key = selectedAnnualKey.trim();
        if (!key) return;
        e.preventDefault();
        e.stopPropagation();
        handleOpenAnnualProgressWorksheet();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [
    handleOpenAnnualProgressPresentation,
    handleOpenAnnualProgressWorksheet,
    isActive,
    navigate,
    rows,
    selectedAnnualKey,
    selectedAnnualTimetableBrowseTarget,
    selectedProgramBrowseTarget,
  ]);

  React.useEffect(() => {
    if (!behaviorPopup) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (behaviorPopupRef.current?.contains(target)) return;
      /** 튜토리얼 자체 UI(말풍선 "다음" 버튼 등) 클릭은 "바깥 클릭"으로 취급하지 않는다.
       * 팝업을 닫고 다음 단계로 넘기는 책임은 튜토리얼 엔진이 전담한다. */
      const targetEl = target instanceof Element ? target : target.parentElement;
      if (targetEl?.closest('.cp-tutorial-root')) return;
      /** 모달 배경(반투명)·태그 그룹 선택 모달 클릭은 각자의 핸들러가 처리 */
      if (targetEl?.closest('.cp-modal-backdrop')) return;
      setBehaviorPopup(null);
      setBehaviorPopupStyle(null);
      setBehaviorSaveError(null);
    };
    document.addEventListener('mousedown', onDocMouseDown, true);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown, true);
    };
  }, [behaviorPopup]);

  React.useEffect(() => {
    if (!behaviorPopup) return;
    const scrollEl = annualTableScrollRef.current;
    const ancestors: HTMLElement[] = [];
    let cur: HTMLElement | null = scrollEl?.parentElement ?? null;
    while (cur) {
      const style = window.getComputedStyle(cur);
      const ov = `${style.overflow}${style.overflowY}${style.overflowX}`;
      if (/auto|scroll/.test(ov)) ancestors.push(cur);
      cur = cur.parentElement;
    }
    const onMove = () => {
      recomputeBehaviorPopupStyle();
    };
    scrollEl?.addEventListener('scroll', onMove, { passive: true });
    ancestors.forEach((el) => el.addEventListener('scroll', onMove, { passive: true }));
    window.addEventListener('scroll', onMove, { passive: true, capture: true });
    window.addEventListener('resize', onMove);
    return () => {
      scrollEl?.removeEventListener('scroll', onMove);
      ancestors.forEach((el) => el.removeEventListener('scroll', onMove));
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [behaviorPopup, recomputeBehaviorPopupStyle]);

  if (error) {
    return (
      <section>
        <h2>{termNumber}학기 연간진도표</h2>
        <p className="page-desc" role="alert">
          {error}
        </p>
      </section>
    );
  }

  return (
    <section>
      <div className="cp-page-title-row">
        <div>
          <h2>{termNumber}학기 연간진도표</h2>
        </div>
        <div className="cp-annual-top-actions-save cp-program-page-save-actions">
          <button
            type="button"
            className="cp-btn-save-all"
            onClick={() => void handleProgramGradeTermSave()}
            disabled={!programSavePending || !selectedEntry?.gradeId}
            title={
              programSavePending
                ? '프로그램 워크스페이스·프레젠테이션·활동지 초안을 DB에 저장합니다(연간진도표 본문은 저장하지 않음)'
                : '프로그램 변경사항 없음'
            }
          >
            {programSavePending ? '프로그램 통합 저장하기' : '프로그램 변경사항 없음'}
          </button>
        </div>
      </div>

      <article className="cp-card cp-weekly-class-hours-card cp-annual-progress-card">
        <div className="cp-weekly-class-hours-controls cp-weekly-class-hours-controls--stacked">
          <div className="cp-weekly-class-hours-controls-row cp-annual-progress-academic-year-row">
            <label>
              학년도
              <span className="cp-academic-year-readonly" aria-label={`학년도 ${academicYear}학년도(학사일정 기준 고정)`}>
                {academicYear}학년도
              </span>
            </label>
            <div className="cp-annual-progress-academic-year-actions">
              <button
                type="button"
                className="cp-btn-annual-action"
                onClick={() => setCrossTopicCountModalOpen(true)}
                disabled={!selectedEntry || tableLoading}
                title="1,2학기 범교과 주제 카운팅"
              >
                <span className="cp-annual-progress-year-action-label-full">1,2학기 범교과 주제 카운팅</span>
                <span className="cp-annual-progress-year-action-label-short">1,2학기 범교과</span>
              </button>
              <button
                type="button"
                className="cp-btn-annual-action cp-btn-annual-action-with-document-icon"
                onClick={() => setNeisLearningUploadModalOpen(true)}
                disabled={!selectedEntry || tableLoading}
                title="연간 진도 계획 및 편제별 진도 계획"
              >
                <DocumentActionIcon />
                <span className="cp-annual-progress-year-action-label-full">연간 진도 계획 및 편제별 진도 계획</span>
                <span className="cp-annual-progress-year-action-label-short">연간 및 편제별 진도계획</span>
              </button>
              <button
                type="button"
                className="cp-btn-annual-action cp-btn-annual-action-with-document-icon"
                onClick={() => setNeisExportFormatPick('neis-class-timetable')}
                disabled={!selectedEntry || tableLoading}
                title={`${termNumber}학기 NEIS 반별 시간표 다운로드`}
              >
                <span className="cp-annual-progress-year-action-label-full">
                  {termNumber}학기 NEIS 반별 시간표
                </span>
                <span className="cp-annual-progress-year-action-label-short">
                  {termNumber}학기 NEIS 반별시간표
                </span>
                <DownloadActionIcon className="cp-btn-annual-action-download-icon" />
              </button>
              <button
                type="button"
                className="cp-btn-annual-action cp-btn-annual-action-with-document-icon"
                onClick={() => setNeisExportFormatPick('neis-learning-upload')}
                disabled={!selectedEntry || tableLoading}
                title={`${termNumber}학기 NEIS 업로드용 학습내용 다운로드`}
              >
                <span className="cp-annual-progress-year-action-label-full">
                  {termNumber}학기 NEIS 업로드용 학습내용
                </span>
                <span className="cp-annual-progress-year-action-label-short">
                  {termNumber}학기 NEIS 학습내용
                </span>
                <DownloadActionIcon className="cp-btn-annual-action-download-icon" />
              </button>
            </div>
          </div>
          <div className="cp-annual-progress-card-actions-row" role="toolbar" aria-label="연간진도표 빠른 동작">
            <div className="cp-annual-progress-card-actions-left">
              <div className="cp-annual-progress-action-set cp-annual-progress-action-set--nav-lesson">
              <button
                type="button"
                className="cp-btn-copy-paste cp-btn-program-browse-nav"
                data-tutorial-browse="annual-timetable"
                onClick={() => {
                  if (!selectedAnnualTimetableBrowseTarget) return;
                  navigate(selectedAnnualTimetableBrowseTarget);
                }}
                title="선택된 셀이 있는 연간시간표 위치로 이동 (Shift+F)"
                disabled={!selectedAnnualTimetableBrowseTarget}
              >
                <span className="cp-btn-program-browse-nav-icon" aria-hidden="true">
                  <svg width="14" height="14" viewBox="0 0 14 14" focusable="false">
                    <path
                      d="M7 2.2 2.4 6.8l1.6 1.6 1.9-1.9V12h2.2V6.5L10 8.4l1.6-1.6L7 2.2z"
                      fill="currentColor"
                    />
                  </svg>
                </span>
                <span className="cp-annual-progress-action-label-full">선택셀 연간시간표 탐색</span>
                <span className="cp-annual-progress-action-label-compact">탐색</span>
              </button>
              <button
                type="button"
                className="cp-btn-copy-paste cp-btn-program-browse-nav"
                data-tutorial-browse="program"
                onClick={() => {
                  if (!selectedProgramBrowseTarget) return;
                  navigate(selectedProgramBrowseTarget);
                }}
                title="선택된 셀 행의 프로그램 페이지로 이동 (Shift+G)"
                disabled={!selectedProgramBrowseTarget}
              >
                <span className="cp-btn-program-browse-nav-icon" aria-hidden="true">
                  <svg width="14" height="14" viewBox="0 0 14 14" focusable="false">
                    <path
                      d="M8.1 2v5.5L10 5.6l1.6 1.6L7 11.8 2.4 7.2 4 5.6l1.9 1.9V2h2.2z"
                      fill="currentColor"
                    />
                  </svg>
                </span>
                <span className="cp-annual-progress-action-label-full">선택셀 프로그램 탐색</span>
                <span className="cp-annual-progress-action-label-compact">탐색</span>
              </button>
              <button
                type="button"
                className="cp-btn-copy-paste cp-btn-program-browse-nav"
                onClick={() => selectTodayLessonsFromButton()}
                disabled={!selectedEntry || rows.length === 0}
                title="오늘 학습내용: 첫 클릭은 현재 시각 교시, 이어서 누르면 다음 교시 순환. Shift+T는 버튼과 동일. 이미 이 페이지일 때 Shift+R으로 보내는 합성 단축만 매번 현재 시각만"
              >
                <span className="cp-annual-progress-action-label-full">오늘 학습내용 보기</span>
                <span className="cp-annual-progress-action-label-compact">
                  <span className="cp-annual-progress-action-label-compact-wide">오늘 학습내용</span>
                  <span className="cp-annual-progress-action-label-compact-narrow">오늘 학습</span>
                </span>
              </button>
              <button
                type="button"
                className="cp-btn-copy-paste cp-btn-program-browse-nav"
                onClick={() => selectTomorrowLessons()}
                disabled={!selectedEntry || rows.length === 0}
                title="내일 학습내용 보기 (Shift+Y). 내일 일정이 없으면 가장 가까운 미래 날짜를 선택"
              >
                <span className="cp-annual-progress-action-label-full">내일 학습내용 보기</span>
                <span className="cp-annual-progress-action-label-compact">
                  <span className="cp-annual-progress-action-label-compact-wide">내일 학습내용</span>
                  <span className="cp-annual-progress-action-label-compact-narrow">내일 학습</span>
                </span>
              </button>
              </div>
              <div className="cp-annual-progress-action-set cp-annual-progress-action-set--tools">
              <span className="cp-annual-progress-action-divider" aria-hidden="true" />
              <button
                type="button"
                className={`cp-btn-copy-paste cp-btn-program-browse-nav${passLockClassOf(presentationLocked)}`}
                data-tutorial-slide="open-presentation"
                onClick={() => void handleOpenAnnualProgressPresentation()}
                disabled={!selectedAnnualKey.trim()}
                title={
                  presentationLocked
                    ? '이용권 구매 후 이용할 수 있습니다'
                    : '선택한 셀 행의 슬라이드·프레젠테이션을 새 탭에서 엽니다 (Shift+U와 동일)'
                }
              >
                <span className="cp-annual-progress-action-label-full">프레젠테이션 열기</span>
                <span className="cp-annual-progress-action-label-compact">프레젠테이션</span>
              </button>
              <button
                type="button"
                className={`cp-btn-copy-paste cp-btn-program-browse-nav${passLockClassOf(presentationLocked)}`}
                onClick={() => {
                  if (requireFeature('presentation')) return;
                  setPresentationListOpen(true);
                }}
                disabled={!selectedEntry?.gradeId}
                title={
                  presentationLocked
                    ? '이용권 구매 후 이용할 수 있습니다'
                    : '이 연간진도표에 기록된 프레젠테이션 목록'
                }
              >
                <span className="cp-annual-progress-action-label-full">프레젠테이션 목록</span>
                <span className="cp-annual-progress-action-label-compact">프레젠테이션 목록</span>
              </button>
              <span className="cp-annual-progress-action-divider" aria-hidden="true" />
              <button
                type="button"
                className={`cp-btn-copy-paste cp-btn-program-browse-nav${passLockClassOf(worksheetLocked)}`}
                data-tutorial-ws="open-worksheet"
                onClick={() => handleOpenAnnualProgressWorksheet()}
                disabled={
                  !selectedAnnualKey.trim() ||
                  !!rows.find((r) => r.annualCellKey === selectedAnnualKey.trim())?.isPresentationSyncMirror
                }
                title={
                  worksheetLocked
                    ? '이용권 구매 후 이용할 수 있습니다'
                    : '선택한 셀 행의 활동지를 모달에서 엽니다 (Shift+I와 동일). 프로그램 원본 학습내용 셀에 연결됩니다'
                }
              >
                <span className="cp-annual-progress-action-label-full">활동지</span>
                <span className="cp-annual-progress-action-label-compact">활동지</span>
              </button>
              <button
                type="button"
                className={`cp-btn-copy-paste cp-btn-program-browse-nav${passLockClassOf(worksheetLocked)}`}
                onClick={() => {
                  if (requireFeature('worksheet')) return;
                  setProgramWorksheetListOpen(true);
                }}
                disabled={!selectedEntry?.gradeId}
                title={
                  worksheetLocked
                    ? '이용권 구매 후 이용할 수 있습니다'
                    : '이 연간진도표에 연결된 활동지 목록'
                }
              >
                <span className="cp-annual-progress-action-label-full">활동지 목록</span>
                <span className="cp-annual-progress-action-label-compact">활동지 목록</span>
              </button>
              <button
                type="button"
                className="cp-btn-copy-paste cp-btn-program-browse-nav"
                onClick={() => setBehaviorListOpen(true)}
                disabled={!selectedEntry?.gradeId}
                title="이 연간진도표의 날짜 기반 행동특성 목록"
              >
                <span className="cp-annual-progress-action-label-full">행동특성 목록</span>
                <span className="cp-annual-progress-action-label-compact">행동특성 목록</span>
              </button>
              <span className="cp-annual-progress-action-divider" aria-hidden="true" />
              <button
                type="button"
                className="cp-btn-copy-paste cp-btn-program-browse-nav"
                onClick={() => setAnnualDataCleanupOpen(true)}
                disabled={!selectedEntry?.gradeId}
                title="연간진도표·프로그램 진도셀·프레젠테이션 DB 원본 조회 및 고아 데이터 삭제"
              >
                <span className="cp-annual-progress-action-label-full">데이터 정리</span>
                <span className="cp-annual-progress-action-label-compact">데이터 정리</span>
              </button>
              </div>
            </div>
          </div>
        </div>
        {presentationListToast
          ? createPortal(
              <div className="cp-toast-auto cp-annual-presentation-list-toast" role="status" aria-live="polite">
                <div className="cp-toast-auto-box">{presentationListToast}</div>
              </div>,
              document.body
            )
          : null}

        {!currentGradeId && visibleEntries.length > 1 ? (
          <div className="cp-annual-progress-tabs" role="tablist" aria-label="클래스 탭">
            {visibleEntries.map((entry) => {
              const gradeRawName = grades.find((g) => g.id === entry.gradeId)?.name ?? entry.label;
              const title = parseGradeDisplayName(gradeRawName).title;
              const active = entry.gradeId === (selectedEntry?.gradeId ?? '');
              return (
                <button
                  key={entry.gradeId}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  className={`cp-annual-progress-tab ${active ? 'is-active' : ''}`}
                  onClick={() => setActiveGradeTabId(entry.gradeId)}
                >
                  {title}
                </button>
              );
            })}
          </div>
        ) : null}

        <div
          className="cp-annual-progress-table-wrap cp-annual-progress-table-wrap--fluid"
          ref={annualTableScrollRef}
          tabIndex={-1}
          aria-busy={entryFocusPending || undefined}
          /** 탐색 대상 행을 찾는 동안(보통 1~2프레임) 이전 스크롤 화면을 보이지 않게 가림 */
          style={entryFocusPending && !entryGeometryLoading ? { visibility: 'hidden' } : undefined}
        >
          {entryGeometryLoading ? (
            <p className="page-desc">불러오는 중…</p>
          ) : !termDef ? (
            <>
              <p className="page-desc">해당 학기의 학사일정이 없습니다.</p>
              <div className="cp-disabled-actual-ui cp-annual-progress-readonly-empty-ui" aria-hidden="true">
                <AnnualProgressReadOnlyEmptyTable />
              </div>
            </>
          ) : !selectedEntry ? (
            <div className="cp-disabled-actual-ui cp-annual-progress-readonly-empty-ui" aria-hidden="true">
              <AnnualProgressReadOnlyEmptyTable />
            </div>
          ) : (
            <>
              <table className="cp-annual-progress-table cp-annual-progress-table--annual-read-only">
              <colgroup>
                <col className="cp-annual-progress-col-seq" />
                <col className="cp-annual-progress-col-date" />
                <col className="cp-annual-progress-col-weekday" />
                <col className="cp-annual-progress-col-period" />
                <col className="cp-annual-progress-col-grade" />
                <col className="cp-annual-progress-col-semester" />
                <col className="cp-annual-progress-col-template" />
                <col className="cp-annual-progress-col-program" />
                <col className="cp-annual-progress-col-timetable" />
                <col className="cp-annual-progress-col-unit" />
                <col className="cp-annual-progress-col-learning" />
                <col className="cp-annual-progress-col-metric" />
                <col className="cp-annual-progress-col-metric" />
                <col className="cp-annual-progress-col-metric-period" />
                <col className="cp-annual-progress-col-metric-period" />
                <col className="cp-annual-progress-col-metric" />
                <col className="cp-annual-progress-col-record" />
                <col className="cp-annual-progress-col-record" />
                <col className="cp-annual-progress-col-record" />
                <col className="cp-annual-progress-col-suhub-girog" />
                <col className="cp-annual-progress-col-log" />
              </colgroup>
              <thead ref={annualTableHeadRef}>
                <tr>
                  <th colSpan={9} className="cp-annual-progress-head-group cp-annual-progress-head-group-base">
                    프로그램 일정
                  </th>
                  <th colSpan={7} className="cp-annual-progress-head-group cp-annual-progress-head-group-learning">
                    프로그램 내용
                  </th>
                  <th colSpan={4} className="cp-annual-progress-head-group cp-annual-progress-head-group-record">
                    메모 및 기록
                  </th>
                  <th className="cp-annual-progress-head-group cp-annual-progress-head-group-log">누가기록</th>
                </tr>
                <tr>
                  <th className="cp-annual-progress-col-seq">순</th>
                  <th className="cp-annual-progress-col-date">날짜</th>
                  <th className="cp-annual-progress-col-weekday">요일</th>
                  <th className="cp-annual-progress-col-period" title="해당 수업시간(교시·방과후·활동)">
                    교시
                  </th>
                  <th className="cp-annual-progress-col-grade">학년</th>
                  <th className="cp-annual-progress-col-semester">학기</th>
                  <th className="cp-annual-progress-col-template">편제</th>
                  <th className="cp-annual-progress-col-program">구분</th>
                  <th className="cp-annual-progress-col-timetable">시간표</th>
                  <th className="cp-annual-progress-col-unit">단원</th>
                  <th className="cp-annual-progress-col-learning">프로그램 내용</th>
                  <th className="cp-annual-progress-col-metric">쪽수</th>
                  <th className="cp-annual-progress-col-metric">보조쪽수</th>
                  <th className="cp-annual-progress-col-metric-period">해당 차시</th>
                  <th className="cp-annual-progress-col-metric-period">전체 차시</th>
                  <th className="cp-annual-progress-col-metric">준비물</th>
                  <th className="cp-annual-progress-col-record cp-annual-progress-col-record-fill">범교과</th>
                  <th className="cp-annual-progress-col-record cp-annual-progress-col-record-fill">메모</th>
                  <th className="cp-annual-progress-col-record cp-annual-progress-col-record-fill">평가</th>
                  <th className="cp-annual-progress-col-suhub-girog cp-annual-progress-col-record-fill">수업기록</th>
                  <th className={`cp-annual-progress-col-log cp-annual-progress-behavior-head-th${passLockClassOf(behaviorLocked)}`}>
                    <button
                      type="button"
                      className={`cp-annual-progress-behavior-head-btn${passLockClassOf(behaviorLocked)}`}
                      onClick={onBehaviorHeadButtonClick}
                      title={behaviorLocked ? '이용권 구매 후 이용할 수 있습니다' : undefined}
                    >
                      행동특성
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody ref={annualTableBodyRef} data-tutorial-table-body="annual-progress">
                {virtualTopSpacerHeight > 0 ? (
                  <tr aria-hidden="true">
                    <td
                      colSpan={ANNUAL_PROGRESS_TOTAL_COLUMN_COUNT}
                      style={{ height: virtualTopSpacerHeight, padding: 0, border: 0 }}
                    />
                  </tr>
                ) : null}
                {visibleRows.map((row, idx) => (
                  <AnnualProgressTableRow
                    key={row.annualCellKey}
                    rowIndex={virtualStartRow + idx}
                    row={row}
                    isRowSelection={selectedAnnualKey === row.annualCellKey && selectionMode === 'row'}
                    isBehaviorSelection={selectedAnnualKey === row.annualCellKey && selectionMode === 'behavior'}
                    rowFieldEdgeClassById={rowFieldEdgeClassById}
                    displayFontSize={displayFontSize}
                    annualNoteCellHandlers={annualNoteCellHandlers}
                    classRecordMemberTagMembersForRow={classRecordMemberTagMembersForRow}
                    behaviorRecordMemberTagMembersForRow={behaviorRecordMemberTagMembersForRow}
                    selectRowByAnnualKey={selectRowByAnnualKey}
                    selectBehaviorCellByAnnualKey={selectBehaviorCellByAnnualKey}
                    openBehaviorPopup={openBehaviorPopup}
                    openProgramRecordModal={openProgramRecordModal}
                    learningHasSlidePages={!!learningSlidePagesByAnnualKey[row.annualCellKey]}
                    learningHasWorksheet={!!learningWorksheetByAnnualKey[row.annualCellKey]}
                    learningHasMemoNote={!!learningMemoNoteByAnnualKey[row.annualCellKey]}
                    learningDisplayText={learningDisplayByAnnualKey[row.annualCellKey]?.text ?? row.progressLearning}
                    learningDisplayNote={learningDisplayByAnnualKey[row.annualCellKey]?.note ?? row.progressLearningNote}
                    presentationLockedAnnualCellKeys={presentationLockedAnnualCellKeys}
                  />
                ))}
                {virtualBottomSpacerHeight > 0 ? (
                  <tr aria-hidden="true">
                    <td
                      colSpan={ANNUAL_PROGRESS_TOTAL_COLUMN_COUNT}
                      style={{ height: virtualBottomSpacerHeight, padding: 0, border: 0 }}
                    />
                  </tr>
                ) : null}
              </tbody>
            </table>
            </>
          )}
        </div>
        {behaviorPopup
          ? (() => {
              const br = rowByAnnualKey[behaviorPopup.annualCellKey];
              if (!br) return null;
              const closeBehaviorPopup = () => {
                setBehaviorPopup(null);
                setBehaviorPopupStyle(null);
                setBehaviorSaveError(null);
              };
              return createPortal(
                <div
                  className="cp-modal-backdrop"
                  role="presentation"
                  onMouseDown={(e) => {
                    if (e.target === e.currentTarget && !behaviorSaving) closeBehaviorPopup();
                  }}
                >
                  <AnnualProgressBehaviorModalBody
                    key={behaviorPopup.annualCellKey}
                    annualCellKey={behaviorPopup.annualCellKey}
                    initialNote={br.progressBehaviorNote}
                    initialAiFeedback={br.progressBehaviorAiFeedback ?? null}
                    contextLine={`${br.dateLabel} (${br.weekdayLabel}) ${br.periodLabel}`}
                    workspaceId={workspaceId}
                    memberLayout={memberGradeLayout}
                    initialRecordMemberGroupId={br.progressBehaviorRecordMemberGroupId}
                    classMembers={classMembers}
                    innerRef={behaviorPopupRef}
                    saving={behaviorSaving}
                    error={behaviorSaveError}
                    canDelete={!!br.progressBehaviorNote.trim()}
                    onClose={closeBehaviorPopup}
                    onSave={commitBehaviorNote}
                    onDelete={deleteBehaviorNote}
                  />
                </div>,
                document.body
              );
            })()
          : null}
        {annualHoverTooltip
          ? createPortal(
              <div
                className={[
                  'cp-program-table-hover-memo-tooltip',
                  'cp-annual-cell-memo-tooltip',
                  annualHoverTooltip.kind === 'note'
                    ? 'cp-program-note-tooltip'
                    : annualHoverTooltip.kind === 'unit'
                      ? 'cp-program-unit-tooltip'
                      : 'cp-program-cross-tooltip',
                ].join(' ')}
                style={annualHoverTooltip.style}
                role="note"
                onMouseEnter={
                  annualHoverTooltip.kind === 'note' &&
                  !annualHoverTooltip.isClassRecord &&
                  learningPlanGuideHasPlanMarkers(annualHoverTooltip.note)
                    ? () => {
                        if (annualHoverHideTimerRef.current != null) {
                          clearTimeout(annualHoverHideTimerRef.current);
                          annualHoverHideTimerRef.current = null;
                        }
                      }
                    : undefined
                }
                onMouseLeave={
                  annualHoverTooltip.kind === 'note' &&
                  !annualHoverTooltip.isClassRecord &&
                  learningPlanGuideHasPlanMarkers(annualHoverTooltip.note)
                    ? () => {
                        if (annualHoverHideTimerRef.current != null) return;
                        annualHoverHideTimerRef.current = setTimeout(() => {
                          annualHoverHideTimerRef.current = null;
                          setAnnualHoverTooltip((prev) =>
                            prev?.anchorKey === annualHoverTooltip.anchorKey ? null : prev
                          );
                        }, LEARNING_PLAN_HOVER_TOOLTIP_DISMISS_MS);
                      }
                    : undefined
                }
              >
                {annualHoverTooltip.kind === 'note' &&
                !annualHoverTooltip.isClassRecord &&
                learningPlanGuideHasPlanMarkers(annualHoverTooltip.note) ? (
                  <LearningPlanGuideView
                    guide={annualHoverTooltip.note}
                    className="cp-program-hover-learning-guide"
                    ariaLabel="학습계획"
                  />
                ) : annualHoverTooltip.kind === 'note' && annualHoverTooltip.isClassRecord ? (
                  renderNoteWithMemberTags(
                    annualHoverTooltip.note,
                    annualHoverTooltip.memberTagMembers ?? classMembers,
                    {
                      ...(annualHoverTooltip.applyBulkMemberTagRules !== undefined
                        ? { applyBulkMemberTagRules: annualHoverTooltip.applyBulkMemberTagRules }
                        : {}),
                      sealedLabelByToken,
                    }
                  )
                ) : (
                  annualHoverTooltip.note
                )}
              </div>,
              document.body
            )
          : null}
      </article>
      <AnnualProgressProgramRecordModals
        openKind={programRecordModal?.kind ?? null}
        row={
          programRecordModal
            ? (() => {
                const r = rowByAnnualKey[programRecordModal.annualCellKey];
                if (!r) return null;
                return {
                  annualCellKey: r.annualCellKey,
                  dateIso: r.dateIso,
                  weekdayLabel: r.weekdayLabel,
                  periodLabel: r.periodLabel,
                  timetableText: r.timetableText,
                  templateLabel: r.templateLabel,
                  progressUnit: r.progressUnit,
                  progressUnitNote: r.progressUnitNote,
                  progressLearning: r.progressLearning,
                  progressLearningNote: r.progressLearningNote,
                  progressEvalCode: r.progressEvalCode,
                  progressEvalNote: r.progressEvalNote,
                  progressClassRecordKind: r.progressClassRecordKind,
                  progressClassRecordNote: r.progressClassRecordNote,
                  progressClassRecordMemberGroupId: r.progressClassRecordMemberGroupId,
                  evalRecordTarget: r.evalRecordTarget,
                  classRecordTarget: r.classRecordTarget,
                  evalStorageRaw: r.progressEvalRaw,
                  classStorageRaw: r.progressClassRaw,
                };
              })()
            : null
        }
        workspaceId={workspaceId}
        gradeId={selectedEntry?.gradeId ?? ''}
        academicYear={academicYear}
        termNumber={termNumber}
        memberLayout={memberGradeLayout}
        classMembers={classMembers}
        onClose={() => setProgramRecordModal(null)}
      />
      {(selectedEntry?.gradeId ?? '').trim() ? (
        <>
          <ProgramPresentationListModal
            open={presentationListOpen}
            onClose={() => setPresentationListOpen(false)}
            workspaceId={workspaceId}
            items={presentationListItemsMerged}
            onOpenItem={(key) => void openAnnualProgressPresentationForKey(key)}
            title="연간진도표 프레젠테이션 목록"
            getActionState={(annualCellKey) => {
              const state = resolveAnnualPresentationActionState(annualCellKey);
              return { canCopy: state.canCopy, canDelete: state.canDelete };
            }}
            onCopy={copyAnnualPresentationAtKey}
            onDelete={deleteAnnualPresentationAtKey}
            onNotify={(message) => showPresentationListToast(message, 2200)}
          />
          <ProgramWorksheetListModal
            open={programWorksheetListOpen}
            onClose={() => setProgramWorksheetListOpen(false)}
            workspaceId={workspaceId}
            items={worksheetListItems}
            onOpenItem={(annualKey) => openAnnualWorksheetEditorForKey(annualKey)}
            title="연간진도표 활동지 목록"
            onNotify={(message) => showPresentationListToast(message, 2200)}
          />
          <LearningContentWorksheetEditorModal
            key={worksheetEditorAnnualKey ?? 'annual-worksheet-editor-closed'}
            open={worksheetEditorOpen}
            draftStorageKey={
              worksheetEditorAnnualKey
                ? resolveWorksheetDraftStorageRowKey(
                    worksheetEditorAnnualKey,
                    !!rows.find((r) => r.annualCellKey === worksheetEditorAnnualKey)?.isPresentationSyncMirror,
                    annualSyncMirrorIndicatorMaps.repAnnualCellKeyByMirrorKey
                  )
                : ''
            }
            workspaceId={workspaceId}
            initialDocument={worksheetEditorInitialDocument}
            onClose={() => {
              setWorksheetEditorOpen(false);
              setWorksheetEditorAnnualKey(null);
              setWorksheetEditorInitialDocument(null);
            }}
            onSave={handleSaveAnnualWorksheetDocument}
          />
          <AnnualBehaviorNoteListModal
            open={behaviorListOpen}
            onClose={() => setBehaviorListOpen(false)}
            items={behaviorNoteListItems}
            onOpenItem={openBehaviorListItem}
            title="연간진도표 행동특성 목록"
            isLoading={behaviorListOpen && behaviorNotesLoading}
          />
          <AnnualCrossTopicCountModal
            open={crossTopicCountModalOpen}
            onClose={() => setCrossTopicCountModalOpen(false)}
            workspaceId={workspaceId}
            grades={grades}
            academicYear={academicYear}
            programGradeId={programGradeId}
            currentTermNumber={termNumber}
            currentTermCrossValues={rows.map((row) => row.progressCross)}
            currentTermDetailRows={currentTermDetailRows}
            schoolScopedCacheSyncReady={schoolScopedCacheSyncReady}
            onOpenAiCompose={isCrossAiMaster ? () => setCrossTopicAiModalOpen(true) : undefined}
            suspendEscape={crossTopicAiModalOpen}
          />
          {isCrossAiMaster ? (
            <AnnualCrossTopicAiModal
              open={crossTopicAiModalOpen}
              onClose={() => setCrossTopicAiModalOpen(false)}
              workspaceId={workspaceId}
              grades={grades}
              academicYear={academicYear}
              programGradeId={programGradeId}
              schoolScopedCacheSyncReady={schoolScopedCacheSyncReady}
            />
          ) : null}
          <NeisLearningContentUploadModal
            open={neisLearningUploadModalOpen}
            onClose={() => setNeisLearningUploadModalOpen(false)}
            sourceRows={neisLearningUploadSourceRows}
            subscribedTrackNames={subscribedTrackNamesForClass}
            termNumber={termNumber}
            loading={tableLoading}
            workspaceId={workspaceId}
            programGradeId={programGradeId}
          />
          <NeisExportFormatPickModal
            open={neisExportFormatPick != null}
            title={
              neisExportFormatPick === 'neis-class-timetable'
                ? `${termNumber}학기 NEIS 반별 시간표 다운로드`
                : `${termNumber}학기 NEIS 업로드용 학습내용 다운로드`
            }
            onClose={() => {
              if (!neisShortcutExportBusy) setNeisExportFormatPick(null);
            }}
            onPick={(format) => {
              void handleNeisExportFormatPick(format);
            }}
            busy={neisShortcutExportBusy}
          />
          <AnnualProgressDataCleanupModal
          open={annualDataCleanupOpen}
          onClose={() => setAnnualDataCleanupOpen(false)}
          workspaceId={workspaceId}
          academicYear={academicYear}
          termNumber={termNumber}
          gradeId={(selectedEntry?.gradeId ?? '').trim()}
          onCleanupComplete={() => {
            clearAnnualProgressRelatedCachesForGrade(
              workspaceId,
              academicYear,
              termNumber,
              (selectedEntry?.gradeId ?? '').trim()
            );
            setAnnualAggregateRefreshNonce((n) => n + 1);
          }}
          />
        </>
      ) : null}
      {programSaveToastState
        ? createPortal(
            <div className="cp-toast-auto" role="status" aria-live="polite">
              <div className={`cp-toast-auto-box cp-toast-auto-box-${programSaveToastState.kind}`}>
                {programSaveToastState.kind === 'saving' ? (
                  <span className="cp-toast-auto-spinner" aria-hidden="true" />
                ) : programSaveToastState.kind === 'success' ? (
                  <span className="cp-toast-auto-success-icon" aria-hidden="true">
                    v
                  </span>
                ) : null}
                <span>{programSaveToastState.message}</span>
              </div>
            </div>,
            document.body
          )
        : null}
      <button
        type="button"
        className="cp-fab-save"
        onClick={() => {
          void handleProgramGradeTermSave();
        }}
        disabled={!programSavePending || !selectedEntry?.gradeId}
        aria-label={
          programSavePending ? '프로그램 통합 저장하기' : '프로그램 변경사항 없음'
        }
        title={
          programSavePending
            ? '프로그램 워크스페이스·프레젠테이션·활동지 초안을 DB에 저장합니다(연간진도표 본문은 저장하지 않음)'
            : '프로그램 변경사항 없음'
        }
      >
        <img src="/Save.svg" alt="" className="cp-fab-save-icon" />
      </button>
    </section>
  );
};

export default AnnualProgressPage;

